import React from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography, useMediaQuery } from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useNavigate } from "react-router-dom";
import logoWithText from "../assets/images/headerlogo.svg";
import { getPreflopDecisionGrid } from "../preflop/opens";
import { TRAINER_ALLOWED_FLOPS } from "../data/trainerFlops";
import {
  TABLE_STYLE_OPTIONS,
  FELT_OPTIONS,
  RAIL_OPTIONS,
  DECK_COLORWAYS,
  CARD_BACK_OPTIONS,
  CHIP_COLOR_OPTIONS,
  BACKGROUND_OPTIONS,
  GLOW_OPTIONS,
  DEFAULT_APPEARANCE,
  loadAppearance,
  hexToRgba,
  TRAINER_APPEARANCE_EVENT,
  SOLVER_DECK_OPTIONS,
  loadSolverPrefs,
} from "../theme/appearancePrefs";
import { hasFeatureAccess, isPaywallRequired, openSubscriptionGate } from "../utils/billingAccess";

const COLORS = {
  bg: "#101012",
  cardBg: "#222222",
  border: "#333333",
  text: "#e0e0e0",
  textMuted: "#8f8f8f",
  white: "#ffffff",
  green: "#56ECCF",
  gold: "#B99E4F",
  black: "#282828",
  danger: "#e57f7f",
};
const SOLVER_ACTION_BUTTON_COLORS = {
  fold: "#821C2A",
  call: "#1EAB80",
  aggressive: "#077396",
};
const PRE_FLOP_THINK_DELAY_MS = 500;
const POST_FLOP_THINK_DELAY_MS = 0;

const SUIT_SYMBOLS = { s: "♠", h: "♥", d: "♦", c: "♣" };

const AppearanceContext = React.createContext(DEFAULT_APPEARANCE);

function suitColorFor(appearance, suit) {
  const colorway = DECK_COLORWAYS[appearance.deckColors] || DECK_COLORWAYS.four;
  const set = appearance.deckStyle === "classic" ? colorway.light : colorway.dark;
  return set[suit] || "#d0d0d0";
}

function cardFaceStyle(appearance, suitColor) {
  if (appearance.deckStyle === "classic") {
    return { backgroundColor: "#f2efe6", borderColor: "#b9b4a4" };
  }
  return { backgroundColor: COLORS.black, borderColor: suitColor };
}
const PREFLOP_ORDER = ["lj", "hj", "co", "btn", "sb", "bb"];
const POSTFLOP_ORDER = ["sb", "bb", "lj", "hj", "co", "btn"];
const SEAT_CODE = { lj: "LO", hj: "HI", co: "CO", btn: "BTN", sb: "SB", bb: "BB" };
const SOLVER_API_URL = process.env.REACT_APP_SOLVER_API_URL || "http://localhost:3001";
const SOLVER_V6_VERSION_ID = process.env.REACT_APP_SOLVER_TRAINER_V6_VERSION_ID || process.env.REACT_APP_SOLVER_V6_VERSION_ID || "v6-final";
const TRAINER_CONTEXT = {
  mode: "cash",
  tableSize: (process.env.REACT_APP_SOLVER_CASH_TABLE_SIZE || "6handed").toLowerCase(),
  stackSize: (process.env.REACT_APP_SOLVER_DEFAULT_STACK_SIZE || "100bbs").toLowerCase(),
  openSize: (process.env.REACT_APP_SOLVER_CASH_OPEN_SIZE || "2.5x").toLowerCase(),
};
const LEGACY_SPOT_LOOKUP_REMAP = {
  LOopenHIcall: "HIopenCOcall",
  LOopenCOcall: "HIopenCOcall",
  LOopenBTNcall: "HIopenBTNcall",
  LOopenSBcall: "HIopenSBcall",
  LOopenBBcall: "HIopenBBcall",
  HI3betLOcall: "CO3betHIcall",
  CO3betLOcall: "CO3betHIcall",
  BTN3betLOcall: "BTN3betHIcall",
  SB3betLOcall: "SB3betHIcall",
  BB3betLOcall: "BB3betHIcall",
};

// Seats sit ON the table border (rail); cards sit on the felt next to each player.
const VISUAL_SLOTS = [
  { id: "bottomRight", top: "97%", left: "66%", chipTop: "68%", chipLeft: "61%", cardTop: "80%", cardLeft: "64%" },
  { id: "bottomLeft", top: "97%", left: "36%", chipTop: "68%", chipLeft: "42%", cardTop: "80%", cardLeft: "38%" },
  { id: "left", top: "50%", left: "2%", chipTop: "50%", chipLeft: "24%", cardTop: "50%", cardLeft: "13%" },
  { id: "topLeft", top: "3%", left: "36%", chipTop: "31%", chipLeft: "42%", cardTop: "18%", cardLeft: "38%" },
  { id: "topRight", top: "3%", left: "66%", chipTop: "31%", chipLeft: "61%", cardTop: "18%", cardLeft: "64%" },
  { id: "right", top: "50%", left: "98%", chipTop: "50%", chipLeft: "77%", cardTop: "50%", cardLeft: "87%" },
];
/** Slim-phone overrides: push paired seats apart so panels/cards cannot overlap. Hero stays bottomRight. */
const VISUAL_SLOTS_SLIM = {
  bottomRight: { left: "76%", cardLeft: "76%", chipLeft: "64%" },
  bottomLeft: { left: "24%", cardLeft: "24%", chipLeft: "32%" },
  left: { left: "3%", cardLeft: "3%", chipLeft: "26%" },
  topLeft: { left: "24%", cardLeft: "24%", chipLeft: "32%" },
  topRight: { left: "76%", cardLeft: "76%", chipLeft: "68%" },
  right: { left: "97%", cardLeft: "97%", chipLeft: "74%" },
};
/** Phone (non-slim) horizontal spread; still clearer than desktop %, cards align under/over seat. */
const VISUAL_SLOTS_PHONE = {
  bottomRight: { left: "72%", cardLeft: "72%", chipLeft: "62%" },
  bottomLeft: { left: "28%", cardLeft: "28%", chipLeft: "38%" },
  left: { left: "3%", cardLeft: "3%", chipLeft: "24%" },
  topLeft: { left: "28%", cardLeft: "28%", chipLeft: "38%" },
  topRight: { left: "72%", cardLeft: "72%", chipLeft: "62%" },
  right: { left: "97%", cardLeft: "97%", chipLeft: "76%" },
};
const PLAY_VISUAL_SLOTS = [
  { id: "hero", top: "74%", left: "50%", chipTop: "63%", chipLeft: "56%" },
  { id: "villain", top: "24%", left: "50%", chipTop: "34%", chipLeft: "44%" },
];
/** Match Solver table typography floors (on-screen px). */
const TABLE_FS_FLOOR = 14;
const CHIP_LABEL_FS_FLOOR = 10;
const FUTURA_BOOK = "\"Futura Md Bt\", Futura, \"Trebuchet MS\", Arial, sans-serif";
const chipLabelGapPx = (fs = CHIP_LABEL_FS_FLOOR) => Math.max(6, Math.round(Number(fs) * 0.45));
/**
 * Phone Play HU table height budget:
 * - MAX: fill the phone when 4 stacked hero actions already fit underneath
 * - MIN: still fits board + pot chips + both bet piles + hero cards with small buffers
 * - ACTION/CHROME reserves: space that must stay outside the felt
 */
const PLAY_HU_PHONE_TABLE_MAX = 560;
const PLAY_HU_PHONE_TABLE_MIN = 400;
const PLAY_HU_PHONE_ACTION_RESERVE = 4 * 46 + 3 * 6 + 16; // ~218px for 4 full-width action buttons
const PLAY_HU_PHONE_CHROME_RESERVE = 58 + 74 + 44 + 12; // header + session strip + opp pad + page pad

function resolveSeatGeometry(seat, { slimTable = false, phoneTable = false } = {}) {
  const override =
    (slimTable && VISUAL_SLOTS_SLIM[seat?.id]) ||
    (phoneTable && VISUAL_SLOTS_PHONE[seat?.id]) ||
    null;
  if (!override) {
    return {
      top: seat.top,
      left: seat.left,
      chipTop: seat.chipTop,
      chipLeft: seat.chipLeft,
      cardTop: seat.cardTop,
      cardLeft: seat.cardLeft,
    };
  }
  return {
    top: seat.top,
    left: override.left || seat.left,
    chipTop: seat.chipTop,
    chipLeft: override.chipLeft || seat.chipLeft,
    cardTop: seat.cardTop,
    cardLeft: override.cardLeft || seat.cardLeft,
  };
}

function isBottomTableSeat(seatId) {
  return seatId === "bottomLeft" || seatId === "bottomRight";
}
const PLAY_SESSION_HISTORY_KEY = "pm_play_session_history_v1";
const PLAY_OPPONENT_NAMES = [
  "Alex", "Jordan", "Taylor", "Morgan", "Casey", "Riley", "Avery", "Sam", "Jamie", "Cameron",
  "Liam", "Noah", "Ethan", "Mason", "Lucas", "Logan", "Ella", "Olivia", "Ava", "Mia",
];
const PLAY_OPPONENT_QUOTE_BY_KEY = {
  anna: "I'm only kind until the cards are dealt",
  peter: "Let's have a good clean game, no funny business",
  mimi: "Ive got somewhere to be, lets play fast yeah?",
  ryan: "First time? You look nervous ...",
};
const PLAY_OPPONENT_BET_TAUNTS = {
  anna: ["Try this", "Not too much for you", "Tired?", "[chip amount] ok for you?", "I dont want you to lose all your money"],
  peter: ["Nothing for free", "Its a fair bet", "Not too expensive", "[chip amount] is fair I think"],
  mimi: ["Come on...", "You scared?", "Just fold already", "Nervous?", "I've got to go...", "Dont think too much", "Gimmie", "[chip amount] is nothing"],
  ryan: ["Think about this one", "You can always fold", "Pay up", "Throw them away", "I can read you", "Give up", "Its only [chip amount]", "You must pay to play"],
};
const SOLVER_RANKS = ["A", "K", "Q", "J", "T", "9", "8", "7", "6", "5", "4", "3", "2"];
const SOLVER_SUITS = ["s", "h", "d", "c"];
const CANONICAL_SUITS = ["h", "d", "c", "s"];
const RANK_VALUE = { A: 14, K: 13, Q: 12, J: 11, T: 10, "9": 9, "8": 8, "7": 7, "6": 6, "5": 5, "4": 4, "3": 3, "2": 2 };
const STRAIGHT_WINDOWS = [
  [14, 13, 12, 11, 10],
  [13, 12, 11, 10, 9],
  [12, 11, 10, 9, 8],
  [11, 10, 9, 8, 7],
  [10, 9, 8, 7, 6],
  [9, 8, 7, 6, 5],
  [8, 7, 6, 5, 4],
  [7, 6, 5, 4, 3],
  [6, 5, 4, 3, 2],
  [14, 5, 4, 3, 2],
];

function makeSeatNumberMap(start = 100) {
  return PREFLOP_ORDER.reduce((acc, seat) => ({ ...acc, [seat]: start }), {});
}

function flopStrToCards(flopStr) {
  if (!flopStr) return [];
  return String(flopStr).match(/.{1,2}/g) || [];
}

function createSuitMapping(flopCards) {
  const userSuits = [];
  for (const card of flopCards || []) {
    const suit = card?.[1]?.toLowerCase();
    if (!suit) continue;
    if (!userSuits.includes(suit)) userSuits.push(suit);
  }
  const toCanonical = {};
  const fromCanonical = {};
  for (let i = 0; i < userSuits.length; i++) {
    toCanonical[userSuits[i]] = CANONICAL_SUITS[i];
    fromCanonical[CANONICAL_SUITS[i]] = userSuits[i];
  }
  const remainingUserSuits = SOLVER_SUITS.filter((s) => !userSuits.includes(s));
  const remainingCanonicalSuits = CANONICAL_SUITS.filter((s) => !Object.values(toCanonical).includes(s));
  for (let i = 0; i < remainingUserSuits.length; i++) {
    toCanonical[remainingUserSuits[i]] = remainingCanonicalSuits[i];
    fromCanonical[remainingCanonicalSuits[i]] = remainingUserSuits[i];
  }
  return { toCanonical, fromCanonical };
}

function cardToCanonical(card, suitMapping) {
  if (!card || !suitMapping) return card;
  const rank = card[0];
  const suit = String(card[1] || "").toLowerCase();
  return `${rank}${suitMapping.toCanonical[suit] || suit}`;
}

function flopToCanonical(flopCards, suitMapping) {
  return (flopCards || []).map((card) => cardToCanonical(card, suitMapping));
}

function canonicalizeUserFlopTo1755(cards) {
  const sorted = (cards || [])
    .slice()
    .sort((a, b) => {
      const ra = RANK_VALUE[a?.[0]] || 0;
      const rb = RANK_VALUE[b?.[0]] || 0;
      if (rb !== ra) return rb - ra;
      const sa = SOLVER_SUITS.indexOf(String(a?.[1] || "").toLowerCase());
      const sb = SOLVER_SUITS.indexOf(String(b?.[1] || "").toLowerCase());
      return sa - sb;
    });
  const suitMap = createSuitMapping(sorted);
  const canonicalCards = flopToCanonical(sorted, suitMap);
  return { canonicalCards, canonicalStr: canonicalCards.join("") };
}

async function lookupQueriedFlopFromApi(canonicalFlop1755) {
  const canonical = String(canonicalFlop1755 || "").trim();
  if (!canonical || canonical.length !== 6) return null;
  try {
    const qs = new URLSearchParams({ canonical });
    const res = await fetch(`${SOLVER_API_URL}/api/v6/flop-map?${qs.toString()}`, { credentials: "include" });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data?.success) return null;
    const queried = String(data?.queriedFlop || "").trim();
    if (!queried || queried.length !== 6) return null;
    return queried;
  } catch {
    return null;
  }
}

function createSuitMappingBetweenFlops(desiredFlopCards, queriedFlopCards) {
  const desired = (desiredFlopCards || []).slice(0, 3);
  const queried = (queriedFlopCards || []).slice(0, 3);
  const suitCounts = (cards) => {
    const out = {};
    for (const c of cards) {
      const s = c?.[1]?.toLowerCase();
      if (!s) continue;
      out[s] = (out[s] || 0) + 1;
    }
    return out;
  };
  const maxSuitCount = (cards) => {
    const vals = Object.values(suitCounts(cards));
    return vals.length ? Math.max(...vals) : 0;
  };
  const sortSuitsByCountDesc = (counts) =>
    Object.entries(counts)
      .sort((a, b) => (b[1] - a[1]) || (SOLVER_SUITS.indexOf(a[0]) - SOLVER_SUITS.indexOf(b[0])))
      .map(([s]) => s);
  const assign = (toCanonical, fromCanonical, desiredSuit, queriedSuit) => {
    if (!desiredSuit || !queriedSuit) return;
    if (toCanonical[desiredSuit] && toCanonical[desiredSuit] !== queriedSuit) return;
    if (fromCanonical[queriedSuit] && fromCanonical[queriedSuit] !== desiredSuit) return;
    toCanonical[desiredSuit] = queriedSuit;
    fromCanonical[queriedSuit] = desiredSuit;
  };
  const toCanonical = {};
  const fromCanonical = {};
  const dMax = maxSuitCount(desired);
  const qMax = maxSuitCount(queried);
  if (dMax === 3 && qMax === 3) {
    assign(toCanonical, fromCanonical, desired[0]?.[1]?.toLowerCase(), queried[0]?.[1]?.toLowerCase());
  }
  if (dMax === 2 && qMax === 2) {
    const dOrder = sortSuitsByCountDesc(suitCounts(desired));
    const qOrder = sortSuitsByCountDesc(suitCounts(queried));
    assign(toCanonical, fromCanonical, dOrder[0], qOrder[0]);
    assign(toCanonical, fromCanonical, dOrder[1], qOrder[1]);
  }
  if (dMax === 1 && qMax === 1) {
    const dSorted = desired
      .slice()
      .sort((a, b) => (RANK_VALUE[b[0]] - RANK_VALUE[a[0]]) || (SOLVER_SUITS.indexOf(String(a[1] || "").toLowerCase()) - SOLVER_SUITS.indexOf(String(b[1] || "").toLowerCase())));
    const qSorted = queried
      .slice()
      .sort((a, b) => (RANK_VALUE[b[0]] - RANK_VALUE[a[0]]) || (SOLVER_SUITS.indexOf(String(a[1] || "").toLowerCase()) - SOLVER_SUITS.indexOf(String(b[1] || "").toLowerCase())));
    for (let i = 0; i < 3; i++) {
      assign(toCanonical, fromCanonical, dSorted[i]?.[1]?.toLowerCase(), qSorted[i]?.[1]?.toLowerCase());
    }
  }
  const remainingDesired = SOLVER_SUITS.filter((s) => !toCanonical[s]);
  const remainingQueried = SOLVER_SUITS.filter((s) => !fromCanonical[s]);
  for (let i = 0; i < Math.min(remainingDesired.length, remainingQueried.length); i++) {
    assign(toCanonical, fromCanonical, remainingDesired[i], remainingQueried[i]);
  }
  return { toCanonical, fromCanonical };
}

function createFlopRankMappingStrengthPaired(desiredFlopCards, queriedFlopCards) {
  const desired = (desiredFlopCards || []).slice(0, 3).filter((c) => c && c.length >= 2);
  const queried = (queriedFlopCards || []).slice(0, 3).filter((c) => c && c.length >= 2);
  if (desired.length !== 3 || queried.length !== 3) return null;
  const byStrength = (a, b) => (RANK_VALUE[a] || 0) - (RANK_VALUE[b] || 0);
  const dRanks = desired.map((c) => c[0]);
  const qRanks = queried.map((c) => c[0]);
  const dSorted = [...dRanks].sort(byStrength);
  const qSorted = [...qRanks].sort(byStrength);
  const desiredToQueried = {};
  for (let i = 0; i < 3; i++) desiredToQueried[dSorted[i]] = qSorted[i];
  const desiredFlopRankSet = new Set(dRanks);
  const queriedFlopRankSet = new Set(qRanks);
  const desiredOnlyRanks = new Set(dRanks.filter((r) => !queriedFlopRankSet.has(r)));
  const queriedOnlyRanks = new Set(qRanks.filter((r) => !desiredFlopRankSet.has(r)));
  return { desiredToQueried, desiredFlopRankSet, queriedFlopRankSet, desiredOnlyRanks, queriedOnlyRanks };
}

function canonicalizeComboHand(comboHand) {
  if (!comboHand || comboHand.length < 4) return comboHand;
  const r1 = comboHand[0];
  const s1 = String(comboHand[1] || "").toLowerCase();
  const r2 = comboHand[2];
  const s2 = String(comboHand[3] || "").toLowerCase();
  const rankOrder = (r) => SOLVER_RANKS.indexOf(r);
  const suitOrder = (s) => SOLVER_SUITS.indexOf(s);
  if (rankOrder(r2) !== -1 && rankOrder(r1) !== -1 && rankOrder(r2) < rankOrder(r1)) {
    return `${r2}${s2}${r1}${s1}`;
  }
  if (r1 === r2 && suitOrder(s1) > suitOrder(s2)) {
    return `${r2}${s2}${r1}${s1}`;
  }
  return `${r1}${s1}${r2}${s2}`;
}

function comboToHandCategory(combo) {
  if (combo?.length !== 4) return null;
  const rank1 = combo[0];
  const suit1 = combo[1];
  const rank2 = combo[2];
  const suit2 = combo[3];
  const r1idx = SOLVER_RANKS.indexOf(rank1);
  const r2idx = SOLVER_RANKS.indexOf(rank2);
  if (r1idx === -1 || r2idx === -1) return null;
  if (rank1 === rank2) return rank1 + rank1;
  if (suit1 === suit2) return r1idx < r2idx ? rank1 + rank2 + "s" : rank2 + rank1 + "s";
  return r1idx < r2idx ? rank1 + rank2 + "o" : rank2 + rank1 + "o";
}

function isPocketPairCombo(comboStr) {
  return comboStr?.length === 4 && comboStr[0] === comboStr[2];
}

function isPrimaryApiComboUsable(apiComboStr, comboData, desiredComboHand = null) {
  if (!apiComboStr || !comboData?.frequencies?.length) return false;
  const cat = comboToHandCategory(apiComboStr);
  const mappedIsPairCategory = !!(cat && cat.length === 2 && cat[0] === cat[1]);
  if (mappedIsPairCategory && !isPocketPairCombo(desiredComboHand || "")) return false;
  return true;
}

function comboDesiredToApiLookup(comboHand, suitMapping, flopRankMap) {
  if (!comboHand || comboHand.length < 4) return comboHand;
  const rankMap = flopRankMap?.desiredToQueried;
  const onFlop = flopRankMap?.desiredFlopRankSet;
  const remapCard = (card) => {
    const r0 = card[0];
    const s = String(card[1] || "").toLowerCase();
    const r = onFlop?.has(r0) && rankMap && rankMap[r0] !== undefined ? rankMap[r0] : r0;
    const mapped = `${r}${s}`;
    return suitMapping ? cardToCanonical(mapped, suitMapping) : mapped;
  };
  const q1 = remapCard(comboHand.slice(0, 2));
  const q2 = remapCard(comboHand.slice(2, 4));
  return canonicalizeComboHand(q1 + q2);
}

function findComboDataInGrid(gridData, apiHandStr) {
  if (!gridData || !apiHandStr) return null;
  const targetKey = canonicalComboKey(String(apiHandStr).slice(0, 2), String(apiHandStr).slice(2, 4));
  for (const row of gridData || []) {
    for (const cell of row || []) {
      for (const combo of cell?.combos || []) {
        const comboKey = canonicalComboKey(String(combo?.hand || "").slice(0, 2), String(combo?.hand || "").slice(2, 4));
        if (comboKey === targetKey) return combo;
      }
    }
  }
  return null;
}

function comboIsStraightOrStraightFlush(boardCards, comboHand) {
  const board = (boardCards || []).filter(Boolean);
  if (!comboHand || comboHand.length < 4 || board.length < 3) return false;
  const cards = [comboHand.slice(0, 2), comboHand.slice(2, 4), ...board];
  const rankSet = new Set(
    cards
      .map((c) => c?.[0])
      .filter(Boolean)
      .map((r) => RANK_VALUE[r] || 0)
      .filter((v) => v > 0)
  );
  return STRAIGHT_WINDOWS.some((w) => w.every((r) => rankSet.has(r)));
}

function shouldSkipStepOneDirect(comboHand, flopRankMap, desiredBoardCards, queriedBoardCards, apiComboStr) {
  if (!comboHand || comboHand.length < 4) return false;
  const r1 = comboHand[0];
  const r2 = comboHand[2];
  const donorShifted = !!flopRankMap?.queriedOnlyRanks && (flopRankMap.queriedOnlyRanks.has(r1) || flopRankMap.queriedOnlyRanks.has(r2));
  if (donorShifted) return true;
  if (!apiComboStr || !desiredBoardCards?.length || !queriedBoardCards?.length) return false;
  const desiredStraight = comboIsStraightOrStraightFlush(desiredBoardCards, comboHand);
  const queriedStraight = comboIsStraightOrStraightFlush(queriedBoardCards, apiComboStr);
  return desiredStraight !== queriedStraight;
}

function highestDesiredRankNotOnQueried(desiredBoardCards, queriedBoardCards) {
  const queriedRanks = new Set((queriedBoardCards || []).filter(Boolean).map((c) => c[0]));
  let best = null;
  for (const c of desiredBoardCards || []) {
    if (!c || c.length < 2) continue;
    const r = c[0];
    if (queriedRanks.has(r)) continue;
    const v = RANK_VALUE[r] || 0;
    if (!best || v > best.v) best = { r, v };
  }
  return best?.r || null;
}

function resolveRunoutCanonicalCard(userCard, desiredBoardCards, queriedBoardCards, suitMapping) {
  if (!userCard || userCard.length < 2) return userCard;
  const desiredRanks = new Set((desiredBoardCards || []).filter(Boolean).map((c) => c[0]));
  const queriedRanks = new Set((queriedBoardCards || []).filter(Boolean).map((c) => c[0]));
  const desiredRank = userCard[0];
  const primaryCanonical = cardToCanonical(userCard, suitMapping);
  const queriedRank = primaryCanonical[0];
  const exactCardCollision = (queriedBoardCards || []).includes(primaryCanonical);
  const rankOnlyCollision = queriedRanks.has(queriedRank) && !desiredRanks.has(desiredRank);
  if (exactCardCollision || rankOnlyCollision) {
    const fallbackDesiredRank = highestDesiredRankNotOnQueried(desiredBoardCards, queriedBoardCards);
    if (fallbackDesiredRank) return cardToCanonical(`${fallbackDesiredRank}${userCard[1]}`, suitMapping);
  }
  return primaryCanonical;
}

async function fetchTextureResolutionForSingleCombo({
  spot,
  flop,
  nodeId,
  desiredBoardCards,
  queriedBoardCards,
  desiredCombo4,
  preferredApiHand,
}) {
  const body = {
    spot,
    versionId: SOLVER_V6_VERSION_ID,
    flop,
    nodeId,
    mode: TRAINER_CONTEXT.mode,
    tableSize: TRAINER_CONTEXT.tableSize,
    stackSize: TRAINER_CONTEXT.stackSize,
    openSize: TRAINER_CONTEXT.openSize,
    desiredBoardCards,
    queriedBoardCards,
    comboRequests: [{ desiredCombo4, preferredApiHand }],
  };
  const res = await fetch(`${SOLVER_API_URL}/api/v6/texture-resolve`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body),
  });
  const payload = await res.json().catch(() => null);
  if (!res.ok || !payload?.success) return null;
  return payload?.textureByCombo?.[desiredCombo4] || null;
}

function extractNumber(label) {
  const nums = String(label || "").match(/\d+(?:\.\d+)?/g);
  if (!nums?.length) return 0;
  return Number(nums[nums.length - 1]) || 0;
}

function cardSortValue(card) {
  const ranks = "23456789TJQKA";
  const suits = "cdhs";
  const r = ranks.indexOf(card?.[0] || "2");
  const s = suits.indexOf(card?.[1] || "c");
  return r * 4 + s;
}

function cardRankValue(card) {
  return "23456789TJQKA".indexOf(card?.[0] || "2") + 2;
}

function getStraightHighFromRanks(rankValues = []) {
  const set = new Set(rankValues.filter(Boolean));
  if (set.has(14)) set.add(1); // Wheel support
  const sorted = Array.from(set).sort((a, b) => a - b);
  let run = 1;
  let best = 0;
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i] === sorted[i - 1] + 1) {
      run += 1;
      if (run >= 5) best = sorted[i];
    } else {
      run = 1;
    }
  }
  return best;
}

function evaluateSevenCardHand(cards = []) {
  const rankCounts = new Map();
  const suitBuckets = { c: [], d: [], h: [], s: [] };
  for (const card of cards) {
    const rank = cardRankValue(card);
    const suit = card?.[1];
    rankCounts.set(rank, (rankCounts.get(rank) || 0) + 1);
    if (suitBuckets[suit]) suitBuckets[suit].push(rank);
  }
  const byCountDesc = Array.from(rankCounts.entries()).sort((a, b) => (b[1] - a[1]) || (b[0] - a[0]));
  const allRanksDesc = Array.from(rankCounts.keys()).sort((a, b) => b - a);

  let flushSuit = null;
  for (const suit of Object.keys(suitBuckets)) {
    if (suitBuckets[suit].length >= 5) {
      flushSuit = suit;
      break;
    }
  }
  if (flushSuit) {
    const sfHigh = getStraightHighFromRanks(suitBuckets[flushSuit]);
    if (sfHigh) return [8, sfHigh];
  }

  const quads = byCountDesc.find(([, c]) => c === 4);
  if (quads) {
    const kicker = allRanksDesc.find((r) => r !== quads[0]) || 0;
    return [7, quads[0], kicker];
  }

  const trips = byCountDesc.filter(([, c]) => c === 3).map(([r]) => r).sort((a, b) => b - a);
  const pairs = byCountDesc.filter(([, c]) => c === 2).map(([r]) => r).sort((a, b) => b - a);
  if (trips.length && (pairs.length || trips.length > 1)) {
    const topTrip = trips[0];
    const pairRank = pairs[0] || trips[1];
    return [6, topTrip, pairRank];
  }

  if (flushSuit) {
    const flushRanks = [...suitBuckets[flushSuit]].sort((a, b) => b - a).slice(0, 5);
    return [5, ...flushRanks];
  }

  const straightHigh = getStraightHighFromRanks(allRanksDesc);
  if (straightHigh) return [4, straightHigh];

  if (trips.length) {
    const kickers = allRanksDesc.filter((r) => r !== trips[0]).slice(0, 2);
    return [3, trips[0], ...(kickers.length ? kickers : [0, 0])];
  }

  if (pairs.length >= 2) {
    const top = pairs[0];
    const second = pairs[1];
    const kicker = allRanksDesc.find((r) => r !== top && r !== second) || 0;
    return [2, top, second, kicker];
  }

  if (pairs.length === 1) {
    const pair = pairs[0];
    const kickers = allRanksDesc.filter((r) => r !== pair).slice(0, 3);
    return [1, pair, ...(kickers.length ? kickers : [0, 0, 0])];
  }

  return [0, ...allRanksDesc.slice(0, 5)];
}

function compareHandRanks(a = [], b = []) {
  const n = Math.max(a.length, b.length);
  for (let i = 0; i < n; i++) {
    const av = Number(a[i] || 0);
    const bv = Number(b[i] || 0);
    if (av > bv) return 1;
    if (av < bv) return -1;
  }
  return 0;
}

function canonicalComboKey(a, b) {
  return [a, b].filter(Boolean).sort((x, y) => cardSortValue(x) - cardSortValue(y)).join("");
}

function rankOrderIndex(rank) {
  return ["A", "K", "Q", "J", "T", "9", "8", "7", "6", "5", "4", "3", "2"].indexOf(rank);
}

function handLabelFromTwoCards(cards) {
  const c1 = cards?.[0];
  const c2 = cards?.[1];
  if (!c1 || !c2 || c1.length < 2 || c2.length < 2) return null;
  const r1 = c1[0];
  const r2 = c2[0];
  const s1 = c1[1];
  const s2 = c2[1];
  const i1 = rankOrderIndex(r1);
  const i2 = rankOrderIndex(r2);
  if (i1 < 0 || i2 < 0) return null;
  if (r1 === r2) return `${r1}${r2}`;
  const hi = i1 < i2 ? r1 : r2;
  const lo = i1 < i2 ? r2 : r1;
  return `${hi}${lo}${s1 === s2 ? "s" : "o"}`;
}

function buildDeck() {
  const ranks = "23456789TJQKA".split("");
  const suits = "cdhs".split("");
  const deck = [];
  for (const r of ranks) for (const s of suits) deck.push(`${r}${s}`);
  for (let i = deck.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [deck[i], deck[j]] = [deck[j], deck[i]];
  }
  return deck;
}

function classifyAction(rawAction, raiseCountBefore) {
  const txt = String(rawAction || "").trim().toLowerCase();
  if (txt.includes("fold")) return { action: "fold", raiseSize: null, aggressive: false };
  if (txt.includes("call")) return { action: "call", raiseSize: null, aggressive: false };
  if (txt.includes("check")) return { action: "check", raiseSize: null, aggressive: false };
  const n = extractNumber(txt);
  if (txt.includes("all in") || txt.includes("all-in") || txt.includes("allin")) {
    const next = raiseCountBefore <= 0 ? "open" : `${Math.min(4, raiseCountBefore + 2)}bet`;
    return { action: next, raiseSize: n || 10_000, aggressive: true };
  }
  if (txt.includes("open")) return { action: "open", raiseSize: n || 2.5, aggressive: true };
  if (txt.includes("3bet")) return { action: "3bet", raiseSize: n || 9, aggressive: true };
  if (txt.includes("4bet")) return { action: "4bet", raiseSize: n || 22, aggressive: true };
  if (txt.includes("raise") || txt.includes("bet")) {
    // raiseCountBefore counts prior aggressive acts including open.
    // Example:
    // - after open (count=1), a raise is a 3bet
    // - after open+3bet (count=2), a raise is a 4bet
    const next = raiseCountBefore <= 0 ? "open" : `${Math.min(4, raiseCountBefore + 2)}bet`;
    return { action: next, raiseSize: n || 2.5, aggressive: true };
  }
  return { action: "fold", raiseSize: null, aggressive: false };
}

function countRaises(history) {
  return (history || []).filter((h) => /open|bet|raise/i.test(String(h.action || ""))).length;
}

function pickByWeights(actions, freqs) {
  const items = [];
  for (let i = 0; i < actions.length; i++) {
    const f = Math.max(0, Number(freqs?.[i] ?? 0));
    if (f > 0) items.push({ action: actions[i], weight: f });
  }
  // When no action has positive frequency for this exact hand,
  // treat it as pure fold (e.g. open-only node where open=0%).
  if (!items.length) return { action: "Fold", rngRoll: null, rngTotal: 0 };
  const total = items.reduce((s, it) => s + it.weight, 0);
  const rngRoll = Math.random();
  let r = rngRoll * total;
  for (const it of items) {
    r -= it.weight;
    if (r <= 0) return { action: it.action, rngRoll, rngTotal: total };
  }
  return { action: items[items.length - 1].action, rngRoll, rngTotal: total };
}

function actionAggressionScore(actionLabel) {
  const txt = String(actionLabel || "").trim().toLowerCase();
  if (!txt) return 0;
  if (txt.includes("fold")) return 0;
  if (txt.includes("check")) return 1;
  if (txt.includes("call")) return 2;
  if (txt.includes("all in") || txt.includes("all-in") || txt.includes("allin")) return 10_000;
  if (txt.includes("open")) return 100 + extractNumber(txt);
  if (txt.includes("bet") || txt.includes("raise") || txt.includes("3bet") || txt.includes("4bet") || txt.includes("5bet")) {
    return 100 + extractNumber(txt);
  }
  return 50 + extractNumber(txt);
}

function applyProfileToMixedStrategy(actions = [], frequencies = [], profileKey = null) {
  const freqs = (frequencies || []).map((f) => Math.max(0, Number(f || 0)));
  const positiveIdxs = freqs.map((f, i) => ({ f, i })).filter((x) => x.f > 0).map((x) => x.i);
  if (!profileKey || positiveIdxs.length <= 1) {
    return { frequencies: freqs, forcedIndex: null, applied: false };
  }

  const byPassive = [...positiveIdxs].sort((a, b) => {
    const d = actionAggressionScore(actions[a]) - actionAggressionScore(actions[b]);
    return d !== 0 ? d : a - b;
  });
  const byAggressive = [...positiveIdxs].sort((a, b) => {
    const d = actionAggressionScore(actions[b]) - actionAggressionScore(actions[a]);
    return d !== 0 ? d : a - b;
  });
  const passiveIdx = byPassive[0];
  const aggressiveIdx = byAggressive[0];

  if (profileKey === "anna") {
    return { frequencies: freqs, forcedIndex: passiveIdx, applied: true };
  }
  if (profileKey === "mimi") {
    return { frequencies: freqs, forcedIndex: aggressiveIdx, applied: true };
  }

  const adjusted = [...freqs];
  let moveBudget = 0.15; // 15 percentage points
  if (profileKey === "peter") {
    for (const idx of byAggressive) {
      if (idx === passiveIdx || moveBudget <= 0) continue;
      const take = Math.min(adjusted[idx], moveBudget);
      adjusted[idx] -= take;
      adjusted[passiveIdx] += take;
      moveBudget -= take;
    }
    return { frequencies: adjusted, forcedIndex: null, applied: true };
  }
  if (profileKey === "ryan") {
    for (const idx of byPassive) {
      if (idx === aggressiveIdx || moveBudget <= 0) continue;
      const take = Math.min(adjusted[idx], moveBudget);
      adjusted[idx] -= take;
      adjusted[aggressiveIdx] += take;
      moveBudget -= take;
    }
    return { frequencies: adjusted, forcedIndex: null, applied: true };
  }

  return { frequencies: freqs, forcedIndex: null, applied: false };
}

function findCellByLabel(grid, label) {
  if (!grid || !label) return null;
  for (const row of grid) {
    for (const cell of row || []) {
      if (String(cell?.label || "").toLowerCase() === String(label || "").toLowerCase()) return cell;
    }
  }
  return null;
}

function getFrequenciesForHand(cell, handCards) {
  const combos = cell?.combos || [];
  const targetKey = canonicalComboKey(handCards?.[0], handCards?.[1]);
  const exact = combos.find((c) => canonicalComboKey(String(c?.hand || "").slice(0, 2), String(c?.hand || "").slice(2, 4)) === targetKey);
  if (exact?.frequencies) {
    return {
      frequencies: exact.frequencies,
      exactMatch: true,
      matchedHand: exact.hand || null,
      usedFallbackCellFrequencies: false,
    };
  }
  return {
    frequencies: cell?.frequencies || [],
    exactMatch: false,
    matchedHand: null,
    usedFallbackCellFrequencies: true,
  };
}

function formatHistoryAction(entry) {
  const pos = String(entry.position || "").toUpperCase();
  if (entry.action === "call") return `${pos} call`;
  if (entry.action === "check") return `${pos} check`;
  if (entry.action === "fold") return `${pos} fold`;
  return `${pos} ${entry.raiseSize ?? ""}`.trim();
}

function nextActingQueueFromRaiser(raiser, foldedSet) {
  const idx = PREFLOP_ORDER.indexOf(raiser);
  if (idx < 0) return [];
  const out = [];
  for (let i = 1; i < PREFLOP_ORDER.length; i++) {
    const p = PREFLOP_ORDER[(idx + i) % PREFLOP_ORDER.length];
    if (!foldedSet.has(p)) out.push(p);
  }
  return out;
}

function buildHeroActionSetBySpot(raiseCountBefore, heroSeat) {
  // Requested trainer UX presets:
  // - Unopened: Open 2.5 / Call / Fold (SB uses Open 3)
  // - Facing open: Raise 7.5 / Call / Fold (except SB/BB => Bet 10 / Call / Fold)
  // - Facing 3bet+: Raise 25 / Call / Fold
  if (raiseCountBefore <= 0) {
    const seat = String(heroSeat || "").toLowerCase();
    if (seat === "sb") return ["Open 3", "Call", "Fold"];
    return ["Open 2.5", "Call", "Fold"];
  }
  if (raiseCountBefore === 1) {
    const seat = String(heroSeat || "").toLowerCase();
    if (seat === "sb" || seat === "bb") return ["Bet 10", "Call", "Fold"];
    return ["Raise 7.5", "Call", "Fold"];
  }
  return ["Raise 25", "Call", "Fold"];
}

function buildPlayHeroActionSet(raiseCountBefore, heroSeat) {
  const seat = String(heroSeat || "").toLowerCase();
  if (raiseCountBefore <= 0) return ["Open 3", "Fold"];
  if (raiseCountBefore === 1) return [seat === "bb" ? "Raise 10" : "Raise 7.5", "Call", "Fold"];
  if (raiseCountBefore === 2) return ["Raise 25", "Call", "Fold"];
  if (raiseCountBefore === 3) return ["Call", "All in", "Fold"];
  return ["Call", "Fold"];
}

function normalizeActionToken(action) {
  return String(action || "").trim().toLowerCase();
}

function expandHeroValidActionsForUi(validActions = [], heroSeat, raiseCountBefore) {
  const set = new Set((validActions || []).map((a) => normalizeActionToken(a)));
  const seat = String(heroSeat || "").toLowerCase();
  // Blind facing-open UX uses Bet 10, while DB action can be Raise 7.5.
  if (raiseCountBefore === 1 && (seat === "sb" || seat === "bb")) {
    const hasRaiseLike = Array.from(set).some((a) => /raise|3bet|4bet/.test(a));
    if (hasRaiseLike) set.add("bet 10");
  }
  return Array.from(set);
}

function buildVisualSeats(heroSeat, foldedSeats, actionOnSeat, thinkingSeat, stacksBySeat) {
  const heroIdx = Math.max(0, PREFLOP_ORDER.indexOf(heroSeat));
  return VISUAL_SLOTS.map((slot, offset) => {
    const logicalSeat = PREFLOP_ORDER[(heroIdx + offset) % PREFLOP_ORDER.length];
    return {
      ...slot,
      logicalSeat,
      label: logicalSeat.toUpperCase(),
      folded: foldedSeats.has(logicalSeat),
      isHero: logicalSeat === heroSeat,
      isAction: logicalSeat === actionOnSeat,
      isThinking: logicalSeat === thinkingSeat,
      stack: Number(stacksBySeat[logicalSeat] ?? 100),
    };
  });
}

function buildPlayVisualSeats(heroSeat, villainSeat, foldedSeats, actionOnSeat, thinkingSeat, stacksBySeat) {
  return PLAY_VISUAL_SLOTS.map((slot) => {
    const logicalSeat = slot.id === "hero" ? heroSeat : villainSeat;
    return {
      ...slot,
      logicalSeat,
      label: String(logicalSeat || "").toUpperCase(),
      folded: !!logicalSeat && foldedSeats.has(logicalSeat),
      isHero: slot.id === "hero",
      isAction: logicalSeat === actionOnSeat,
      isThinking: logicalSeat === thinkingSeat,
      stack: Number(stacksBySeat?.[logicalSeat] ?? 100),
    };
  });
}

function pickRandomPlayOpponentName() {
  return PLAY_OPPONENT_NAMES[Math.floor(Math.random() * PLAY_OPPONENT_NAMES.length)] || "Opponent";
}

function getPlayOpponentQuoteByName(opponentName) {
  const name = String(opponentName || "").trim().toLowerCase();
  if (!name) return "";
  if (name.includes("anna")) return PLAY_OPPONENT_QUOTE_BY_KEY.anna;
  if (name.includes("peter")) return PLAY_OPPONENT_QUOTE_BY_KEY.peter;
  if (name.includes("mimi")) return PLAY_OPPONENT_QUOTE_BY_KEY.mimi;
  if (name.includes("ryan")) return PLAY_OPPONENT_QUOTE_BY_KEY.ryan;
  return "";
}

function getPlayOpponentKeyByName(opponentName) {
  const name = String(opponentName || "").trim().toLowerCase();
  if (!name) return "";
  if (name.includes("anna")) return "anna";
  if (name.includes("peter")) return "peter";
  if (name.includes("mimi")) return "mimi";
  if (name.includes("ryan")) return "ryan";
  return "";
}

function shouldShowImmediateOpponentThinking(firstActorAction, heroActionLabel) {
  const first = String(firstActorAction || "").trim().toLowerCase();
  const hero = String(heroActionLabel || "").trim().toLowerCase();
  const isHeroCheck = /\bcheck\b/.test(hero);
  const isHeroFold = /\bfold\b/.test(hero);
  const isHeroCall = /\bcall\b/.test(hero);
  const isHeroAggro = /(bet|raise|open|all in|all-in|allin)/.test(hero);
  const firstChecked = /\bcheck\b/.test(first);
  const firstAggro = /(bet|raise|open|all in|all-in|allin)/.test(first);

  if (isHeroFold) return false;
  if (firstChecked && isHeroCheck) return false; // check-check closes action
  if (firstAggro && (isHeroCall || isHeroFold)) return false; // call/fold vs bet usually closes action
  if (isHeroAggro) return true;
  return true;
}

function getHeroActionButtonColor(actionLabel, isBlocked, actionList = []) {
  if (isBlocked) return "#2b2b2b";
  const token = String(actionLabel || "").trim().toLowerCase();
  if (token.includes("fold")) return SOLVER_ACTION_BUTTON_COLORS.fold;
  if (token.includes("call") || token.includes("check")) return SOLVER_ACTION_BUTTON_COLORS.call;
  if (token.includes("bet") || token.includes("raise") || token.includes("open") || token.includes("all in") || token.includes("all-in") || token.includes("allin")) {
    const aggrIdxs = (actionList || [])
      .map((a, i) => ({ token: String(a || "").trim().toLowerCase(), i }))
      .filter(({ token: t }) => t.includes("bet") || t.includes("raise") || t.includes("open") || t.includes("all in") || t.includes("all-in") || t.includes("allin"))
      .map(({ i }) => i);
    const thisIdx = (actionList || []).findIndex((a) => String(a || "").trim().toLowerCase() === token);
    const rank = Math.max(0, aggrIdxs.indexOf(thisIdx));
    if (rank <= 0) return "#077396"; // smallest bet/open = blue
    if (rank === 1) return "#48149E"; // larger = purple
    return "#8F2C7D"; // largest+ = pink
  }
  return SOLVER_ACTION_BUTTON_COLORS.aggressive;
}

function buildPlayOpponentNamesBySeat(seats = [], heroSeat = "") {
  const out = {};
  const available = [...PLAY_OPPONENT_NAMES];
  for (const s of seats) {
    if (s === heroSeat) continue;
    if (!available.length) {
      out[s] = pickRandomPlayOpponentName();
      continue;
    }
    const idx = Math.floor(Math.random() * available.length);
    out[s] = available.splice(idx, 1)[0];
  }
  return out;
}

function toPostflopDisplayAction(rawAction, contribution) {
  const txt = String(rawAction || "").toLowerCase();
  if (!txt) return String(rawAction || "");
  if (txt.includes("check")) return "Check";
  if (txt.includes("fold")) return "Fold";
  if (txt.includes("call")) return `Call ${Number(contribution || 0).toFixed(1)}`;
  if (txt.includes("raise")) return `Raise ${Number(contribution || 0).toFixed(1)}`;
  if (txt.includes("bet")) return `Bet ${Number(contribution || 0).toFixed(1)}`;
  return String(rawAction || "");
}

function buildPostflopHeroTurnConfig(dbActions = [], dbFreqs = []) {
  const options = [...(dbActions || [])];
  const uiValid = [];
  const uiPctMap = {};
  for (let i = 0; i < options.length; i++) {
    const action = String(options[i] || "");
    const f = Number(dbFreqs?.[i] ?? 0);
    uiPctMap[action.trim().toLowerCase()] = f * 100;
    if (f > 0) uiValid.push(action);
  }
  return { options, validActions: uiValid, actionPctMap: uiPctMap };
}

function getDbActionIndexFromUiAction(uiAction, dbActions = []) {
  const key = String(uiAction || "").trim().toLowerCase();
  return (dbActions || []).findIndex((a) => String(a || "").trim().toLowerCase() === key);
}

function getPlayPostflopHeroOptions(streetPhase, facingBet, runtime, heroSeat) {
  const street = normalizeStreetKey(streetPhase);
  const stack = Number(runtime?.stacks?.[heroSeat] || 0);
  const alreadyIn = Number(runtime?.invested?.[heroSeat] || 0);
  const currentBet = Number(runtime?.currentBet || 0);
  const mainPot = Math.max(0.1, Number(runtime?.mainPot || 0));
  const defs = [];
  if (facingBet) {
    defs.push(
      { key: "call", label: "Call" },
      { key: "raise3x", label: "Raise 3x" },
      { key: "raise5x", label: "Raise 5x" },
      { key: "fold", label: "Fold" }
    );
  } else if (street === "flop") {
    defs.push(
      { key: "check", label: "Check" },
      { key: "bet33", label: "Bet 33%" },
      { key: "bet50", label: "Bet 50%" }
    );
  } else {
    defs.push(
      { key: "check", label: "Check" },
      { key: "bet50", label: "Bet 50%" },
      { key: "bet100", label: "Bet 100%" },
      { key: "bet150", label: "Bet 150%" }
    );
  }

  const contributionFor = (defKey, label) => {
    const txt = String(label || "").toLowerCase();
    if (defKey === "raise3x") return Math.max(0, currentBet * 3 - alreadyIn);
    if (defKey === "raise5x") return Math.max(0, currentBet * 5 - alreadyIn);
    if (txt.includes("bet")) {
      const n = extractNumber(label);
      if (txt.includes("%")) return Math.max(0, (n / 100) * mainPot - alreadyIn);
      return Math.max(0, n - alreadyIn);
    }
    if (txt.includes("all")) return Math.max(0, stack);
    return 0;
  };

  const options = [];
  const templateMap = {};
  for (const def of defs) {
    const key = String(def?.key || "");
    let label = String(def?.label || "");
    if (key === "raise3x" || key === "raise5x") {
      const raiseTarget = key === "raise3x" ? currentBet * 3 : currentBet * 5;
      label = `Raise ${Number(raiseTarget || 0).toFixed(1)}`;
    }
    if (label.toLowerCase().includes("bet") && label.includes("%")) {
      const pct = Math.max(0, Number(extractNumber(label) || 0)) / 100;
      const chips = Number((pct * mainPot).toFixed(1));
      label = `Bet ${chips.toFixed(1)}`;
    }
    let finalLabel = label;
    let finalKey = key;
    if (/(raise|bet|open|all in|all-in|allin)/i.test(label)) {
      const c = contributionFor(key, label);
      if (c >= stack * 0.6) {
        finalLabel = "All in";
        finalKey = "allin";
      }
    }
    if (!options.includes(finalLabel)) {
      options.push(finalLabel);
      templateMap[finalLabel] = finalKey;
    }
  }
  return { options, templateMap };
}

function mapPlayHeroActionToDbAction(heroUiAction, dbActions = [], templateHint = null) {
  const ui = String(heroUiAction || "").trim().toLowerCase();
  const actions = dbActions || [];
  const direct = actions.find((a) => String(a || "").trim().toLowerCase() === ui);
  if (direct) return direct;

  const isAggressive = (txt) => /raise|bet|open|all in|all-in|allin/i.test(String(txt || ""));
  const aggr = actions
    .map((a, i) => ({ a, i, n: extractNumber(a) }))
    .filter(({ a }) => isAggressive(a))
    .sort((x, y) => (x.n - y.n) || (x.i - y.i));

  const hint = String(templateHint || "").trim().toLowerCase();
  if (hint) {
    if (hint === "fold") return actions.find((a) => /fold/i.test(String(a || ""))) || null;
    if (hint === "check") return actions.find((a) => /check/i.test(String(a || ""))) || null;
    if (hint === "call") return actions.find((a) => /call/i.test(String(a || ""))) || null;
    if (hint === "allin") return actions.find((a) => /all in|all-in|allin/i.test(String(a || ""))) || aggr[aggr.length - 1]?.a || null;
    if (hint === "raise3x") return aggr[0]?.a || null;
    if (hint === "raise5x") return aggr[Math.min(1, aggr.length - 1)]?.a || aggr[aggr.length - 1]?.a || null;
    if (hint === "bet33" || hint === "bet50") return aggr[0]?.a || null;
    if (hint === "bet100") return aggr[Math.min(1, aggr.length - 1)]?.a || aggr[aggr.length - 1]?.a || null;
    if (hint === "bet150") return aggr[Math.min(2, aggr.length - 1)]?.a || aggr[aggr.length - 1]?.a || null;
  }

  if (ui.includes("fold")) return actions.find((a) => /fold/i.test(String(a || ""))) || null;
  if (ui.includes("check")) return actions.find((a) => /check/i.test(String(a || ""))) || null;
  if (ui.includes("call")) return actions.find((a) => /call/i.test(String(a || ""))) || null;
  if (ui.includes("all")) return actions.find((a) => /all in|all-in|allin/i.test(String(a || ""))) || aggr[aggr.length - 1]?.a || null;

  if (ui.includes("raise") || ui.includes("bet")) {
    const n = extractNumber(ui);
    if (n <= 0 || !aggr.length) return aggr[0]?.a || null;
    const closest = [...aggr].sort((a, b) => Math.abs(a.n - n) - Math.abs(b.n - n))[0];
    return closest?.a || null;
  }

  return aggr[0]?.a || null;
}

function toPostflopHeroUiActionLabel(dbAction) {
  const raw = String(dbAction || "").trim();
  if (!raw) return raw;
  const lower = raw.toLowerCase();
  const n = extractNumber(raw);
  if ((lower.startsWith("bet") || lower.startsWith("raise")) && Number.isFinite(n) && n > 0) {
    const shown = Number((n / 2).toFixed(1));
    const prefix = lower.startsWith("raise") ? "Raise" : "Bet";
    return `${prefix} ${shown.toFixed(1)}`;
  }
  return raw;
}

function buildHeroUiActionMapFromDb(dbActions = []) {
  const uiToDb = {};
  const optionsUi = [];
  for (const action of dbActions || []) {
    const ui = toPostflopHeroUiActionLabel(action);
    optionsUi.push(ui);
    uiToDb[String(ui || "").trim().toLowerCase()] = String(action || "");
  }
  return { optionsUi, uiToDb };
}

function getActionChildNodeId(v6, actionIndex) {
  if (!v6 || actionIndex == null || actionIndex < 0) return null;
  const child = v6?.actionChildren?.[actionIndex];
  if (!child) return null;
  if (typeof child === "string") return child;
  if (typeof child === "object" && child?.nodeId) return String(child.nodeId);
  return null;
}

/** V6 terminal children are often 32-char zero ids - truthy but not playable decisions. */
function isZeroNodeId(nodeId) {
  const id = String(nodeId || "");
  return id.length === 32 && /^0+$/.test(id);
}

function getChanceChildNodeId(v6, dealtCard) {
  const card = String(dealtCard || "").trim();
  if (!card) return null;
  const children = v6?.chanceChildren;
  if (!children || typeof children !== "object") return null;
  const direct = children[card] || children[card.toLowerCase()] || children[card.toUpperCase()];
  if (direct) {
    if (typeof direct === "string") return direct;
    if (typeof direct === "object" && direct?.nodeId) return String(direct.nodeId);
  }
  return null;
}

function normalizeStreetKey(streetPhase) {
  const s = String(streetPhase || "").toLowerCase();
  if (s === "preflop" || s === "flop" || s === "turn" || s === "river") return s;
  return "preflop";
}

function hasAllInSeat(runtime) {
  const active = PREFLOP_ORDER.filter((s) => !runtime?.folded?.has(s));
  if (active.length < 2) return false;
  return active.every((s) => Number(runtime?.stacks?.[s] || 0) <= 1e-9);
}

function detectPostflopSpotFromHistory(history = []) {
  const sig = (history || []).filter((h) => String(h?.action || "").toLowerCase() !== "fold");
  if (sig.length === 2) {
    const [a1, a2] = sig;
    if (a1.action === "open" && a2.action === "call" && SEAT_CODE[a1.position] && SEAT_CODE[a2.position]) {
      return `${SEAT_CODE[a1.position]}open${SEAT_CODE[a2.position]}call`;
    }
  }
  if (sig.length === 3) {
    const [a1, a2, a3] = sig;
    if (a1.action === "open" && a2.action === "3bet" && a3.action === "call" && a1.position === a3.position && SEAT_CODE[a1.position] && SEAT_CODE[a2.position]) {
      return `${SEAT_CODE[a2.position]}3bet${SEAT_CODE[a1.position]}call`;
    }
  }
  if (sig.length === 4) {
    const [a1, a2, a3, a4] = sig;
    if (
      a1.action === "open" &&
      a2.action === "3bet" &&
      a3.action === "4bet" &&
      a4.action === "call" &&
      a3.position === a1.position &&
      a4.position === a2.position &&
      SEAT_CODE[a3.position] &&
      SEAT_CODE[a4.position]
    ) {
      return `${SEAT_CODE[a3.position]}4bet${SEAT_CODE[a4.position]}call`;
    }
  }
  return null;
}

function remapSpotForLegacyDb(spot) {
  const key = String(spot || "").trim();
  if (!key) return key;
  const explicitRemaps = {
    LO4betHIcall: "HI4betCOcall",
    LO4betCOcall: "HI4betCOcall",
    LO4betBTNcall: "HI4betBTNcall",
    LO4betSBcall: "HI4betSBcall",
    LO4betBBcall: "HI4betBBcall",
  };
  if (explicitRemaps[key]) return explicitRemaps[key];
  return LEGACY_SPOT_LOOKUP_REMAP[key] || key;
}

const SEAT_FROM_CODE = { LO: "lj", HI: "hj", CO: "co", BTN: "btn", SB: "sb", BB: "bb" };
const SPOT_ROLES_RE = /^(LO|HI|CO|BTN|SB|BB)(open|3bet|4bet)(LO|HI|CO|BTN|SB|BB)call$/i;

function parsePostflopSpotRoles(spot) {
  const m = String(spot || "").trim().match(SPOT_ROLES_RE);
  if (!m) return null;
  const aggressorCode = m[1].toUpperCase();
  const potKind = String(m[2] || "").toLowerCase();
  const callerCode = m[3].toUpperCase();
  const aggressorSeat = SEAT_FROM_CODE[aggressorCode];
  const callerSeat = SEAT_FROM_CODE[callerCode];
  if (!aggressorSeat || !callerSeat || !potKind) return null;
  return { aggressorCode, potKind, callerCode, aggressorSeat, callerSeat };
}

function buildPostflopSpotName(aggressorSeat, potKind, callerSeat) {
  return `${SEAT_CODE[aggressorSeat]}${potKind}${SEAT_CODE[callerSeat]}call`;
}

function callerIsIpVsAggressor(aggressorSeat, callerSeat) {
  return POSTFLOP_ORDER.indexOf(callerSeat) > POSTFLOP_ORDER.indexOf(aggressorSeat);
}

/**
 * Alternate open/3bet/4bet-call spots that keep the same aggressor and the
 * caller's IP/OOP. Table seats stay as dealt; these names are lookup-only.
 * IP caller: walk later seats toward BTN. OOP caller: start at SB, walk away from BTN.
 */
function buildIpOopPreservingSpotFallbacks(derivedSpot) {
  const parsed = parsePostflopSpotRoles(derivedSpot);
  if (!parsed) return [];
  const { aggressorSeat, potKind, callerSeat } = parsed;
  const out = [];
  const pushSeat = (seat) => {
    if (!seat || seat === aggressorSeat || seat === callerSeat) return;
    out.push(buildPostflopSpotName(aggressorSeat, potKind, seat));
  };

  if (callerIsIpVsAggressor(aggressorSeat, callerSeat)) {
    const towardBtn = ["lj", "hj", "co", "btn"];
    const start = towardBtn.indexOf(callerSeat);
    const walk =
      start >= 0
        ? towardBtn.slice(start + 1)
        : towardBtn.filter((s) => POSTFLOP_ORDER.indexOf(s) > POSTFLOP_ORDER.indexOf(callerSeat));
    for (const seat of walk) {
      if (!callerIsIpVsAggressor(aggressorSeat, seat)) continue;
      pushSeat(seat);
    }
  } else {
    for (const seat of ["sb", "bb", "lj", "hj", "co"]) {
      if (callerIsIpVsAggressor(aggressorSeat, seat)) continue;
      pushSeat(seat);
    }
  }
  return out;
}

function buildPostflopLookupSpotCandidates(derivedSpot) {
  const derivedList = [derivedSpot, ...buildIpOopPreservingSpotFallbacks(derivedSpot)].filter(Boolean);
  const out = [];
  const seen = new Set();
  for (const derived of derivedList) {
    const spot = remapSpotForLegacyDb(derived);
    if (!spot || seen.has(spot)) continue;
    seen.add(spot);
    out.push({ derived, spot });
  }
  return out;
}

function v6RootLooksUsable(data) {
  if (!data) return false;
  const kind = data?.v6?.kind;
  if (kind && kind !== "decision" && kind !== "chance") return false;
  const actions = Array.isArray(data?.actions) ? data.actions : [];
  if (kind === "decision" && !actions.length) return false;
  return true;
}

function decisionHasUsableFrequencies(decision) {
  const freqs = decision?.frequencies;
  if (!Array.isArray(freqs) || !freqs.length) return false;
  return freqs.some((f) => Number(f) > 0);
}

function chooseValidFlopFrom184(dealtBySeat = {}) {
  const used = new Set(Object.values(dealtBySeat).flat().filter(Boolean).map((c) => String(c)));
  const candidates = (TRAINER_ALLOWED_FLOPS || []).filter((f) => {
    const cards = String(f || "").match(/.{1,2}/g) || [];
    return cards.length === 3 && cards.every((c) => !used.has(c));
  });
  if (!candidates.length) return null;
  return candidates[Math.floor(Math.random() * candidates.length)];
}

function chooseNextStreetCard(dealtBySeat = {}, boardCards = []) {
  const used = new Set([
    ...Object.values(dealtBySeat).flat().filter(Boolean).map((c) => String(c)),
    ...(boardCards || []).filter(Boolean).map((c) => String(c)),
  ]);
  const ranks = "23456789TJQKA".split("");
  const suits = "cdhs".split("");
  const deck = [];
  for (const r of ranks) {
    for (const s of suits) {
      const card = `${r}${s}`;
      if (!used.has(card)) deck.push(card);
    }
  }
  if (!deck.length) return null;
  return deck[Math.floor(Math.random() * deck.length)];
}

function chooseRandomFlopFromDeck(dealtBySeat = {}) {
  const board = [];
  for (let i = 0; i < 3; i++) {
    const next = chooseNextStreetCard(dealtBySeat, board);
    if (!next) return null;
    board.push(next);
  }
  return board.join("");
}

// ─── Drill settings (GTO-Wizard-style trainer configuration) ────────────────
const DRILL_SETTINGS_KEY = "pm_trainer_drill_v1";
const DRILL_DEFAULTS = {
  street: "full",        // full | preflop | flop | turn | river
  potType: "any",        // any | srp | 3bet | 4bet (postflop drills only)
  heroPosition: "any",   // any | lj | hj | co | btn | sb | bb
  relPosition: "any",    // any | ip | oop (postflop drills only)
  hands: [],             // 169-grid labels e.g. ["AKo","TT"]; empty = any hand
  timebank: 10,          // seconds; 0 = off
  pauseMode: "every",    // every | mistake | never
  sessionLength: 0,      // hands per session; 0 = endless
  analysisMode: "during", // during | after (review the whole hand once it ends)
};
const STREET_RANK = { flop: 0, turn: 1, river: 2 };
const DRILL_STREET_LABELS = { full: "Full Hand", preflop: "Preflop", flop: "Flop", turn: "Turn", river: "River" };

function loadDrillSettings() {
  try {
    const raw = window.localStorage.getItem(DRILL_SETTINGS_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return { ...DRILL_DEFAULTS, ...(parsed && typeof parsed === "object" ? parsed : {}), hands: Array.isArray(parsed?.hands) ? parsed.hands : [] };
  } catch {
    return { ...DRILL_DEFAULTS };
  }
}

function saveDrillSettings(settings) {
  try {
    window.localStorage.setItem(DRILL_SETTINGS_KEY, JSON.stringify(settings));
  } catch (_) {}
}

function handGridLabel(rowIdx, colIdx) {
  const r1 = SOLVER_RANKS[rowIdx];
  const r2 = SOLVER_RANKS[colIdx];
  if (rowIdx === colIdx) return `${r1}${r1}`;
  return rowIdx < colIdx ? `${r1}${r2}s` : `${r2}${r1}o`;
}

/** All specific 2-card combos for a 169-grid label, e.g. "AKs" -> ["AsKs",...]. */
function combosForHandLabel(label) {
  const out = [];
  const txt = String(label || "");
  if (txt.length === 2 && txt[0] === txt[1]) {
    const r = txt[0];
    for (let i = 0; i < SOLVER_SUITS.length; i++) {
      for (let j = i + 1; j < SOLVER_SUITS.length; j++) out.push([`${r}${SOLVER_SUITS[i]}`, `${r}${SOLVER_SUITS[j]}`]);
    }
    return out;
  }
  if (txt.length === 3) {
    const r1 = txt[0], r2 = txt[1];
    if (txt[2] === "s") {
      for (const s of SOLVER_SUITS) out.push([`${r1}${s}`, `${r2}${s}`]);
    } else {
      for (const s1 of SOLVER_SUITS) {
        for (const s2 of SOLVER_SUITS) {
          if (s1 !== s2) out.push([`${r1}${s1}`, `${r2}${s2}`]);
        }
      }
    }
  }
  return out;
}

/** Sample hero's exact cards from the drill hand filter. Returns [c1, c2] or null for "deal random". */
function sampleHeroCardsFromFilter(handsFilter) {
  const labels = Array.isArray(handsFilter) ? handsFilter.filter(Boolean) : [];
  if (!labels.length) return null;
  const label = labels[Math.floor(Math.random() * labels.length)];
  const combos = combosForHandLabel(label);
  if (!combos.length) return null;
  return combos[Math.floor(Math.random() * combos.length)];
}

/** Weighted sample that skips Fold while a drill autopilot is dealing toward a later street. */
function pickByWeightsNoFold(actions, freqs) {
  const adjusted = (freqs || []).map((f, i) => (/fold/i.test(String(actions?.[i] || "")) ? 0 : Math.max(0, Number(f || 0))));
  const hasAlternative = adjusted.some((f) => f > 0);
  return pickByWeights(actions, hasAlternative ? adjusted : freqs);
}

/** Realistic opponent think time: mostly quick, occasionally tanking (capped at 6s). */
function realisticThinkDelayMs() {
  const base = 800 + Math.random() * 1700;
  const tank = Math.random() < 0.12 ? 1500 + Math.random() * 2500 : 0;
  return Math.min(6000, Math.round(base + tank));
}

/** Whether the drill's pot-type filter matches a detected spot key like "BTNopenBBcall". */
function spotMatchesPotType(spotKey, potType) {
  const key = String(spotKey || "");
  if (!key || !potType || potType === "any") return true;
  if (potType === "srp") return key.includes("open");
  if (potType === "3bet") return key.includes("3bet");
  if (potType === "4bet") return key.includes("4bet");
  return true;
}

/** Human label of the highest-frequency action in an action->pct map, e.g. "Check (72%)". */
function describeBestActionFromPctMap(pctMap = {}) {
  let bestKey = null;
  let bestPct = -1;
  for (const [key, pct] of Object.entries(pctMap || {})) {
    const p = Number(pct || 0);
    if (p > bestPct) {
      bestPct = p;
      bestKey = key;
    }
  }
  if (!bestKey || bestPct <= 0) return "see analysis";
  const label = bestKey.charAt(0).toUpperCase() + bestKey.slice(1);
  return `${label} (${Math.round(bestPct)}%)`;
}

/** For a heads-up postflop pot, is hero in position (acts last)? */
function heroIsIpPostflop(heroSeat, activeSeats) {
  const others = (activeSeats || []).filter((s) => s !== heroSeat);
  if (!others.length) return true;
  const heroIdx = POSTFLOP_ORDER.indexOf(heroSeat);
  return others.every((s) => POSTFLOP_ORDER.indexOf(s) < heroIdx);
}

async function fetchV6Grid({ spot, flop, nodeId }) {
  const baseParams = {
    spot,
    versionId: SOLVER_V6_VERSION_ID,
    flop,
    nodeId,
    trainer: "1", // Trainer is free: exempt from solver login/quota rules server-side.
  };
  const attempts = [
    {
      ...baseParams,
      mode: TRAINER_CONTEXT.mode,
      tableSize: TRAINER_CONTEXT.tableSize,
      stackSize: TRAINER_CONTEXT.stackSize,
      openSize: TRAINER_CONTEXT.openSize,
    },
    // Legacy cash fallback: keep exact same spot/action/seat path, drop context filters.
    {
      ...baseParams,
    },
  ];
  let lastError = null;
  for (const params of attempts) {
    const qs = new URLSearchParams(params);
    const res = await fetch(`${SOLVER_API_URL}/api/v6/grid?${qs.toString()}`, { credentials: "include" });
    const data = await res.json().catch(() => ({}));
    if (res.ok && data?.success) return data;
    lastError = new Error(data?.error || `HTTP ${res.status}`);
  }
  throw lastError || new Error("V6 grid lookup failed.");
}

function Card({ card }) {
  const ap = React.useContext(AppearanceContext);
  const rank = card?.[0] || "?";
  const suit = card?.[1] || "s";
  const color = suitColorFor(ap, suit);
  const face = cardFaceStyle(ap, color);
  const symbol = SUIT_SYMBOLS[suit] || "";
  return (
    <Box
      sx={{
        width: { xs: 38, sm: 46 },
        height: { xs: 54, sm: 62 },
        borderRadius: "8px",
        border: `2px solid ${face.borderColor}`,
        backgroundColor: face.backgroundColor,
        display: "grid",
        placeItems: "center",
      }}
    >
      <Typography sx={{ fontSize: { xs: 13, sm: 15 }, color, fontWeight: 700 }}>{`${rank}${symbol}`}</Typography>
    </Box>
  );
}

function MiniCard({ card }) {
  const ap = React.useContext(AppearanceContext);
  const rank = card?.[0] || "?";
  const suit = card?.[1] || "s";
  const color = suitColorFor(ap, suit);
  const face = cardFaceStyle(ap, color);
  const symbol = SUIT_SYMBOLS[suit] || "";
  return (
    <Box
      sx={{
        width: 26,
        height: 36,
        borderRadius: "5px",
        border: `1.5px solid ${face.borderColor}`,
        backgroundColor: face.backgroundColor,
        display: "grid",
        placeItems: "center",
        flexShrink: 0,
      }}
    >
      <Typography sx={{ fontSize: 11, color, fontWeight: 700 }}>{`${rank}${symbol}`}</Typography>
    </Box>
  );
}

function HoleCard({ card }) {
  const ap = React.useContext(AppearanceContext);
  const rank = card?.[0] || "?";
  const suit = card?.[1] || "s";
  const color = suitColorFor(ap, suit);
  const face = cardFaceStyle(ap, color);
  const symbol = SUIT_SYMBOLS[suit] || "";
  return (
    <Box
      sx={{
        width: { xs: 34, sm: 42 },
        height: { xs: 50, sm: 60 },
        borderRadius: "8px",
        border: `2px solid ${face.borderColor}`,
        backgroundColor: face.backgroundColor,
        display: "grid",
        placeItems: "center",
      }}
    >
      <Typography sx={{ fontSize: { xs: 13, sm: 16 }, color, fontWeight: 700 }}>{`${rank}${symbol}`}</Typography>
    </Box>
  );
}

/**
 * Shared card-back surface. The grayscale filter is applied only to the logo
 * layer (a filter on the whole card previously washed out the colour tint).
 */
function CardBackSurface({ width, height, radius, border, logoSize }) {
  const ap = React.useContext(AppearanceContext);
  const back = CARD_BACK_OPTIONS[ap.cardBack] || CARD_BACK_OPTIONS.graphite;
  const pattern = ap.cardBackPattern || "logo";
  return (
    <Box
      sx={{
        width,
        height,
        borderRadius: radius,
        border: `${border}px solid ${back.border}`,
        backgroundColor: "#15161a",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {pattern === "logo" && (
        <>
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              backgroundImage: `url(${logoWithText})`,
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
              backgroundSize: `${logoSize} auto`,
              filter: "grayscale(1)",
              opacity: 0.85,
            }}
          />
          <Box sx={{ position: "absolute", inset: 0, backgroundColor: back.tint }} />
        </>
      )}
      {pattern === "stripes" && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundColor: hexToRgba(back.accent, 0.22),
            backgroundImage: `repeating-linear-gradient(135deg, ${hexToRgba(back.accent, 0.85)} 0 3px, transparent 3px 8px)`,
          }}
        />
      )}
      {pattern === "dots" && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundColor: hexToRgba(back.accent, 0.18),
            backgroundImage: `radial-gradient(${hexToRgba(back.accent, 0.9)} 1.2px, transparent 1.4px)`,
            backgroundSize: "7px 7px",
            backgroundPosition: "2px 3px",
          }}
        />
      )}
      {pattern === "weave" && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundColor: hexToRgba(back.accent, 0.16),
            backgroundImage: `repeating-linear-gradient(45deg, ${hexToRgba(back.accent, 0.6)} 0 2px, transparent 2px 7px), repeating-linear-gradient(-45deg, ${hexToRgba(back.accent, 0.6)} 0 2px, transparent 2px 7px)`,
          }}
        />
      )}
      {pattern === "solid" && (
        <Box
          sx={{
            position: "absolute",
            inset: 2,
            borderRadius: "4px",
            backgroundColor: hexToRgba(back.accent, 0.75),
            border: "1px solid rgba(255,255,255,0.18)",
          }}
        />
      )}
    </Box>
  );
}

function CardBack() {
  return (
    <CardBackSurface
      width={{ xs: 34, sm: 42 }}
      height={{ xs: 50, sm: 60 }}
      radius="8px"
      border={2}
      logoSize="80%"
    />
  );
}

function SmallHoleCard({ card }) {
  const ap = React.useContext(AppearanceContext);
  const rank = card?.[0] || "?";
  const suit = card?.[1] || "s";
  const color = suitColorFor(ap, suit);
  const face = cardFaceStyle(ap, color);
  const symbol = SUIT_SYMBOLS[suit] || "";
  return (
    <Box
      sx={{
        width: { xs: 24, sm: 28 },
        height: { xs: 34, sm: 38 },
        borderRadius: "6px",
        border: `1.5px solid ${face.borderColor}`,
        backgroundColor: face.backgroundColor,
        display: "grid",
        placeItems: "center",
      }}
    >
      <Typography sx={{ fontSize: { xs: 10, sm: 11 }, color, fontWeight: 700, lineHeight: 1 }}>{`${rank}${symbol}`}</Typography>
    </Box>
  );
}

function SmallCardBack() {
  return (
    <CardBackSurface
      width={{ xs: 24, sm: 28 }}
      height={{ xs: 34, sm: 38 }}
      radius="6px"
      border={1.5}
      logoSize="78%"
    />
  );
}

const TRAINER_SOLVE_HANDOFF_KEY = "pm_trainer_solve_handoff_v1";

function trainerActionColor(actionLabel, index, actionList = []) {
  return getHeroActionButtonColor(actionLabel, false, actionList);
}

/** Mini holecards matching Solver combo breakdown (rank + suit symbol, suit-coloured). */
function TrainerComboMiniHand({ combo }) {
  const prefs = loadSolverPrefs();
  const suitColors = (SOLVER_DECK_OPTIONS[prefs.deck] || SOLVER_DECK_OPTIONS.four).colors;
  if (!combo || String(combo).length < 4) {
    return <Typography sx={{ color: COLORS.border, fontSize: "7px" }}>--</Typography>;
  }
  const hand = String(combo);
  const c1 = hand.slice(0, 2);
  const c2 = hand.slice(2, 4);
  return (
    <Box sx={{ display: "flex", gap: 0.2, flexShrink: 0 }}>
      {[c1, c2].map((c, i) => (
        <Box
          key={`${hand}-${i}`}
          sx={{
            width: 14,
            height: 20,
            borderRadius: "3px",
            border: `1px solid ${COLORS.border}`,
            backgroundColor: COLORS.black,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            lineHeight: 1,
          }}
        >
          <Typography sx={{ color: suitColors[c[1]] || COLORS.textMuted, fontSize: "8px", fontWeight: 700, lineHeight: 1 }}>
            {c[0]}
          </Typography>
          <Typography sx={{ color: suitColors[c[1]] || COLORS.textMuted, fontSize: "7px", lineHeight: 1 }}>
            {SUIT_SYMBOLS[c[1]] || "?"}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function normalizeTrainerFreqs(frequencies = [], actionCount = 0) {
  const len = Math.max(actionCount, (frequencies || []).length);
  const raw = Array.from({ length: len }, (_, i) => Math.max(0, Number(frequencies?.[i] || 0)));
  const sum = raw.reduce((s, v) => s + v, 0);
  if (sum <= 0) return raw.map(() => 0);
  // Grid data may be 0-1 fractions or already percentages.
  if (sum > 1.5) return raw.map((v) => v / sum);
  return raw;
}

function TrainerMiniRangePanel({ actions = [], grid = null, heroLabel = "", boardCards = [] }) {
  const [selected, setSelected] = React.useState(null);
  React.useEffect(() => {
    if (!heroLabel || !Array.isArray(grid)) {
      setSelected(null);
      return;
    }
    for (let r = 0; r < (grid || []).length; r++) {
      for (let c = 0; c < (grid[r] || []).length; c++) {
        if (String(grid[r][c]?.label || "").toLowerCase() === String(heroLabel).toLowerCase()) {
          setSelected({ row: r, col: c, cell: grid[r][c] });
          return;
        }
      }
    }
    setSelected(null);
  }, [grid, heroLabel]);

  const selectedCell = selected?.cell || null;
  const comboRows = React.useMemo(() => {
    if (!selectedCell) return [];
    const label = selectedCell.label;
    const listed = Array.isArray(selectedCell.combos) ? selectedCell.combos : [];
    const boardSet = new Set((boardCards || []).filter(Boolean));
    if (listed.length) {
      return listed.map((combo) => {
        const hand = String(combo?.hand || "");
        const c1 = hand.slice(0, 2);
        const c2 = hand.slice(2, 4);
        const blocked = boardSet.has(c1) || boardSet.has(c2);
        return {
          hand,
          frequencies: normalizeTrainerFreqs(combo?.frequencies || selectedCell.frequencies || [], actions.length),
          blocked,
        };
      });
    }
    return combosForHandLabel(label).map(([a, b]) => {
      const hand = `${a}${b}`;
      const blocked = boardSet.has(a) || boardSet.has(b);
      return {
        hand,
        frequencies: normalizeTrainerFreqs(selectedCell.frequencies || [], actions.length),
        blocked,
      };
    });
  }, [selectedCell, actions.length, boardCards]);

  if (!Array.isArray(grid) || !grid.length) {
    return (
      <Typography sx={{ fontSize: 12, color: COLORS.textMuted }}>
        Full range unavailable for this node.
      </Typography>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 1.25, alignItems: "stretch" }}>
      <Box sx={{ flex: "0 0 auto", width: { xs: "100%", md: 280 }, maxWidth: 320 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(13, 1fr)",
            gap: "1px",
            backgroundColor: COLORS.black,
            border: `1px solid ${COLORS.border}`,
            borderRadius: "6px",
            overflow: "hidden",
          }}
        >
          {(grid || []).map((row, rowIdx) =>
            (row || []).map((cell, colIdx) => {
              const freqs = normalizeTrainerFreqs(cell?.frequencies || [], actions.length);
              const inRange = !!cell?.inRange || freqs.some((f) => f > 0.001);
              const isSelected = selected?.row === rowIdx && selected?.col === colIdx;
              const isHero = !!heroLabel && String(cell?.label || "").toLowerCase() === String(heroLabel).toLowerCase();
              let left = 0;
              return (
                <Box
                  key={`mini-${rowIdx}-${colIdx}`}
                  onClick={() => setSelected({ row: rowIdx, col: colIdx, cell })}
                  sx={{
                    aspectRatio: "1",
                    position: "relative",
                    backgroundColor: inRange ? COLORS.cardBg : "#161616",
                    cursor: "pointer",
                    outline: isSelected ? `2px solid ${COLORS.white}` : isHero ? `1px solid ${COLORS.gold}` : "none",
                    outlineOffset: "-1px",
                    zIndex: isSelected || isHero ? 2 : 1,
                  }}
                >
                  {inRange &&
                    freqs.map((f, idx) => {
                      if (f <= 0) return null;
                      const pct = f * 100;
                      const el = (
                        <Box
                          key={`bar-${idx}`}
                          sx={{
                            position: "absolute",
                            left: `${left}%`,
                            top: 0,
                            bottom: 0,
                            width: `${pct}%`,
                            backgroundColor: trainerActionColor(actions[idx], idx, actions),
                            opacity: 0.9,
                          }}
                        />
                      );
                      left += pct;
                      return el;
                    })}
                  <Typography
                    sx={{
                      position: "relative",
                      zIndex: 1,
                      fontSize: { xs: 5.5, sm: 6.5 },
                      fontWeight: 700,
                      color: inRange ? COLORS.white : COLORS.textMuted,
                      textAlign: "center",
                      lineHeight: 1,
                      height: "100%",
                      display: "grid",
                      placeItems: "center",
                      textShadow: "0 1px 2px rgba(0,0,0,0.75)",
                    }}
                  >
                    {cell?.label || ""}
                  </Typography>
                </Box>
              );
            })
          )}
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.6 }}>
          {(actions || []).map((action, idx) => (
            <Box key={`legend-${action}-${idx}`} sx={{ display: "flex", alignItems: "center", gap: 0.35 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: "2px", backgroundColor: trainerActionColor(action, idx, actions) }} />
              <Typography sx={{ fontSize: 9, color: COLORS.textMuted }}>{action}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, border: `1px solid ${COLORS.border}`, borderRadius: "8px", backgroundColor: "#1b1b1b", p: 0.9, maxHeight: { xs: 180, md: 220 }, overflow: "auto" }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: COLORS.text, mb: 0.5 }}>
          {selectedCell ? `Combos, ${selectedCell.label}` : "Select a hand"}
        </Typography>
        {!comboRows.length ? (
          <Typography sx={{ fontSize: 11, color: COLORS.textMuted }}>No combo breakdown for this cell.</Typography>
        ) : (
          comboRows.map((row) => {
            let left = 0;
            return (
              <Box key={row.hand} sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.45, opacity: row.blocked ? 0.35 : 1 }}>
                <TrainerComboMiniHand combo={row.hand} />
                <Box sx={{ flex: 1, height: 12, borderRadius: "3px", backgroundColor: "#111", position: "relative", overflow: "hidden" }}>
                  {row.frequencies.map((f, idx) => {
                    if (f <= 0) return null;
                    const pct = f * 100;
                    const el = (
                      <Box
                        key={`${row.hand}-f-${idx}`}
                        sx={{
                          position: "absolute",
                          left: `${left}%`,
                          top: 0,
                          bottom: 0,
                          width: `${pct}%`,
                          backgroundColor: trainerActionColor(actions[idx], idx, actions),
                        }}
                      />
                    );
                    left += pct;
                    return el;
                  })}
                </Box>
              </Box>
            );
          })
        )}
      </Box>
    </Box>
  );
}

function PokerChip({ size = 16, alt = false, sx = {} }) {
  const ap = React.useContext(AppearanceContext);
  const chip = CHIP_COLOR_OPTIONS[ap.chipColor] || CHIP_COLOR_OPTIONS.gold;
  const fill = alt ? chip.a : chip.b;
  const striped = ap.chipStyle === "striped";
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: "50%",
        border: `1px solid ${chip.border}`,
        background: striped
          ? `radial-gradient(circle at 50% 50%, ${fill} 0 60%, transparent 61%), repeating-conic-gradient(${chip.border} 0deg 20deg, ${fill} 20deg 60deg)`
          : fill,
        boxShadow: `0 0 0 1px ${hexToRgba(chip.border, 0.18)}`,
        ...sx,
      }}
    />
  );
}

function DrillChip({ active, label, onClick, disabled = false, locked = false }) {
  return (
    <Button
      size="small"
      onClick={onClick}
      disabled={disabled && !locked}
      startIcon={locked ? <LockOutlinedIcon sx={{ fontSize: 12 }} /> : null}
      sx={{
        minWidth: "unset",
        px: 0.9,
        py: 0.25,
        borderRadius: "6px",
        border: `1px solid ${active ? COLORS.white : locked ? "#555" : COLORS.border}`,
        backgroundColor: active ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.02)",
        color: locked ? COLORS.gold : disabled ? "#555" : active ? COLORS.white : COLORS.textMuted,
        fontSize: "10.5px",
        fontWeight: 400,
        textTransform: "none",
        lineHeight: 1.2,
        "& .MuiButton-startIcon": { mr: 0.35 },
        "&:hover": locked
          ? { borderColor: COLORS.gold, color: COLORS.gold, backgroundColor: "rgba(185,158,79,0.1)" }
          : { borderColor: COLORS.white, color: COLORS.white, backgroundColor: "rgba(255,255,255,0.08)" },
      }}
    >
      {label}
    </Button>
  );
}

function DrillSection({ title, children, hint = "" }) {
  return (
    <Box sx={{ mb: 1 }}>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.6 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: COLORS.text, mb: 0.4 }}>{title}</Typography>
        {!!hint && <Typography sx={{ fontSize: 9.5, color: COLORS.textMuted }}>{hint}</Typography>}
      </Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.45 }}>{children}</Box>
    </Box>
  );
}

function HandFilterGrid({ selected = [], onToggle }) {
  const selectedSet = new Set(selected);
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(13, 1fr)", gap: "1.5px", mt: 0.5 }}>
      {SOLVER_RANKS.map((_, row) =>
        SOLVER_RANKS.map((__, col) => {
          const label = handGridLabel(row, col);
          const isSel = selectedSet.has(label);
          const isPair = row === col;
          return (
            <Box
              key={label}
              onClick={() => onToggle(label)}
              sx={{
                aspectRatio: "1 / 1",
                display: "grid",
                placeItems: "center",
                fontSize: "7.5px",
                fontWeight: 700,
                cursor: "pointer",
                userSelect: "none",
                borderRadius: "2px",
                color: isSel ? "#04150f" : COLORS.textMuted,
                backgroundColor: isSel ? COLORS.green : isPair ? "#2c2c2c" : "#242424",
                border: `1px solid ${isSel ? COLORS.green : "#333"}`,
                "&:hover": { borderColor: COLORS.green },
              }}
            >
              {label}
            </Box>
          );
        })
      )}
    </Box>
  );
}

export default function Trainer({
  modeVariant = "trainer",
  playConfig = null,
  onPlaySessionFinalize = null,
  onExitPlayLobby = null,
  embed = false,
  initialDrillSettings = null,
  continuePostflopThroughRiver = false,
  onSessionComplete = null,
}) {
  const navigate = useNavigate();
  const isPlayMode = String(modeVariant || "").toLowerCase() === "play";
  const isEmbed = !!embed;
  const playThroughRiver = !!continuePostflopThroughRiver || isEmbed;
  const onSessionCompleteRef = React.useRef(onSessionComplete);
  React.useEffect(() => {
    onSessionCompleteRef.current = onSessionComplete;
  }, [onSessionComplete]);
  const isSlimPhone = useMediaQuery("(max-width: 430px)");
  const isPhoneTable = useMediaQuery("(max-width: 767px)");
  const sitOutNeedsBelowHero = useMediaQuery("(max-width: 1100px)");
  const playOpponentCount = Math.max(1, Math.min(5, Number(process.env.REACT_APP_PLAY_OPPONENT_COUNT || 1) || 1));
  const isPlaySixMax = isPlayMode && playOpponentCount >= 5;
  const usePhoneTableLayout = isPhoneTable && (!isPlayMode || isPlaySixMax);
  const useCompactTableSeats = usePhoneTableLayout; // spread seats + stack cards on phone
  const isPlayHu = isPlayMode && !isPlaySixMax;
  const isDesktopPlayHu = isPlayHu && !isPhoneTable;
  const isPhonePlayHu = isPlayHu && isPhoneTable;
  // Same floors as Solver table UI (Play uses direct px - no CSS stage scale).
  const seatFsFloor = isPlayMode ? TABLE_FS_FLOOR : null;
  const chipFsFloor = isPlayMode ? CHIP_LABEL_FS_FLOOR : null;
  const chipGap = chipLabelGapPx(chipFsFloor || CHIP_LABEL_FS_FLOOR);
  const [playPhoneTableH, setPlayPhoneTableH] = React.useState(PLAY_HU_PHONE_TABLE_MAX);
  React.useEffect(() => {
    if (!isPhonePlayHu) return undefined;
    const measure = () => {
      const vh = Number(window.innerHeight) || 700;
      const avail = vh - PLAY_HU_PHONE_CHROME_RESERVE - PLAY_HU_PHONE_ACTION_RESERVE;
      // Buttons already fit under MAX → keep MAX (fill neatly). Else shrink, never below MIN.
      const next = Math.round(Math.max(PLAY_HU_PHONE_TABLE_MIN, Math.min(PLAY_HU_PHONE_TABLE_MAX, avail)));
      setPlayPhoneTableH(next);
    };
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("orientationchange", measure);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("orientationchange", measure);
    };
  }, [isPhonePlayHu]);
  const playPhoneTableScale = isPhonePlayHu ? (playPhoneTableH / PLAY_HU_PHONE_TABLE_MAX) : 1;
  const playPhoneHeroCardsBottom = isPhonePlayHu ? Math.max(58, Math.round(88 * playPhoneTableScale)) : null;
  const playPhoneHeroChipsBottom = isPhonePlayHu
    ? Math.max(playPhoneHeroCardsBottom + 48, Math.round(148 * playPhoneTableScale))
    : null;
  const playStakeFactor = isPlayMode ? Math.max(1, Number(playConfig?.stakeFactor || 1) || 1) : 1;
  const toUiChips = React.useCallback((baseAmount) => Number((Number(baseAmount || 0) * playStakeFactor).toFixed(1)), [playStakeFactor]);
  const formatUiChips = React.useCallback((baseAmount) => {
    const v = Number((Number(baseAmount || 0) * playStakeFactor).toFixed(1));
    return Number.isInteger(v) ? String(v) : v.toFixed(1);
  }, [playStakeFactor]);
  const toUiActionLabel = React.useCallback((rawAction) => {
    if (!isPlayMode) return String(rawAction || "");
    const txt = String(rawAction || "");
    return txt.replace(/\b(\d+(\.\d+)?)\b/g, (m) => {
      const n = Number(m);
      if (!Number.isFinite(n)) return m;
      const scaled = Number((n * playStakeFactor).toFixed(1));
      return Number.isInteger(scaled) ? String(scaled) : scaled.toFixed(1);
    });
  }, [isPlayMode, playStakeFactor]);
  // Drill settings (persisted). Only used in trainer mode. Embed uses overrides and does not write localStorage.
  const [drillSettings, setDrillSettings] = React.useState(() => {
    if (isEmbed || initialDrillSettings) {
      return { ...DRILL_DEFAULTS, ...(initialDrillSettings && typeof initialDrillSettings === "object" ? initialDrillSettings : {}) };
    }
    return loadDrillSettings();
  });
  const drillSettingsRef = React.useRef(drillSettings);
  const updateDrillSettings = React.useCallback((patch) => {
    setDrillSettings((prev) => {
      const next = { ...prev, ...patch };
      drillSettingsRef.current = next;
      if (!isEmbed) saveDrillSettings(next);
      return next;
    });
  }, [isEmbed]);
  const [handPickerOpen, setHandPickerOpen] = React.useState(false);
  // Table appearance theme (edited from the global Settings dialog).
  const [appearance, setAppearance] = React.useState(() => loadAppearance());
  React.useEffect(() => {
    const onAppearanceChange = () => setAppearance(loadAppearance());
    window.addEventListener(TRAINER_APPEARANCE_EVENT, onAppearanceChange);
    return () => window.removeEventListener(TRAINER_APPEARANCE_EVENT, onAppearanceChange);
  }, []);
  const [sessionHandsPlayed, setSessionHandsPlayed] = React.useState(0);
  const sessionHandsPlayedRef = React.useRef(0);
  const [sessionSummaryOpen, setSessionSummaryOpen] = React.useState(false);
  // Seat clock: visible countdown for whichever seat is acting (hero timebank / opponent think timer).
  const [seatClock, setSeatClock] = React.useState(null);
  const [, setClockNow] = React.useState(0);
  const heroClockFiredRef = React.useRef(false);
  const onHeroActionRef = React.useRef(null);
  const startTrainingRef = React.useRef(null);
  const continuePostflopRef = React.useRef(null);
  const autopilotRedealsRef = React.useRef(0);
  const autoNextTimerRef = React.useRef(null);
  // Sit out: suppress auto-dealing until toggled off or Deal new hand is pressed.
  const [sitOutNextHand, setSitOutNextHand] = React.useState(false);
  const sitOutRef = React.useRef(false);
  const pendingDealRef = React.useRef(false);
  // End-of-hand review (analysisMode "after"): hero decisions collected per hand.
  const handReviewRef = React.useRef([]);
  const [handReview, setHandReview] = React.useState(null);

  const [heroSeat, setHeroSeat] = React.useState("sb");
  const [handsBySeat, setHandsBySeat] = React.useState({});
  const [foldedSeats, setFoldedSeats] = React.useState(new Set());
  const [actionOnSeat, setActionOnSeat] = React.useState(null);
  const [thinkingSeat, setThinkingSeat] = React.useState(null);
  const [history, setHistory] = React.useState([]);
  const [feedback, setFeedback] = React.useState("");
  const [heroOptions, setHeroOptions] = React.useState([]);
  const [heroValidActions, setHeroValidActions] = React.useState([]);
  const [heroBlockedActions, setHeroBlockedActions] = React.useState([]);
  const [heroDecisionContext, setHeroDecisionContext] = React.useState(null);
  const [heroTurn, setHeroTurn] = React.useState(false);
  const [waitForHeroContinue, setWaitForHeroContinue] = React.useState(false);
  const [showFullRange, setShowFullRange] = React.useState(false);
  const [feedbackNodeSnapshot, setFeedbackNodeSnapshot] = React.useState(null);
  const [isRunning, setIsRunning] = React.useState(false);
  const [decisionDebugRows, setDecisionDebugRows] = React.useState([]);
  const [committedBySeat, setCommittedBySeat] = React.useState(() => ({ ...makeSeatNumberMap(0), sb: 0.5, bb: 1 }));
  const [stacksBySeat, setStacksBySeat] = React.useState(() => ({ ...makeSeatNumberMap(100), sb: 99.5, bb: 99 }));
  const [boardCards, setBoardCards] = React.useState([]);
  const [centerMessage, setCenterMessage] = React.useState("");
  const [streetPhase, setStreetPhase] = React.useState("preflop");
  const [postflopDebug, setPostflopDebug] = React.useState(null);
  const [mainPot, setMainPot] = React.useState(1.5);
  const [postflopDecision, setPostflopDecision] = React.useState(null);
  const [handSummary, setHandSummary] = React.useState(null);
  const [showdownRevealedSeats, setShowdownRevealedSeats] = React.useState(new Set());
  const [showdownWinnerSeat, setShowdownWinnerSeat] = React.useState(null);
  const [showdownAnimating, setShowdownAnimating] = React.useState(false);
  const [feedbackOpen, setFeedbackOpen] = React.useState(false);
  const [feedbackLike, setFeedbackLike] = React.useState("");
  const [feedbackDislike, setFeedbackDislike] = React.useState("");
  const [feedbackOther, setFeedbackOther] = React.useState("");
  const [feedbackBusy, setFeedbackBusy] = React.useState(false);
  const [feedbackInfo, setFeedbackInfo] = React.useState("");
  const [feedbackError, setFeedbackError] = React.useState("");
  const [sessionActionLog, setSessionActionLog] = React.useState([]);
  const sessionActionLogRef = React.useRef([]);
  React.useEffect(() => {
    sessionActionLogRef.current = sessionActionLog;
  }, [sessionActionLog]);
  const [handStreetResults, setHandStreetResults] = React.useState({
    preflop: { blunders: 0, correct: 0 },
    flop: { blunders: 0, correct: 0 },
    turn: { blunders: 0, correct: 0 },
    river: { blunders: 0, correct: 0 },
  });
  const [playHandCounter, setPlayHandCounter] = React.useState(0);
  const [playSessionScore, setPlaySessionScore] = React.useState(0);
  const [playSessionHands, setPlaySessionHands] = React.useState(0);
  const [playSessionHistory, setPlaySessionHistory] = React.useState([]);
  const [playOpponentNamesBySeat, setPlayOpponentNamesBySeat] = React.useState({});
  const [playAutoAdvanceNonce, setPlayAutoAdvanceNonce] = React.useState(0);
  const [playSessionStarted, setPlaySessionStarted] = React.useState(false);
  const [playVillainSpeech, setPlayVillainSpeech] = React.useState("");
  const [playShowOpponentCards, setPlayShowOpponentCards] = React.useState(false);
  const [, setIsDealing] = React.useState(false);

  const timerRef = React.useRef(null);
  const playAutoAdvanceTimerRef = React.useRef(null);
  const playLastHandledAdvanceNonceRef = React.useRef(0);
  const runtimeRef = React.useRef(null);
  const replayCheckpointRef = React.useRef(null);
  const [, setHasReplayCheckpoint] = React.useState(false);

  React.useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (playAutoAdvanceTimerRef.current) clearTimeout(playAutoAdvanceTimerRef.current);
    if (autoNextTimerRef.current) clearTimeout(autoNextTimerRef.current);
  }, []);

  React.useEffect(() => {
    if (!isPlayMode) return;
    try {
      const raw = window.localStorage.getItem(PLAY_SESSION_HISTORY_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      if (Array.isArray(parsed)) setPlaySessionHistory(parsed);
    } catch (_) {}
  }, [isPlayMode]);

  React.useEffect(() => {
    if (!isPlayMode) return;
    try {
      window.localStorage.setItem(PLAY_SESSION_HISTORY_KEY, JSON.stringify(playSessionHistory));
    } catch (_) {}
  }, [isPlayMode, playSessionHistory]);

  // Trainer hand finished: count it toward the session, then show the session
  // summary / hand review, or auto-deal the next filtered hand (unless sitting out).
  const finishTrainerHand = React.useCallback(() => {
    if (isPlayMode) return;
    const played = sessionHandsPlayedRef.current + 1;
    sessionHandsPlayedRef.current = played;
    setSessionHandsPlayed(played);
    const settings = drillSettingsRef.current || DRILL_DEFAULTS;
    const target = Number(settings.sessionLength || 0);
    if (target > 0 && played >= target) {
      const log = sessionActionLogRef.current || [];
      const correct = log.filter((r) => r?.outcome === "correct").length;
      const blunders = log.filter((r) => r?.outcome === "blunder").length;
      const total = correct + blunders;
      if (typeof onSessionCompleteRef.current === "function") {
        setIsRunning(false);
        setActionOnSeat(null);
        setThinkingSeat(null);
        setHeroTurn(false);
        onSessionCompleteRef.current({
          correct,
          blunders,
          total,
          hands: played,
          log,
        });
        return;
      }
      setSessionSummaryOpen(true);
      return;
    }
    if (String(settings.analysisMode || "during") === "after" && handReviewRef.current.length) {
      const entries = [...handReviewRef.current];
      const streets = [];
      for (const e of entries) if (!streets.includes(e.street)) streets.push(e.street);
      // Let the final action render on the table before the review pops up.
      if (autoNextTimerRef.current) clearTimeout(autoNextTimerRef.current);
      autoNextTimerRef.current = setTimeout(() => setHandReview({ entries, streets, idx: 0 }), 1100);
      return;
    }
    if (sitOutRef.current) {
      pendingDealRef.current = true;
      setFeedback("Sitting out. Press Deal new hand or toggle Sit out to continue.");
      return;
    }
    if (autoNextTimerRef.current) clearTimeout(autoNextTimerRef.current);
    autoNextTimerRef.current = setTimeout(() => startTrainingRef.current?.(), 2000);
  }, [isPlayMode]);

  const closeHandReview = React.useCallback(() => {
    setHandReview(null);
    if (sitOutRef.current) {
      pendingDealRef.current = true;
      setFeedback("Sitting out. Press Deal new hand or toggle Sit out to continue.");
      return;
    }
    if (autoNextTimerRef.current) clearTimeout(autoNextTimerRef.current);
    autoNextTimerRef.current = setTimeout(() => startTrainingRef.current?.(), 600);
  }, []);

  const toggleSitOut = React.useCallback(() => {
    setSitOutNextHand((prev) => {
      const next = !prev;
      sitOutRef.current = next;
      if (!next && pendingDealRef.current) {
        pendingDealRef.current = false;
        if (autoNextTimerRef.current) clearTimeout(autoNextTimerRef.current);
        autoNextTimerRef.current = setTimeout(() => startTrainingRef.current?.(), 600);
      }
      return next;
    });
  }, []);

  // A postflop drill hand fizzled before reaching the target street (fold-out /
  // all-in runout): silently redeal so the user only ever sees matching spots.
  const scheduleAutopilotRedeal = React.useCallback((reason = "") => {
    if (isPlayMode) return;
    autopilotRedealsRef.current += 1;
    if (autopilotRedealsRef.current > 15) {
      setFeedback("Couldn't reach that street with the current filters; loosen them, then press Deal new hand.");
      setIsRunning(false);
      return;
    }
    setFeedback("Dealing...");
    if (autoNextTimerRef.current) clearTimeout(autoNextTimerRef.current);
    autoNextTimerRef.current = setTimeout(() => startTrainingRef.current?.(), 200);
  }, [isPlayMode]);

  const villainSeat = heroSeat === "sb" ? "bb" : "sb";
  const hasPlayPremiumAccess = React.useCallback(() => {
    // Onboarding embed must play flop→river without billing chrome.
    if (isEmbed || playThroughRiver) return true;
    if (typeof window === "undefined") return false;
    if (isPaywallRequired()) return false;
    const billing = window.__pmBillingState || null;
    if (!billing?.authed) return false;
    if (billing?.hasAccess && typeof billing.hasAccess === "object") {
      return !!billing.hasAccess.trainer || !!billing.hasAccess.play;
    }
    return !!billing?.hasPaidAccess;
  }, [isEmbed, playThroughRiver]);
  const hasTrainerTurnRiverAccess = React.useCallback(() => {
    if (isEmbed || playThroughRiver) return true;
    if (typeof window === "undefined") return false;
    if (isPaywallRequired()) return false;
    const billing = window.__pmBillingState || null;
    if (!billing?.authed) return false;
    if (billing?.hasAccess && typeof billing.hasAccess === "object") {
      if (Object.prototype.hasOwnProperty.call(billing.hasAccess, "trainerTurnRiver")) {
        return !!billing.hasAccess.trainerTurnRiver;
      }
      // Legacy: any paid trainer/play access unlocked turn/river.
      return !!billing.hasAccess.trainer || !!billing.hasAccess.play;
    }
    return !!billing?.hasPaidAccess;
  }, [isEmbed, playThroughRiver]);
  const requirePlayPremiumAccess = React.useCallback((stage = "play_view_cards") => {
    if (hasPlayPremiumAccess()) return true;
    try {
      if (typeof window.__pmOpenSubscriptionGate === "function") {
        window.__pmOpenSubscriptionGate();
      } else {
        window.dispatchEvent(new CustomEvent("pm:require-subscription", { detail: { stage } }));
      }
    } catch (_) {}
    return false;
  }, [hasPlayPremiumAccess]);
  const ensureTurnRiverAccess = React.useCallback((stage = "turn_or_river") => {
    if (hasTrainerTurnRiverAccess()) return true;
    try {
      if (typeof window.__pmOpenSubscriptionGate === "function") {
        window.__pmOpenSubscriptionGate();
      } else {
        window.dispatchEvent(new CustomEvent("pm:require-subscription", { detail: { stage } }));
      }
    } catch (_) {}
    return false;
  }, [hasTrainerTurnRiverAccess]);
  // Starter: preflop + flop only. Improver/Pro/trial: full streets.
  const turnRiverStreetLocked = !isPlayMode && !hasTrainerTurnRiverAccess();
  React.useEffect(() => {
    if (!turnRiverStreetLocked) return;
    const street = String(drillSettings.street || "full");
    if (street === "turn" || street === "river") {
      updateDrillSettings({ street: "flop" });
    }
  }, [turnRiverStreetLocked, drillSettings.street, updateDrillSettings]);
  const onTogglePlayViewCards = React.useCallback(() => {
    if (!isPlayMode) return;
    if (playShowOpponentCards) {
      setPlayShowOpponentCards(false);
      return;
    }
    if (requirePlayPremiumAccess("play_view_cards")) {
      setPlayShowOpponentCards(true);
    }
  }, [isPlayMode, playShowOpponentCards, requirePlayPremiumAccess]);
  const playCardsLocked = isPlayMode && !hasPlayPremiumAccess();

  const evaluateShowdownResult = React.useCallback((runtime) => {
    const heroCards = runtime?.dealt?.[runtime?.heroSeat] || [];
    const villainCards = runtime?.dealt?.[villainSeat] || [];
    const board = runtime?.boardCards || [];
    if (heroCards.length < 2 || villainCards.length < 2 || board.length < 5) return "split";
    const heroRank = evaluateSevenCardHand([...heroCards, ...board]);
    const villainRank = evaluateSevenCardHand([...villainCards, ...board]);
    const cmp = compareHandRanks(heroRank, villainRank);
    if (cmp > 0) return "hero";
    if (cmp < 0) return "villain";
    return "split";
  }, [villainSeat]);

  const settlePlayHandScore = React.useCallback((runtime) => {
    if (!isPlayMode || !runtime?.heroSeat) return null;
    if (runtime.__playSettled) return null;
    runtime.__playSettled = true;
    const hero = runtime.heroSeat;
    const active = PREFLOP_ORDER.filter((s) => !runtime.folded?.has(s));
    let winner = "split";
    if (active.length === 1) winner = active[0] === hero ? "hero" : "villain";
    else if (runtime.boardCards?.length >= 5) winner = evaluateShowdownResult(runtime);
    const pot = Number(runtime.mainPot || 0);
    let heroFinal = Number(runtime.stacks?.[hero] || 0);
    if (winner === "hero") heroFinal += pot;
    else if (winner === "split") heroFinal += pot / 2;
    const handDeltaBase = Number((heroFinal - 100).toFixed(2));
    const handDelta = Number((handDeltaBase * playStakeFactor).toFixed(2));
    setPlaySessionScore((prev) => Number((prev + handDelta).toFixed(2)));
    setPlaySessionHands((prev) => prev + 1);
    setPlayAutoAdvanceNonce((prev) => prev + 1);
    return { handDelta, winner };
  }, [evaluateShowdownResult, isPlayMode, playStakeFactor]);

  const startNewPlaySession = React.useCallback(() => {
    if (!isPlayMode) return;
    if (String(playConfig?.mode || "casual") === "challenge" && playSessionHands < 100) {
      setFeedback(`Challenge requires at least 100 hands (${playSessionHands}/100).`);
      return;
    }
    if (playSessionHands > 0 || Math.abs(playSessionScore) > 0.0001) {
      onPlaySessionFinalize?.({
        opponentId: playConfig?.opponentId || "unknown",
        mode: String(playConfig?.mode || "casual"),
        score: Number(playSessionScore || 0),
        hands: Number(playSessionHands || 0),
        restartSession: true,
      });
      const entry = {
        id: `${Date.now()}`,
        endedAt: new Date().toISOString(),
        score: Number(playSessionScore.toFixed(2)),
        hands: playSessionHands,
      };
      setPlaySessionHistory((prev) => [entry, ...prev].slice(0, 50));
    }
    setPlaySessionScore(0);
    setPlaySessionHands(0);
    setPlayAutoAdvanceNonce(0);
    playLastHandledAdvanceNonceRef.current = 0;
    setPlaySessionStarted(false);
    setPlayShowOpponentCards(false);
    setShowdownWinnerSeat(null);
    setShowdownAnimating(false);
    setHandsBySeat({});
    setHistory([]);
    setFoldedSeats(new Set());
    setActionOnSeat(null);
    setThinkingSeat(null);
    setHeroTurn(false);
    setIsRunning(false);
    setBoardCards([]);
    setMainPot(1.5);
    setCenterMessage("");
    setFeedback("");
    setPlayVillainSpeech("");
    setIsDealing(false);
    runtimeRef.current = null;
  }, [isPlayMode, onPlaySessionFinalize, playConfig?.mode, playConfig?.opponentId, playSessionHands, playSessionScore]);

  const pot = Number(mainPot || 0);
  const latestActionBySeat = React.useMemo(() => {
    const map = {};
    for (const h of history || []) {
      const seat = String(h?.position || "").toLowerCase();
      if (!seat) continue;
      const act = String(h?.action || "").toLowerCase();
      if (act === "call") map[seat] = `Call ${formatUiChips(h?.contribution || 0)}`;
      else if (act === "check") map[seat] = "Check";
      else if (act === "fold") map[seat] = "Fold";
      else if (/raise|bet/i.test(act)) map[seat] = toUiActionLabel(String(h?.action || "").replace(/\s+/g, " ").trim());
      else if (act === "open") map[seat] = `Open ${formatUiChips(h?.raiseSize || 0)}`;
      else map[seat] = String(h?.action || "");
    }
    return map;
  }, [formatUiChips, history, toUiActionLabel]);
  const abilityRating = React.useMemo(() => {
    // Only graded decisions count; off-tree ("na") decisions are excluded.
    const graded = sessionActionLog.filter((r) => r?.outcome === "correct" || r?.outcome === "blunder");
    if (!graded.length) return "0.0";
    const correct = graded.filter((r) => r?.outcome === "correct").length;
    return ((correct / graded.length) * 10).toFixed(1);
  }, [sessionActionLog]);
  const heroStack = Number(stacksBySeat?.[heroSeat] ?? 0);
  const isPostflopDrillSelected = ["flop", "turn", "river"].includes(String(drillSettings.street || "full"));
  const sessionCorrectCount = React.useMemo(() => sessionActionLog.filter((r) => r?.outcome === "correct").length, [sessionActionLog]);
  const sessionGradedCount = React.useMemo(
    () => sessionActionLog.filter((r) => r?.outcome === "correct" || r?.outcome === "blunder").length,
    [sessionActionLog]
  );
  const playVillainProfileKey = React.useMemo(() => {
    const raw = String(playConfig?.opponentId || playConfig?.opponentName || "").trim().toLowerCase();
    if (!raw) return null;
    if (raw.includes("anna")) return "anna";
    if (raw.includes("peter")) return "peter";
    if (raw.includes("mimi")) return "mimi";
    if (raw.includes("ryan")) return "ryan";
    return null;
  }, [playConfig?.opponentId, playConfig?.opponentName]);
  const maybeVillainBetSpeech = React.useCallback((seat, chosenAction, contributionBase) => {
    if (!isPlayMode || seat === heroSeat) return "";
    const actionText = String(chosenAction || "").toLowerCase();
    if (!/(bet|raise)/i.test(actionText)) return "";
    if (Math.floor(Math.random() * 15) !== 0) return "";
    const seatName = playOpponentNamesBySeat?.[seat] || playConfig?.opponentName || "";
    const opponentKey = getPlayOpponentKeyByName(seatName);
    const choices = PLAY_OPPONENT_BET_TAUNTS[opponentKey] || [];
    if (!choices.length) return "";
    const picked = choices[Math.floor(Math.random() * choices.length)] || "";
    const uiAmt = Math.round(Number(toUiChips(contributionBase || 0) || 0));
    return String(picked).replace(/\[chip amount\]/gi, String(uiAmt));
  }, [heroSeat, isPlayMode, playConfig?.opponentName, playOpponentNamesBySeat, toUiChips]);
  const visualSeats = React.useMemo(() => {
    if (isPlayMode && !isPlaySixMax) {
      return buildPlayVisualSeats(heroSeat, villainSeat, foldedSeats, actionOnSeat, thinkingSeat, stacksBySeat);
    }
    return buildVisualSeats(heroSeat, foldedSeats, actionOnSeat, thinkingSeat, stacksBySeat);
  }, [isPlayMode, isPlaySixMax, heroSeat, villainSeat, foldedSeats, actionOnSeat, thinkingSeat, stacksBySeat]);
  const showdownChipTransform = React.useMemo(() => {
    if (!showdownAnimating || !showdownWinnerSeat) return "translate(0, 0) scale(1)";
    const winnerSeat = (visualSeats || []).find((s) => s.logicalSeat === showdownWinnerSeat);
    if (!winnerSeat) return "translate(0, 0) scale(1)";
    const geo = resolveSeatGeometry(winnerSeat, {
      slimTable: useCompactTableSeats && isSlimPhone,
      phoneTable: useCompactTableSeats && !isSlimPhone,
    });
    const left = Number.parseFloat(String(geo?.left || "50"));
    const top = Number.parseFloat(String(geo?.top || "50"));
    if (!Number.isFinite(left) || !Number.isFinite(top)) return "translate(0, 0) scale(1)";
    const dx = (left - 50) * 0.75;
    const dy = (top - 50) * 0.75;
    return `translate(${dx}%, ${dy}%) scale(1.08)`;
  }, [showdownAnimating, showdownWinnerSeat, visualSeats, useCompactTableSeats, isSlimPhone]);

  const sendFeedback = React.useCallback(async () => {
    setFeedbackError("");
    setFeedbackInfo("");
    const like = String(feedbackLike || "").trim();
    const dislike = String(feedbackDislike || "").trim();
    const otherThoughts = String(feedbackOther || "").trim();
    if (!like && !dislike && !otherThoughts) {
      setFeedbackError("Please enter feedback before sending.");
      return;
    }
    setFeedbackBusy(true);
    try {
      const res = await fetch("/api/auth/feedback", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ like, dislike, otherThoughts }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok) {
        setFeedbackError(data?.error || "Could not send feedback.");
      } else {
        setFeedbackInfo("Feedback sent. Thank you.");
        setFeedbackLike("");
        setFeedbackDislike("");
        setFeedbackOther("");
      }
    } catch {
      setFeedbackError("Could not contact server.");
    } finally {
      setFeedbackBusy(false);
    }
  }, [feedbackDislike, feedbackLike, feedbackOther]);

  const getDecisionForSeat = React.useCallback((actingSeat, handCards, handHistory) => {
    const raiseCount = countRaises(handHistory);
    const pre = getPreflopDecisionGrid(handHistory, { position: actingSeat, raiseCount });
    if (!pre?.actions?.length || !pre?.grid) return null;
    const label = handLabelFromTwoCards(handCards);
    const cell = findCellByLabel(pre.grid, label) || pre.grid?.[0]?.[0] || null;
    if (!cell) return null;
    const freqInfo = getFrequenciesForHand(cell, handCards);
    const baseFreqs = freqInfo.frequencies || [];
    const isVillainInPlay = isPlayMode && String(actingSeat || "") !== String(runtimeRef.current?.heroSeat || heroSeat || "");
    const profileMix = isVillainInPlay ? applyProfileToMixedStrategy(pre.actions, baseFreqs, playVillainProfileKey) : { frequencies: baseFreqs, forcedIndex: null, applied: false };
    const freqs = profileMix.frequencies || baseFreqs;
    const weightedPick = profileMix.forcedIndex != null
      ? { action: pre.actions?.[profileMix.forcedIndex] || "Fold", rngRoll: null, rngTotal: null }
      : pickByWeights(pre.actions, freqs);
    const pickedRaw = weightedPick.action;
    const totalFreq = (freqs || []).reduce((s, f) => s + Math.max(0, Number(f || 0)), 0);
    const leftoverFold = Math.max(0, 1 - totalFreq);
    const explicitFoldIdx = (pre.actions || []).findIndex((a) => /^fold$/i.test(String(a || "").trim()));
    const explicitFoldFreq = explicitFoldIdx >= 0 ? Math.max(0, Number(freqs?.[explicitFoldIdx] ?? 0)) : 0;
    const foldTotalFreq = explicitFoldFreq + leftoverFold;
    const positiveActions = pre.actions.filter((_, i) => Number(freqs?.[i] ?? 0) > 0);
    const resolvedPositiveActions = [...positiveActions];
    if (foldTotalFreq > 0 && !resolvedPositiveActions.some((a) => /^fold$/i.test(String(a || "").trim()))) {
      resolvedPositiveActions.push("Fold");
    }
    if (!resolvedPositiveActions.length) resolvedPositiveActions.push("Fold");
    const actionPctMap = {};
    for (let i = 0; i < pre.actions.length; i++) {
      actionPctMap[String(pre.actions[i] || "").trim().toLowerCase()] = Number(freqs?.[i] ?? 0) * 100;
    }
    if (foldTotalFreq > 0) actionPctMap.fold = foldTotalFreq * 100;
    return {
      availableActions: pre.actions,
      positiveActions: resolvedPositiveActions,
      actionPctMap,
      grid: pre.grid,
      actions: pre.actions,
      parsed: classifyAction(pickedRaw, raiseCount),
      debug: {
        seat: actingSeat,
        handCards: handCards || [],
        handLabel: label || "--",
        matchedCellLabel: String(cell?.label || "--"),
        exactComboMatch: !!freqInfo.exactMatch,
        matchedComboHand: freqInfo.matchedHand,
        usedFallbackCellFrequencies: !!freqInfo.usedFallbackCellFrequencies,
        actions: pre.actions || [],
        frequencies: (freqs || []).map((f) => Number(f || 0)),
        chosenAction: pickedRaw,
        validActions: resolvedPositiveActions,
        actionPctMap,
        rangeGrid: pre.grid || null,
        rangeActions: pre.actions || [],
        rngRoll: weightedPick.rngRoll,
        rngTotal: weightedPick.rngTotal,
        profileOverride: isVillainInPlay ? (playVillainProfileKey || null) : null,
        profileForced: profileMix.forcedIndex != null,
      },
    };
  }, [heroSeat, isPlayMode, playVillainProfileKey]);

  const buildFallbackFoldDecision = React.useCallback((actingSeat, handCards, reason) => {
    const label = handLabelFromTwoCards(handCards);
    return {
      availableActions: ["Fold"],
      positiveActions: ["Fold"],
      actionPctMap: { fold: 100 },
      parsed: { action: "fold", raiseSize: null, aggressive: false },
      debug: {
        seat: actingSeat,
        handCards: handCards || [],
        handLabel: label || "--",
        matchedCellLabel: "n/a",
        exactComboMatch: false,
        matchedComboHand: null,
        usedFallbackCellFrequencies: true,
        actions: ["Fold"],
        frequencies: [1],
        chosenAction: "Fold",
        validActions: ["Fold"],
        actionPctMap: { fold: 100 },
        rngRoll: null,
        rngTotal: 0,
        note: reason || "fallback fold",
      },
    };
  }, []);

  const applyContribution = React.useCallback((actor, parsed, localInvested, localCommitted, localStacks, localCurrentBet) => {
    const alreadyIn = Number(localInvested[actor] || 0);
    const stackBefore = Number(localStacks[actor] || 0);
    const maxTotalInvested = Number((alreadyIn + Math.max(0, stackBefore)).toFixed(2));
    let contribution = 0;
    let nextCurrentBet = localCurrentBet;
    if (parsed.action === "call") contribution = Math.min(Math.max(0, localCurrentBet - alreadyIn), Math.max(0, stackBefore));
    else if (parsed.aggressive) {
      const requestedTarget = Math.max(alreadyIn, Number(parsed.raiseSize || 0));
      const target = Math.min(requestedTarget, maxTotalInvested);
      contribution = Math.max(0, target - alreadyIn);
      nextCurrentBet = Math.max(localCurrentBet, target);
    }
    if (contribution > 0) {
      localInvested[actor] = Number((alreadyIn + contribution).toFixed(2));
      localCommitted[actor] = Number((Number(localCommitted[actor] || 0) + contribution).toFixed(2));
      localStacks[actor] = Number(Math.max(0, Number(localStacks[actor] || 0) - contribution).toFixed(2));
    }
    return { contribution: Number(contribution.toFixed(2)), nextCurrentBet };
  }, []);

  const applyPostflopContribution = React.useCallback((actor, actionLabel, runtime, options = {}) => {
    const txt = String(actionLabel || "").trim().toLowerCase();
    const alreadyIn = Number(runtime.invested?.[actor] || 0);
    const n = extractNumber(actionLabel);
    const fromDb = !!options?.fromDb;
    let contribution = 0;
    let nextCurrentBet = Number(runtime.currentBet || 0);

    if (txt.includes("call")) {
      contribution = Math.max(0, nextCurrentBet - alreadyIn);
    } else if (txt.includes("raise 3x")) {
      const target = Math.max(nextCurrentBet * 3, alreadyIn);
      contribution = Math.max(0, target - alreadyIn);
      nextCurrentBet = Math.max(nextCurrentBet, target);
    } else if (txt.includes("raise 5x")) {
      const target = Math.max(nextCurrentBet * 5, alreadyIn);
      contribution = Math.max(0, target - alreadyIn);
      nextCurrentBet = Math.max(nextCurrentBet, target);
    } else if (txt.includes("bet") || txt.includes("raise")) {
      const target = fromDb
        ? Math.max(alreadyIn, Math.max(0, Number(n || 0)) / 2)
        : (String(actionLabel || "").includes("%")
            ? (n / 100) * Math.max(0.1, Number(runtime.mainPot || 0))
            : Math.max(alreadyIn, Math.max(0, Number(n || 0))));
      contribution = Math.max(0, target - alreadyIn);
      nextCurrentBet = Math.max(nextCurrentBet, target);
    } else if (txt.includes("all")) {
      const target = Math.max(0, Number(runtime.stacks?.[actor] || 0) + alreadyIn);
      contribution = Math.max(0, target - alreadyIn);
      nextCurrentBet = Math.max(nextCurrentBet, target);
    }

    if (contribution > 0) {
      runtime.invested[actor] = Number((alreadyIn + contribution).toFixed(2));
      runtime.committed[actor] = Number((Number(runtime.committed?.[actor] || 0) + contribution).toFixed(2));
      runtime.stacks[actor] = Number(Math.max(0, Number(runtime.stacks?.[actor] || 0) - contribution).toFixed(2));
      runtime.mainPot = Number((Number(runtime.mainPot || 0) + contribution).toFixed(2));
    }
    runtime.currentBet = Number(nextCurrentBet.toFixed(2));
    return {
      contribution: Number(contribution.toFixed(2)),
      nextCurrentBet: Number(nextCurrentBet.toFixed(2)),
    };
  }, []);

  const syncFromRuntime = React.useCallback((runtime) => {
    setHistory([...runtime.history]);
    setCommittedBySeat({ ...runtime.committed });
    setStacksBySeat({ ...runtime.stacks });
    setMainPot(Number(runtime.mainPot || 0));
    setFoldedSeats(new Set(runtime.folded));
  }, []);

  const cloneRuntimeState = React.useCallback((runtime) => {
    if (!runtime) return null;
    return {
      heroSeat: runtime.heroSeat,
      dealt: JSON.parse(JSON.stringify(runtime.dealt || {})),
      folded: new Set(Array.from(runtime.folded || [])),
      history: (runtime.history || []).map((h) => ({ ...h })),
      invested: { ...(runtime.invested || {}) },
      committed: { ...(runtime.committed || {}) },
      stacks: { ...(runtime.stacks || {}) },
      mainPot: Number(runtime.mainPot || 0),
      currentBet: Number(runtime.currentBet || 0),
      queue: [...(runtime.queue || [])],
      preflopHistory: (runtime.preflopHistory || []).map((h) => ({ ...h })),
      boardCards: [...(runtime.boardCards || [])],
      queriedBoardCards: [...(runtime.queriedBoardCards || [])],
      flopStr: runtime.flopStr || null,
      desiredFlopStr: runtime.desiredFlopStr || null,
      currentStreet: runtime.currentStreet || "preflop",
      pendingChanceNodeId: runtime.pendingChanceNodeId || null,
      suitMappingBetweenFlops: runtime.suitMappingBetweenFlops || null,
      flopRankMap: runtime.flopRankMap || null,
    };
  }, []);

  const cloneJsonValue = React.useCallback((value, fallback) => {
    if (value == null) return fallback;
    try {
      return JSON.parse(JSON.stringify(value));
    } catch {
      return fallback;
    }
  }, []);

  const captureReplayCheckpoint = React.useCallback((snapshot) => {
    replayCheckpointRef.current = {
      ...snapshot,
      runtime: cloneRuntimeState(snapshot?.runtime),
      heroOptions: [...(snapshot?.heroOptions || [])],
      heroValidActions: [...(snapshot?.heroValidActions || [])],
      heroBlockedActions: [...(snapshot?.heroBlockedActions || [])],
      heroDecisionContext: cloneJsonValue(snapshot?.heroDecisionContext, null),
      decisionDebugRows: cloneJsonValue(snapshot?.decisionDebugRows, []),
      postflopDebug: cloneJsonValue(snapshot?.postflopDebug, null),
      postflopDecision: cloneJsonValue(snapshot?.postflopDecision, null),
      boardCards: [...(snapshot?.boardCards || [])],
      centerMessage: String(snapshot?.centerMessage || ""),
      feedback: String(snapshot?.feedback || ""),
    };
    setHasReplayCheckpoint(true);
  }, [cloneJsonValue, cloneRuntimeState]);

  const resolvePostflopSeatDecision = React.useCallback((seat, runtime, dbActions = [], dbGrid = []) => {
    const handCards = runtime?.dealt?.[seat] || [];
    const handLabel = handLabelFromTwoCards(handCards);
    const comboKey = canonicalComboKey(handCards[0], handCards[1]);
    const cell = findCellByLabel(dbGrid || [], handLabel) || dbGrid?.[0]?.[0] || null;
    const freqInfo = cell
      ? getFrequenciesForHand(cell, handCards)
      : { frequencies: [], exactMatch: false, matchedHand: null, usedFallbackCellFrequencies: true };
    const actions = Array.isArray(dbActions) ? dbActions : [];
    const freqs = Array.isArray(freqInfo?.frequencies) ? freqInfo.frequencies : [];
    const valid = actions.filter((_, i) => Number(freqs?.[i] ?? 0) > 0);
    const actionPctMap = {};
    for (let i = 0; i < actions.length; i++) {
      actionPctMap[String(actions[i] || "").trim().toLowerCase()] = Number(freqs?.[i] ?? 0) * 100;
    }
    return {
      seat,
      handCards,
      handLabel,
      comboKey,
      cell,
      freqInfo,
      actions,
      frequencies: freqs,
      validActions: valid,
      actionPctMap,
    };
  }, []);

  const bindPlayHeroTurnWithoutLookup = React.useCallback((runtime, streetKey, params = {}) => {
    const heroSeatToAct = runtime?.heroSeat;
    if (!heroSeatToAct) return;
    const alreadyIn = Number(runtime?.invested?.[heroSeatToAct] || 0);
    const facingBet = Number(runtime?.currentBet || 0) > alreadyIn + 1e-9;
    const { options: heroOptionsHardcoded, templateMap: playActionTemplateMap } =
      getPlayPostflopHeroOptions(streetKey, facingBet, runtime, heroSeatToAct);
    const nextPostflopDecision = {
      street: streetKey,
      spot: params?.spot || null,
      flop: runtime?.flopStr || null,
      board: runtime?.boardCards || [],
      firstActor: params?.firstActor || null,
      firstActorAction: params?.firstActorAction || null,
      nodeId: params?.nodeId || null,
      dbActions: [],
      dbGrid: [],
      v6Kind: null,
      actionChildren: [],
      nextNodeIdAfterHero: null,
      heroUiToDbActionMap: {},
      playActionTemplateMap,
      isPlayHardcoded: true,
    };
    setActionOnSeat(heroSeatToAct);
    setThinkingSeat(null);
    setHeroTurn(true);
    setIsRunning(false);
    setHeroBlockedActions([]);
    setHeroOptions(heroOptionsHardcoded);
    setHeroValidActions(heroOptionsHardcoded);
    setHeroDecisionContext({
      seat: heroSeatToAct,
      actions: heroOptionsHardcoded,
      frequencies: [],
      validActions: heroOptionsHardcoded,
      actionPctMap: {},
      playActionTemplateMap,
      note: "play hardcoded postflop options",
      rngRoll: null,
      rngTotal: null,
    });
    setPostflopDecision(nextPostflopDecision);
    setFeedback("Your turn.");
  }, []);

  const resolvePlayVillainPostflopDecision = React.useCallback(async (seat, runtime, dbActions = [], dbGrid = [], opts = {}) => {
    const base = resolvePostflopSeatDecision(seat, runtime, dbActions, dbGrid);
    if (!isPlayMode || seat === runtime?.heroSeat) return base;
    const handCards = runtime?.dealt?.[seat] || [];
    if (handCards.length < 2) throw new Error("Villain hand is missing cards.");
    const desiredBoardCards = [...(runtime?.boardCards || [])];
    const queriedBoardCards = [...(runtime?.queriedBoardCards || [])];
    const suitMapping = runtime?.suitMappingBetweenFlops;
    const flopRankMap = runtime?.flopRankMap;
    if (!suitMapping || !flopRankMap || desiredBoardCards.length < 3 || queriedBoardCards.length < 3) {
      throw new Error("Villain mapping context is missing (suit/rank/board mapping).");
    }

    const desiredCombo4 = canonicalizeComboHand(`${handCards[0]}${handCards[1]}`);
    const preferredApiHand = comboDesiredToApiLookup(desiredCombo4, suitMapping, flopRankMap);
    const forceStepTwo = shouldSkipStepOneDirect(desiredCombo4, flopRankMap, desiredBoardCards, queriedBoardCards, preferredApiHand);
    const primaryComboData = findComboDataInGrid(dbGrid, preferredApiHand);

    let chosenComboData = null;
    let resolutionPath = "none";
    let textureResolved = null;
    if (!forceStepTwo && isPrimaryApiComboUsable(preferredApiHand, primaryComboData, desiredCombo4)) {
      chosenComboData = primaryComboData;
      resolutionPath = "step1-direct";
    } else {
      textureResolved = await fetchTextureResolutionForSingleCombo({
        spot: String(opts?.spot || ""),
        flop: String(runtime?.flopStr || ""),
        nodeId: String(opts?.nodeId || "root"),
        desiredBoardCards,
        queriedBoardCards,
        desiredCombo4,
        preferredApiHand,
      });
      if (textureResolved?.fallbackData?.frequencies?.some((f) => Number(f || 0) > 0)) {
        chosenComboData = textureResolved.fallbackData;
        resolutionPath = "step2-texture";
      }
    }

    if (!chosenComboData?.frequencies?.some((f) => Number(f || 0) > 0)) {
      const err = new Error("Villain combo is blocked/invalid after mapping resolution.");
      err.__solverDebug = {
        desiredBoardCards,
        queriedBoardCards,
        desiredCombo4,
        mappedApiCombo: preferredApiHand,
        forceStepTwo,
        hadPrimary: !!primaryComboData,
        textureResolved,
      };
      throw err;
    }

    const actions = Array.isArray(dbActions) ? dbActions : [];
    const freqs = actions.map((_, i) => Number(chosenComboData?.frequencies?.[i] || 0));
    const valid = actions.filter((_, i) => Number(freqs?.[i] ?? 0) > 0);
    const actionPctMap = {};
    for (let i = 0; i < actions.length; i++) {
      actionPctMap[String(actions[i] || "").trim().toLowerCase()] = Number(freqs?.[i] ?? 0) * 100;
    }
    return {
      ...base,
      frequencies: freqs,
      validActions: valid,
      actionPctMap,
      freqInfo: {
        frequencies: freqs,
        exactMatch: resolutionPath === "step1-direct",
        matchedHand: resolutionPath === "step1-direct" ? preferredApiHand : (textureResolved?.donorHand || null),
        usedFallbackCellFrequencies: resolutionPath !== "step1-direct",
      },
      mappingDebug: {
        desiredBoardCards,
        queriedBoardCards,
        desiredCombo4,
        mappedApiCombo: preferredApiHand,
        forceStepTwo,
        resolutionPath,
        donorHand: textureResolved?.donorHand || null,
        streetProfile: textureResolved?.streetProfile || null,
      },
    };
  }, [isPlayMode, resolvePostflopSeatDecision]);

  const revealRiverShowdownCards = React.useCallback((runtime) => {
    if (!runtime || runtime.folded?.has(runtime.heroSeat)) {
      setShowdownRevealedSeats(new Set());
      return;
    }
    const revealSeats = new Set(
      PREFLOP_ORDER.filter((s) => s !== runtime.heroSeat && !runtime.folded.has(s))
    );
    setShowdownRevealedSeats(revealSeats);
  }, []);

  const runPlayShowdown = React.useCallback((runtime, doneLabel = "Hand complete.") => {
    if (!runtime) return;
    const active = PREFLOP_ORDER.filter((s) => !runtime.folded.has(s));
    const hasShowdown = active.length >= 2 && (runtime.boardCards || []).length >= 5;
    if (!hasShowdown || runtime.__showdownAnimating) {
      const settled = settlePlayHandScore(runtime);
      setFeedback(isPlayMode && settled ? `Hand complete. ${settled.handDelta >= 0 ? "+" : ""}${settled.handDelta.toFixed(1)} chips.` : doneLabel);
      if (!isPlayMode && !runtime.__showdownAnimating) finishTrainerHand();
      return;
    }
    runtime.__showdownAnimating = true;
    revealRiverShowdownCards(runtime);
    setHandSummary({
      preflop: { ...handStreetResults.preflop },
      flop: { ...handStreetResults.flop },
      turn: { ...handStreetResults.turn },
      river: { ...handStreetResults.river },
    });
    const winnerTag = evaluateShowdownResult(runtime);
    const winnerSeat = winnerTag === "hero"
      ? runtime.heroSeat
      : winnerTag === "villain"
        ? active.find((s) => s !== runtime.heroSeat) || null
        : null;
    const winnerLabel = winnerSeat === runtime.heroSeat
      ? "Hero"
      : (playOpponentNamesBySeat?.[winnerSeat] || playConfig?.opponentName || "Villain");
    setShowdownWinnerSeat(winnerSeat);
    setShowdownAnimating(true);
    setActionOnSeat(winnerSeat || null);
    setThinkingSeat(null);
    setHeroTurn(false);
    setIsRunning(true);
    setCenterMessage(winnerSeat ? `${winnerLabel} wins` : "Split pot");
    timerRef.current = setTimeout(() => {
      const settled = settlePlayHandScore(runtime);
      setShowdownAnimating(false);
      setShowdownWinnerSeat(null);
      setActionOnSeat(null);
      setIsRunning(false);
      setCenterMessage("");
      setFeedback(isPlayMode && settled ? `Hand complete. ${settled.handDelta >= 0 ? "+" : ""}${settled.handDelta.toFixed(1)} chips.` : doneLabel);
      runtime.__showdownAnimating = false;
      if (!isPlayMode) finishTrainerHand();
    }, 2000);
  }, [evaluateShowdownResult, finishTrainerHand, handStreetResults.flop, handStreetResults.preflop, handStreetResults.river, handStreetResults.turn, isPlayMode, playConfig?.opponentName, playOpponentNamesBySeat, revealRiverShowdownCards, settlePlayHandScore]);

  const recordHeroOutcome = React.useCallback((streetKeyInput, outcome, chosenAction) => {
    const streetKey = normalizeStreetKey(streetKeyInput);
    setSessionActionLog((prev) => [
      ...prev,
      {
        id: `${Date.now()}-${prev.length}`,
        street: streetKey,
        outcome,
        action: String(chosenAction || ""),
      },
    ]);
    // "na" = decision made outside the solved GTO tree: logged but never graded.
    if (outcome === "na") return;
    setHandStreetResults((prev) => ({
      ...prev,
      [streetKey]: {
        blunders: Number(prev?.[streetKey]?.blunders || 0) + (outcome === "blunder" ? 1 : 0),
        correct: Number(prev?.[streetKey]?.correct || 0) + (outcome === "correct" ? 1 : 0),
      },
    }));
    // Persist lifetime trainer stats for the Home dashboard (trainer mode only).
    if (!isPlayMode) {
      try {
        const raw = window.localStorage.getItem("pm_trainer_stats_v1");
        const stats = raw ? JSON.parse(raw) : {};
        const byStreet = stats.byStreet && typeof stats.byStreet === "object" ? stats.byStreet : {};
        const prevStreet = byStreet[streetKey] || { correct: 0, blunders: 0 };
        const next = {
          totalDecisions: Number(stats.totalDecisions || 0) + 1,
          correct: Number(stats.correct || 0) + (outcome === "correct" ? 1 : 0),
          blunders: Number(stats.blunders || 0) + (outcome === "blunder" ? 1 : 0),
          byStreet: {
            ...byStreet,
            [streetKey]: {
              correct: Number(prevStreet.correct || 0) + (outcome === "correct" ? 1 : 0),
              blunders: Number(prevStreet.blunders || 0) + (outcome === "blunder" ? 1 : 0),
            },
          },
          lastSessionAt: new Date().toISOString(),
        };
        window.localStorage.setItem("pm_trainer_stats_v1", JSON.stringify(next));
      } catch (_) {}
    }
  }, [isPlayMode]);

  const beginPostflopStreet = React.useCallback(async (runtime, targetStreet) => {
    // Postflop drill autopilot: once the target street is reached, hand control back to the hero.
    if (!isPlayMode && runtime?.autopilotUntil && (STREET_RANK[targetStreet] ?? 0) >= (STREET_RANK[runtime.autopilotUntil] ?? 0)) {
      runtime.autopilotUntil = null;
      autopilotRedealsRef.current = 0;
    }
    setStreetPhase(targetStreet);
    setHeroTurn(false);
    setHeroOptions([]);
    setHeroValidActions([]);
    setHeroBlockedActions([]);
    setHeroDecisionContext(null);
    setPostflopDecision(null);
    setWaitForHeroContinue(false);
    setActionOnSeat(null);
    setThinkingSeat(null);
    setIsRunning(false);

    if (targetStreet === "flop") {
      let selectedFlopCards = [];
      if (isPlayMode) {
        const randomFlop = chooseRandomFlopFromDeck(runtime?.dealt || {});
        selectedFlopCards = flopStrToCards(randomFlop);
        if (selectedFlopCards.length !== 3) {
          setCenterMessage("something went wrong - please refresh");
          setFeedback("Unable to deal flop.");
          setPostflopDebug({ error: "Could not generate a random flop from deck." });
          return;
        }
        const { canonicalStr } = canonicalizeUserFlopTo1755(selectedFlopCards);
        const queriedFlop = await lookupQueriedFlopFromApi(canonicalStr);
        let queriedFlopCards = flopStrToCards(queriedFlop);
        let flopMapMiss = false;
        if (queriedFlopCards.length !== 3) {
          // Never brick Play on a flop-map miss/API blip - keep the dealt board and
          // identity-map so postflop can continue (tree probe may soft-skip later).
          queriedFlopCards = [...selectedFlopCards];
          flopMapMiss = true;
        }
        runtime.desiredFlopStr = selectedFlopCards.join("");
        runtime.flopStr = flopMapMiss ? selectedFlopCards.join("") : String(queriedFlop || selectedFlopCards.join(""));
        runtime.boardCards = selectedFlopCards;
        runtime.queriedBoardCards = queriedFlopCards;
        runtime.suitMappingBetweenFlops = createSuitMappingBetweenFlops(selectedFlopCards, queriedFlopCards);
        runtime.flopRankMap = createFlopRankMappingStrengthPaired(selectedFlopCards, queriedFlopCards);
        if (flopMapMiss) {
          setPostflopDebug({
            street: "flop",
            selectedBoard: selectedFlopCards,
            canonical1755: canonicalStr,
            queriedBoard: queriedFlopCards,
            note: "flop-map miss or API failure; using identity mapping so Play can continue",
          });
        }
      } else {
        const flopStr = chooseValidFlopFrom184(runtime?.dealt || {});
        if (!flopStr) {
          setCenterMessage("something went wrong - please refresh");
          setFeedback("Unable to deal a valid flop.");
          setPostflopDebug({ error: "No valid flop could be selected after excluding dealt cards." });
          return;
        }
        selectedFlopCards = flopStr.match(/.{1,2}/g) || [];
        runtime.desiredFlopStr = flopStr;
        runtime.flopStr = flopStr;
        runtime.boardCards = selectedFlopCards;
        runtime.queriedBoardCards = [...selectedFlopCards];
        runtime.suitMappingBetweenFlops = createSuitMappingBetweenFlops(selectedFlopCards, selectedFlopCards);
        runtime.flopRankMap = createFlopRankMappingStrengthPaired(selectedFlopCards, selectedFlopCards);
      }
    } else {
      const nextCard = chooseNextStreetCard(runtime?.dealt || {}, runtime?.boardCards || []);
      if (!nextCard) {
        setCenterMessage("something went wrong - please refresh");
        setFeedback("Unable to deal next street card.");
        setPostflopDebug({ error: "No valid turn/river card found." });
        return;
      }
      if (isPlayMode) {
        const desiredBoardBeforeCard = [...(runtime.boardCards || [])];
        const queriedBoardBeforeCard = [...(runtime.queriedBoardCards || [])];
        const queriedCard = resolveRunoutCanonicalCard(
          nextCard,
          desiredBoardBeforeCard,
          queriedBoardBeforeCard,
          runtime?.suitMappingBetweenFlops
        );
        runtime.boardCards = [...desiredBoardBeforeCard, nextCard];
        runtime.queriedBoardCards = [...queriedBoardBeforeCard, queriedCard];
      } else {
        runtime.boardCards = [...(runtime.boardCards || []), nextCard];
        runtime.queriedBoardCards = [...(runtime.queriedBoardCards || []), nextCard];
      }
    }

    setBoardCards([...(runtime.boardCards || [])]);
    setCenterMessage("");
    runtime.currentStreet = targetStreet;

    // New street: clear street chips only.
    runtime.invested = { ...makeSeatNumberMap(0) };
    runtime.committed = { ...makeSeatNumberMap(0) };
    runtime.currentBet = 0;
    syncFromRuntime(runtime);

    const active = PREFLOP_ORDER.filter((s) => !runtime.folded.has(s));
    if (active.length < 2) {
      setCenterMessage("something went wrong - please refresh");
      setFeedback("Hand cannot continue postflop with fewer than 2 players.");
      return;
    }

    if (hasAllInSeat(runtime)) {
      setActionOnSeat(null);
      setThinkingSeat(null);
      setHeroTurn(false);
      setHeroOptions([]);
      setHeroValidActions([]);
      setHeroBlockedActions([]);
      setIsRunning(false);
      if (targetStreet === "river") {
        runPlayShowdown(runtime, "Hand complete.");
        return;
      }
      const nextStreet = targetStreet === "flop" ? "turn" : "river";
      if (!isPlayMode && !hasTrainerTurnRiverAccess() && (nextStreet === "turn" || nextStreet === "river")) {
        setFeedback(`${String(targetStreet).toUpperCase()} complete. Turn & River require Improver or Pro.`);
        finishTrainerHand();
        return;
      }
      await beginPostflopStreet(runtime, nextStreet);
      return;
    }

    const derivedSpot = detectPostflopSpotFromHistory(runtime.preflopHistory || runtime.history);
    const lookupCandidates = buildPostflopLookupSpotCandidates(derivedSpot);
    const firstToAct = POSTFLOP_ORDER.find((s) => active.includes(s)) || active[0];
    const firstSeatCards = runtime.dealt[firstToAct] || [];
    const firstSeatLabel = handLabelFromTwoCards(firstSeatCards);
    const firstSeatComboKey = canonicalComboKey(firstSeatCards[0], firstSeatCards[1]);

    const softSkipNotGtoPostflop = () => {
      runtime.queue = [];
      runtime.pendingChanceNodeId = null;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      setActionOnSeat(null);
      setThinkingSeat(null);
      setHeroTurn(false);
      setWaitForHeroContinue(false);
      setIsRunning(false);
      setHeroOptions([]);
      setHeroValidActions([]);
      setHeroBlockedActions([]);
      syncFromRuntime(runtime);
      setPostflopDebug({
        street: targetStreet,
        flop: runtime.flopStr,
        selectedBoard: runtime.boardCards,
        queriedBoard: runtime.queriedBoardCards,
        derivedSpot,
        lookupCandidates,
        actingSeat: firstToAct,
        handCards: firstSeatCards,
        handLabel: firstSeatLabel,
        comboKey: firstSeatComboKey,
        error: "No IP/OOP-preserving V6 postflop spot for this flop.",
      });
      if (!isPlayMode) {
        setCenterMessage("Not GTO postflop situation - skipping");
        setFeedback("Not GTO postflop situation - skipping");
        recordHeroOutcome(targetStreet, "na", "Not GTO postflop, skipped");
        if (sitOutRef.current) {
          pendingDealRef.current = true;
          return;
        }
        if (autoNextTimerRef.current) clearTimeout(autoNextTimerRef.current);
        autoNextTimerRef.current = setTimeout(() => startTrainingRef.current?.(), 2000);
      } else {
        // Play: keep the board and finish the street with hardcoded actions - do not brick the hand.
        setCenterMessage("");
        setFeedback("Playing this board without a GTO tree.");
        if (firstToAct === runtime.heroSeat) {
          bindPlayHeroTurnWithoutLookup(runtime, targetStreet, {
            spot: null,
            nodeId: null,
            firstActor: firstToAct,
            firstActorAction: null,
          });
          return;
        }
        const villain = firstToAct;
        const facingAlready = Number(runtime?.invested?.[villain] || 0);
        const facingBet = Number(runtime?.currentBet || 0) > facingAlready + 1e-9;
        const { options: villainOpts, templateMap } = getPlayPostflopHeroOptions(targetStreet, facingBet, runtime, villain);
        setPostflopDecision({
          street: targetStreet,
          spot: null,
          flop: runtime?.flopStr || null,
          board: runtime?.boardCards || [],
          firstActor: villain,
          firstActorAction: null,
          nodeId: null,
          dbActions: [],
          dbGrid: [],
          playActionTemplateMap: templateMap,
          isPlayHardcoded: true,
        });
        setActionOnSeat(villain);
        setThinkingSeat(villain);
        setIsRunning(true);
        setFeedback(`${String(villain || "").toUpperCase()} is thinking...`);
        const delayMs = POST_FLOP_THINK_DELAY_MS;
        timerRef.current = setTimeout(() => {
          const fresh = runtimeRef.current;
          if (!fresh) {
            setThinkingSeat(null);
            setActionOnSeat(null);
            setIsRunning(false);
            return;
          }
          const pick = (villainOpts || []).find((a) => !/fold/i.test(String(a || ""))) || (villainOpts || [])[0] || "Check";
          const applied = applyPostflopContribution(villain, pick, fresh, { fromDb: false });
          const contribution = Number(applied?.contribution || 0);
          const displayAction = toPostflopDisplayAction(pick, contribution);
          fresh.history = [...fresh.history, { position: villain, action: String(displayAction || pick), raiseSize: extractNumber(displayAction), contribution }];
          if (/fold/i.test(String(pick || ""))) fresh.folded.add(villain);
          maybeVillainBetSpeech(villain, pick, contribution);
          syncFromRuntime(fresh);
          setThinkingSeat(null);
          setActionOnSeat(null);
          setIsRunning(false);
          const stillActive = PREFLOP_ORDER.filter((s) => !fresh.folded.has(s));
          if (stillActive.length < 2 || /fold/i.test(String(pick || ""))) {
            runPlayShowdown(fresh, "Hand complete.");
            return;
          }
          bindPlayHeroTurnWithoutLookup(fresh, targetStreet, {
            spot: null,
            nodeId: null,
            firstActor: villain,
            firstActorAction: displayAction || pick,
          });
        }, delayMs);
      }
    };

    if (!derivedSpot || !lookupCandidates.length) {
      softSkipNotGtoPostflop();
      return;
    }

    let spot = targetStreet === "flop" ? null : runtime.postflopLookupSpot || null;
    let data = null;
    let firstSeatDecision = null;
    let rootNodeId = "root";

    if (targetStreet === "flop") {
      setFeedback("Loading tree...");
      for (const cand of lookupCandidates) {
        try {
          const tryData = await fetchV6Grid({ spot: cand.spot, flop: runtime.flopStr, nodeId: "root" });
          if (!v6RootLooksUsable(tryData)) continue;
          let decision = null;
          if (firstToAct !== runtime.heroSeat) {
            const tryActions = Array.isArray(tryData?.actions) ? tryData.actions : [];
            const tryGrid = Array.isArray(tryData?.grid) ? tryData.grid : [];
            const tryNodeId = String(tryData?.nodeId || "root");
            decision = (isPlayMode && firstToAct !== runtime.heroSeat)
              ? await resolvePlayVillainPostflopDecision(firstToAct, runtime, tryActions, tryGrid, {
                spot: cand.spot,
                nodeId: tryNodeId,
              })
              : resolvePostflopSeatDecision(firstToAct, runtime, tryActions, tryGrid);
            if (!decisionHasUsableFrequencies(decision)) continue;
          }
          spot = cand.spot;
          data = tryData;
          firstSeatDecision = decision;
          rootNodeId = String(tryData?.nodeId || "root");
          runtime.postflopLookupSpot = spot;
          runtime.postflopDerivedSpot = cand.derived;
          if (cand.derived !== derivedSpot) runtime.gtoOffTree = true;
          break;
        } catch (_) {
          // Try next IP/OOP-preserving candidate.
        }
      }
      if (!spot || !data) {
        softSkipNotGtoPostflop();
        return;
      }
    } else if (!spot) {
      softSkipNotGtoPostflop();
      return;
    }

    if (isPlayMode && firstToAct === runtime.heroSeat) {
      setCenterMessage("");
      setPostflopDebug({
        street: targetStreet,
        flop: runtime.flopStr,
        selectedBoard: runtime.boardCards,
        queriedBoard: runtime.queriedBoardCards,
        derivedSpot,
        spot,
        postflopDerivedSpot: runtime.postflopDerivedSpot || derivedSpot,
        actingSeat: runtime.heroSeat,
        deferredLookup: true,
        note: "Hero postflop turn uses hardcoded actions; lookup deferred until action is chosen.",
      });
      bindPlayHeroTurnWithoutLookup(runtime, targetStreet, {
        spot,
        nodeId: null,
        firstActor: firstToAct,
        firstActorAction: null,
      });
      return;
    }

    setFeedback("Loading tree...");
    try {
      let resolvedFromChance = false;
      let chanceSourceNodeId = null;
      let chanceCard = null;
      let chanceChosenNodeId = null;
      if (targetStreet === "flop") {
        // Root already loaded while probing IP/OOP-preserving candidates.
      } else if (!runtime.pendingChanceNodeId) {
        data = await fetchV6Grid({ spot, flop: runtime.flopStr, nodeId: "root" });
        rootNodeId = String(data?.nodeId || "root");
      } else {
        chanceSourceNodeId = String(runtime.pendingChanceNodeId || "");
        chanceCard = String((isPlayMode ? runtime.queriedBoardCards : runtime.boardCards)?.slice(-1)?.[0] || "");
        const chanceNode = await fetchV6Grid({ spot, flop: runtime.flopStr, nodeId: chanceSourceNodeId });
        chanceChosenNodeId = getChanceChildNodeId(chanceNode?.v6 || null, chanceCard);
        if (!chanceChosenNodeId) {
          softSkipNotGtoPostflop();
          return;
        }
        data = await fetchV6Grid({ spot, flop: runtime.flopStr, nodeId: chanceChosenNodeId });
        rootNodeId = String(data?.nodeId || chanceChosenNodeId);
        resolvedFromChance = true;
      }
      runtime.pendingChanceNodeId = null;
      const nodeActions = Array.isArray(data?.actions) ? data.actions : [];
      const nodeGrid = Array.isArray(data?.grid) ? data.grid : [];
      const nodeV6 = data?.v6 || null;
      if (!firstSeatDecision) {
        firstSeatDecision = (isPlayMode && firstToAct !== runtime.heroSeat)
          ? await resolvePlayVillainPostflopDecision(firstToAct, runtime, nodeActions, nodeGrid, { spot, nodeId: rootNodeId })
          : resolvePostflopSeatDecision(firstToAct, runtime, nodeActions, nodeGrid);
      }
      if (firstToAct !== runtime.heroSeat && !decisionHasUsableFrequencies(firstSeatDecision)) {
        softSkipNotGtoPostflop();
        return;
      }
      setPostflopDebug({
        street: targetStreet,
        flop: runtime.flopStr,
        selectedBoard: runtime.boardCards,
        queriedBoard: runtime.queriedBoardCards,
        derivedSpot,
        spot,
        postflopDerivedSpot: runtime.postflopDerivedSpot || derivedSpot,
        nodeId: rootNodeId,
        resolvedFromChance,
        chanceSourceNodeId,
        chanceCard,
        chanceChosenNodeId,
        actingSeat: firstToAct,
        handCards: firstSeatDecision.handCards,
        handLabel: firstSeatDecision.handLabel,
        comboKey: firstSeatDecision.comboKey,
        dbKind: data?.v6?.kind,
        dbStreet: data?.street,
          actionChildren: Array.isArray(nodeV6?.actionChildren) ? nodeV6.actionChildren : [],
        dbActions: firstSeatDecision.actions,
        dbFrequencies: firstSeatDecision.frequencies,
        mappingDebug: firstSeatDecision.mappingDebug || null,
        exactComboMatch: !!firstSeatDecision.freqInfo.exactMatch,
        matchedComboHand: firstSeatDecision.freqInfo.matchedHand,
        usedFallbackCellFrequencies: !!firstSeatDecision.freqInfo.usedFallbackCellFrequencies,
        validActions: firstSeatDecision.validActions,
      });

      const bindHeroTurn = (heroSeatToAct, contextActionText = null, nodeData = {}) => {
        const useActions = Array.isArray(nodeData?.actions) ? nodeData.actions : nodeActions;
        const useGrid = Array.isArray(nodeData?.grid) ? nodeData.grid : nodeGrid;
        const useV6 = nodeData?.v6 || nodeV6;
        const useNodeId = String(nodeData?.nodeId || rootNodeId);
        const heroSeatDecision = resolvePostflopSeatDecision(heroSeatToAct, runtime, useActions, useGrid);
        // Turn/river drill autopilot: play the hero's GTO-sampled action silently
        // (avoiding fold when possible) instead of pausing for input.
        if (!isPlayMode && runtime.autopilotUntil) {
          const weighted = pickByWeightsNoFold(heroSeatDecision.actions, heroSeatDecision.frequencies);
          const chosen = weighted?.action || "Check";
          const chosenIdx = heroSeatDecision.actions.findIndex((a) => String(a || "").trim().toLowerCase() === String(chosen || "").trim().toLowerCase());
          const nextNodeId = getActionChildNodeId(useV6, chosenIdx);
          const applied = applyPostflopContribution(heroSeatToAct, chosen, runtime, { fromDb: true });
          const contribution = Number(applied?.contribution || 0);
          const displayAction = toPostflopDisplayAction(chosen, contribution);
          runtime.history = [...runtime.history, { position: heroSeatToAct, action: String(displayAction || chosen), raiseSize: extractNumber(displayAction), contribution }];
          if (/fold/i.test(String(chosen || ""))) runtime.folded.add(heroSeatToAct);
          syncFromRuntime(runtime);
          setHeroTurn(false);
          setFeedback("Dealing...");
          continuePostflopRef.current?.({ spot, firstActor: firstToAct, nextNodeIdAfterHero: nextNodeId || null });
          return;
        }
        const heroCfg = buildPostflopHeroTurnConfig(heroSeatDecision.actions, heroSeatDecision.frequencies);
        const heroUiMap = buildHeroUiActionMapFromDb(heroCfg.options);
        const validUi = heroCfg.validActions.map((a) => toPostflopHeroUiActionLabel(a));
        const uiPctMap = {};
        for (const action of heroCfg.options) {
          const key = toPostflopHeroUiActionLabel(action).trim().toLowerCase();
          uiPctMap[key] = Number(heroCfg.actionPctMap?.[String(action || "").trim().toLowerCase()] ?? 0);
        }
        const nextPostflopDecision = {
          street: targetStreet,
          spot,
          flop: runtime.flopStr,
          board: runtime.boardCards,
          firstActor: firstToAct,
          firstActorAction: contextActionText,
          nodeId: useNodeId,
          dbActions: useActions,
          dbGrid: useGrid,
          v6Kind: useV6?.kind || null,
          actionChildren: Array.isArray(useV6?.actionChildren) ? [...useV6.actionChildren] : [],
          nextNodeIdAfterHero: null,
          heroUiToDbActionMap: heroUiMap.uiToDb,
        };
        const nextFeedback = contextActionText ? `Villain acted (${contextActionText}). Your turn.` : "Your turn.";
        setActionOnSeat(heroSeatToAct);
        setHeroTurn(true);
        setHeroBlockedActions([]);
        setHeroOptions(heroUiMap.optionsUi);
        setHeroValidActions(validUi);
        setHeroDecisionContext({
          seat: heroSeatToAct,
          handCards: heroSeatDecision.handCards,
          handLabel: heroSeatDecision.handLabel,
          matchedCellLabel: String(heroSeatDecision.cell?.label || "--"),
          exactComboMatch: !!heroSeatDecision.freqInfo.exactMatch,
          matchedComboHand: heroSeatDecision.freqInfo.matchedHand,
          usedFallbackCellFrequencies: !!heroSeatDecision.freqInfo.usedFallbackCellFrequencies,
          actions: heroUiMap.optionsUi,
          frequencies: heroSeatDecision.frequencies,
          validActions: validUi,
          actionPctMap: uiPctMap,
          rangeGrid: useGrid || null,
          rangeActions: useActions || [],
          rngRoll: null,
          rngTotal: null,
        });
        setPostflopDebug({
          street: targetStreet,
          flop: runtime.flopStr,
          board: runtime.boardCards,
          derivedSpot,
          spot,
          nodeId: useNodeId,
          actingSeat: heroSeatToAct,
          handCards: heroSeatDecision.handCards,
          handLabel: heroSeatDecision.handLabel,
          comboKey: heroSeatDecision.comboKey,
          dbKind: useV6?.kind,
          dbStreet: data?.street,
          actionChildren: Array.isArray(useV6?.actionChildren) ? useV6.actionChildren : [],
          dbActions: heroSeatDecision.actions,
          dbFrequencies: heroSeatDecision.frequencies,
          exactComboMatch: !!heroSeatDecision.freqInfo.exactMatch,
          matchedComboHand: heroSeatDecision.freqInfo.matchedHand,
          usedFallbackCellFrequencies: !!heroSeatDecision.freqInfo.usedFallbackCellFrequencies,
          validActions: heroSeatDecision.validActions,
        });
        setPostflopDecision(nextPostflopDecision);
        setFeedback(nextFeedback);
        captureReplayCheckpoint({
          runtime,
          streetPhase: targetStreet,
          boardCards: runtime.boardCards || [],
          centerMessage: "",
          feedback: nextFeedback,
          heroOptions: heroUiMap.optionsUi,
          heroValidActions: validUi,
          heroBlockedActions: [],
          heroDecisionContext: {
            seat: heroSeatToAct,
            handCards: heroSeatDecision.handCards,
            handLabel: heroSeatDecision.handLabel,
            matchedCellLabel: String(heroSeatDecision.cell?.label || "--"),
            exactComboMatch: !!heroSeatDecision.freqInfo.exactMatch,
            matchedComboHand: heroSeatDecision.freqInfo.matchedHand,
            usedFallbackCellFrequencies: !!heroSeatDecision.freqInfo.usedFallbackCellFrequencies,
            actions: heroUiMap.optionsUi,
            frequencies: heroSeatDecision.frequencies,
            validActions: validUi,
            actionPctMap: uiPctMap,
            rngRoll: null,
            rngTotal: null,
          },
          heroTurn: true,
          waitForHeroContinue: false,
          actionOnSeat: heroSeatToAct,
          decisionDebugRows,
          postflopDebug: {
            street: targetStreet,
            flop: runtime.flopStr,
            board: runtime.boardCards,
            derivedSpot,
            spot,
            actingSeat: firstToAct,
            handCards: firstSeatDecision.handCards,
            handLabel: firstSeatDecision.handLabel,
            comboKey: firstSeatDecision.comboKey,
            dbKind: data?.v6?.kind,
            dbStreet: data?.street,
            dbActions: firstSeatDecision.actions,
            dbFrequencies: firstSeatDecision.frequencies,
            exactComboMatch: !!firstSeatDecision.freqInfo.exactMatch,
            matchedComboHand: firstSeatDecision.freqInfo.matchedHand,
            usedFallbackCellFrequencies: !!firstSeatDecision.freqInfo.usedFallbackCellFrequencies,
            validActions: firstSeatDecision.validActions,
          },
          postflopDecision: nextPostflopDecision,
        });
      };

      if (firstToAct === runtime.heroSeat) {
        bindHeroTurn(runtime.heroSeat, null, {
          nodeId: rootNodeId,
          actions: nodeActions,
          grid: nodeGrid,
          v6: nodeV6,
        });
      } else {
        const isAutopilotDeal = !isPlayMode && !!runtime.autopilotUntil;
        if (!isAutopilotDeal) {
          setThinkingSeat(firstToAct);
          setIsRunning(true);
          setFeedback(`${firstToAct.toUpperCase()} is thinking...`);
        }
        const villainDelayMs = isPlayMode ? POST_FLOP_THINK_DELAY_MS : (isAutopilotDeal ? 0 : realisticThinkDelayMs());
        timerRef.current = setTimeout(async () => {
          try {
            let villainDecision = firstSeatDecision;
            if (!villainDecision?.frequencies?.length) {
              villainDecision = await resolvePlayVillainPostflopDecision(firstToAct, runtime, nodeActions, nodeGrid, { spot, nodeId: rootNodeId });
            }
            const profileMix = applyProfileToMixedStrategy(villainDecision.actions, villainDecision.frequencies, playVillainProfileKey);
            const weighted = profileMix.forcedIndex != null
              ? { action: villainDecision.actions?.[profileMix.forcedIndex] || "Check", rngRoll: null, rngTotal: null }
              : (isAutopilotDeal
                ? pickByWeightsNoFold(villainDecision.actions, profileMix.frequencies)
                : pickByWeights(villainDecision.actions, profileMix.frequencies));
            const chosen = weighted?.action || "Check";
            const chosenIdx = villainDecision.actions.findIndex((a) => String(a || "").trim().toLowerCase() === String(chosen || "").trim().toLowerCase());
            const nextNodeId = getActionChildNodeId(nodeV6, chosenIdx);
            const applied = applyPostflopContribution(firstToAct, chosen, runtime, { fromDb: true });
            const contribution = Number(applied?.contribution || 0);
            const speech = maybeVillainBetSpeech(firstToAct, chosen, contribution);
            if (speech) setPlayVillainSpeech(speech);
            const displayAction = toPostflopDisplayAction(chosen, contribution);
            runtime.history = [...runtime.history, { position: firstToAct, action: String(displayAction || ""), raiseSize: extractNumber(displayAction), contribution }];
            if (/fold/i.test(String(chosen || ""))) runtime.folded.add(firstToAct);
            syncFromRuntime(runtime);
            setDecisionDebugRows((prev) => [
              ...prev,
              {
                id: `${Date.now()}-postflop-${targetStreet}-${firstToAct}-${prev.length}`,
                phase: "postflop",
                source: "villain",
                nodeId: rootNodeId,
                actionIndex: chosenIdx,
                nextNodeId,
                seat: firstToAct,
                handCards: villainDecision.handCards,
                handLabel: villainDecision.handLabel,
                matchedCellLabel: String(villainDecision.cell?.label || "--"),
                exactComboMatch: !!villainDecision.freqInfo.exactMatch,
                usedFallbackCellFrequencies: !!villainDecision.freqInfo.usedFallbackCellFrequencies,
                actions: villainDecision.actions,
                frequencies: profileMix.frequencies,
                validActions: villainDecision.validActions,
                mappingDebug: villainDecision.mappingDebug || null,
                rngRoll: weighted?.rngRoll ?? null,
                rngTotal: weighted?.rngTotal ?? null,
                chosenAction: displayAction,
                decisionOutcome: "auto",
                note: `postflop villain first action (${targetStreet})`,
                profileOverride: playVillainProfileKey || null,
                profileForced: profileMix.forcedIndex != null,
              },
            ]);
            setThinkingSeat(null);
            setIsRunning(false);
            const heroStillIn = runtime.heroSeat && !runtime.folded.has(runtime.heroSeat);
            const activeAfterVillain = PREFLOP_ORDER.filter((s) => !runtime.folded.has(s));
            const villainFolded = /fold/i.test(String(chosen || ""));
            if (
              heroStillIn &&
              nextNodeId &&
              !isZeroNodeId(nextNodeId) &&
              activeAfterVillain.length >= 2 &&
              !villainFolded
            ) {
              if (isPlayMode) {
                // Confirm the child is a real decision node before enabling hero actions.
                setIsRunning(true);
                try {
                  const nextData = await fetchV6Grid({
                    spot,
                    flop: runtime.flopStr,
                    nodeId: String(nextNodeId),
                  });
                  const kind = nextData?.v6?.kind;
                  if (kind === "chance") {
                    runtime.pendingChanceNodeId = String(nextData?.nodeId || nextNodeId);
                    setIsRunning(false);
                    setActionOnSeat(null);
                    setHeroTurn(false);
                    continuePostflopRef.current?.({
                      spot,
                      firstActor: firstToAct,
                      nextNodeIdAfterHero: null,
                    });
                  } else if (kind !== "decision") {
                    setIsRunning(false);
                    setActionOnSeat(null);
                    setHeroTurn(false);
                    continuePostflopRef.current?.({
                      spot,
                      firstActor: firstToAct,
                      nextNodeIdAfterHero: null,
                    });
                  } else {
                    bindPlayHeroTurnWithoutLookup(runtime, targetStreet, {
                      spot,
                      nodeId: String(nextData?.nodeId || nextNodeId),
                      firstActor: firstToAct,
                      firstActorAction: displayAction,
                    });
                  }
                } catch (err) {
                  softSkipNotGtoPostflop();
                  setPostflopDebug((prev) => ({
                    ...(prev || {}),
                    nodeId: nextNodeId,
                    fetchAfterVillainError: err?.message || String(err),
                  }));
                }
              } else {
                fetchV6Grid({ spot, flop: runtime.flopStr, nodeId: nextNodeId })
                  .then((nextData) => {
                    bindHeroTurn(runtime.heroSeat, displayAction, {
                      nodeId: String(nextData?.nodeId || nextNodeId),
                      actions: Array.isArray(nextData?.actions) ? nextData.actions : [],
                      grid: Array.isArray(nextData?.grid) ? nextData.grid : [],
                      v6: nextData?.v6 || null,
                    });
                  })
                  .catch((err) => {
                    softSkipNotGtoPostflop();
                    setPostflopDebug((prev) => ({
                      ...(prev || {}),
                      nodeId: nextNodeId,
                      fetchAfterVillainError: err?.message || String(err),
                    }));
                  });
              }
            } else if (activeAfterVillain.length < 2 || villainFolded || isZeroNodeId(nextNodeId)) {
              // Opponent folded / terminal zero-id / only one player left: settle or advance.
              runtime.pendingChanceNodeId = null;
              setActionOnSeat(null);
              setHeroTurn(false);
              if (activeAfterVillain.length < 2 || villainFolded) {
                const settled = settlePlayHandScore(runtime);
                setFeedback(
                  isPlayMode && settled
                    ? `Hand ended. ${settled.handDelta >= 0 ? "+" : ""}${settled.handDelta.toFixed(1)} chips.`
                    : `Villain acted (${displayAction}).`
                );
                if (!isPlayMode) finishTrainerHand();
              } else {
                setFeedback(`Villain acted (${displayAction}).`);
                continuePostflopRef.current?.({
                  spot,
                  firstActor: firstToAct,
                  nextNodeIdAfterHero: null,
                });
              }
            } else {
              // Missing child with both players still in - advance street / showdown.
              runtime.pendingChanceNodeId = null;
              setActionOnSeat(null);
              setHeroTurn(false);
              setFeedback(`Villain acted (${displayAction}).`);
              continuePostflopRef.current?.({
                spot,
                firstActor: firstToAct,
                nextNodeIdAfterHero: null,
              });
            }
          } catch (err) {
            softSkipNotGtoPostflop();
            setPostflopDebug((prev) => ({
              ...(prev || {}),
              nodeId: rootNodeId,
              selectedBoard: runtime?.boardCards || [],
              queriedBoard: runtime?.queriedBoardCards || [],
              mappingDebug: err?.__solverDebug || null,
              error: err?.message || String(err),
            }));
          }
        }, villainDelayMs);
      }
    } catch (err) {
      softSkipNotGtoPostflop();
      setPostflopDebug({
        street: targetStreet,
        flop: runtime.flopStr,
        selectedBoard: runtime.boardCards,
        queriedBoard: runtime.queriedBoardCards,
        derivedSpot,
        spot,
        actingSeat: firstToAct,
        handCards: firstSeatCards,
        handLabel: firstSeatLabel,
        comboKey: firstSeatComboKey,
        mappingDebug: err?.__solverDebug || null,
        error: err?.message || String(err),
      });
    }
  }, [applyPostflopContribution, bindPlayHeroTurnWithoutLookup, captureReplayCheckpoint, decisionDebugRows, finishTrainerHand, hasPlayPremiumAccess, isPlayMode, maybeVillainBetSpeech, playVillainProfileKey, recordHeroOutcome, resolvePlayVillainPostflopDecision, resolvePostflopSeatDecision, runPlayShowdown, settlePlayHandScore, syncFromRuntime]);

  const startPostflop = React.useCallback(async (runtime) => {
    runtime.preflopHistory = [...(runtime.history || [])];
    runtime.pendingChanceNodeId = null;
    await beginPostflopStreet(runtime, "flop");
  }, [beginPostflopStreet]);

  const advancePreflop = React.useCallback(() => {
    const runtime = runtimeRef.current;
    if (!runtime) return;
    // Trainer: once the hero folds, the training hand is over.
    if (!isPlayMode && runtime.folded.has(runtime.heroSeat)) {
      runtime.queue = [];
      setActionOnSeat(null);
      setThinkingSeat(null);
      setHeroTurn(false);
      setIsRunning(false);
      setCenterMessage("");
      syncFromRuntime(runtime);
      setFeedback("You folded. Hand over.");
      finishTrainerHand();
      return;
    }
    // Trainer: skip multiway pots. The database only covers heads-up spots, so
    // if two players besides the hero have voluntarily put in more than the big
    // blind and are both still in, end the hand immediately and redeal.
    if (!isPlayMode && !runtime.__multiwayEnded) {
      const villainsBeyondBB = PREFLOP_ORDER.filter(
        (s) => s !== runtime.heroSeat && !runtime.folded.has(s) && Number(runtime.committed?.[s] || 0) > 1.001
      );
      if (villainsBeyondBB.length >= 2) {
        runtime.__multiwayEnded = true;
        runtime.queue = [];
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
        setActionOnSeat(null);
        setThinkingSeat(null);
        setHeroTurn(false);
        setWaitForHeroContinue(false);
        setIsRunning(false);
        setCenterMessage("Multiway hand, not in database");
        syncFromRuntime(runtime);
        setFeedback("Multiway hand, not in database. N/A (no data). Dealing next hand...");
        recordHeroOutcome("preflop", "na", "Multiway, skipped");
        if (autoNextTimerRef.current) clearTimeout(autoNextTimerRef.current);
        if (sitOutRef.current) {
          pendingDealRef.current = true;
        } else {
          autoNextTimerRef.current = setTimeout(() => startTrainingRef.current?.(), 2000);
        }
        return;
      }
    }
    const activePreflopPlayers = PREFLOP_ORDER.filter((s) => !runtime.folded.has(s));
    if (activePreflopPlayers.length <= 1) {
      runtime.queue = [];
      setStreetPhase("preflop");
      setActionOnSeat(null);
      setThinkingSeat(null);
      setHeroTurn(false);
      setIsRunning(false);
      setCenterMessage("");
      syncFromRuntime(runtime);
      const settled = settlePlayHandScore(runtime);
      setFeedback(isPlayMode && settled ? `Hand ended preflop. ${settled.handDelta >= 0 ? "+" : ""}${settled.handDelta.toFixed(1)} chips.` : "Hand ended preflop.");
      if (!isPlayMode) finishTrainerHand();
      return;
    }
    if (!runtime.queue.length) {
      const activePlayers = PREFLOP_ORDER.filter((s) => !runtime.folded.has(s));
      if (activePlayers.length <= 1) {
        setStreetPhase("preflop");
        setActionOnSeat(null);
        setThinkingSeat(null);
        setHeroTurn(false);
        setIsRunning(false);
        setCenterMessage("");
        const settled = settlePlayHandScore(runtime);
        setFeedback(isPlayMode && settled ? `Hand ended preflop. ${settled.handDelta >= 0 ? "+" : ""}${settled.handDelta.toFixed(1)} chips.` : "Hand ended preflop.");
        if (!isPlayMode) finishTrainerHand();
      } else if (!isPlayMode && runtime.drillStreet === "preflop") {
        // Preflop-only drill: don't play postflop, move straight to the next hand.
        setActionOnSeat(null);
        setThinkingSeat(null);
        setHeroTurn(false);
        setIsRunning(false);
        setFeedback("Preflop complete. Nice work, next hand coming up.");
        finishTrainerHand();
      } else {
        setFeedback("Preflop action complete. Moving to flop...");
        startPostflop(runtime);
      }
      return;
    }
    const actor = runtime.queue[0];
    setActionOnSeat(actor);
    if (actor === runtime.heroSeat) {
      const resolvedDecision =
        getDecisionForSeat(actor, runtime.dealt[actor], runtime.history) ||
        buildFallbackFoldDecision(actor, runtime.dealt[actor], "no preflop node for hero");
      const raiseCountBefore = countRaises(runtime.history);
      const heroActionSet = isPlayMode
        ? buildPlayHeroActionSet(raiseCountBefore, actor)
        : buildHeroActionSetBySpot(raiseCountBefore, actor);
      setCenterMessage("");
      setHeroOptions(heroActionSet);
      setHeroValidActions(expandHeroValidActionsForUi(resolvedDecision?.positiveActions || [], actor, raiseCountBefore));
      setHeroDecisionContext(resolvedDecision?.debug || null);
      setHeroBlockedActions([]);
      setHeroTurn(true);
      setWaitForHeroContinue(false);
      setThinkingSeat(null);
      setIsRunning(false);
      setFeedback("Your turn (hero).");
      captureReplayCheckpoint({
        runtime,
        streetPhase: "preflop",
        boardCards: runtime.boardCards || [],
        centerMessage: "",
        feedback: "Your turn (hero).",
          heroOptions: heroActionSet,
        heroValidActions: expandHeroValidActionsForUi(resolvedDecision?.positiveActions || [], actor, raiseCountBefore),
        heroBlockedActions: [],
        heroDecisionContext: resolvedDecision?.debug || null,
        heroTurn: true,
        waitForHeroContinue: false,
        actionOnSeat: actor,
        decisionDebugRows,
        postflopDebug,
        postflopDecision,
      });
      return;
    }
    setThinkingSeat(actor);
    setHeroTurn(false);
    setIsRunning(true);
    setFeedback(`${actor.toUpperCase()} is thinking...`);
    const preflopDelayMs = isPlayMode ? PRE_FLOP_THINK_DELAY_MS : Math.round(400 + Math.random() * 800);
    timerRef.current = setTimeout(() => {
      const fresh = runtimeRef.current;
      if (!fresh) {
        setThinkingSeat(null);
        setActionOnSeat(null);
        setIsRunning(false);
        return;
      }
      const villain = fresh.queue[0];
      const decision =
        getDecisionForSeat(villain, fresh.dealt[villain], fresh.history) ||
        buildFallbackFoldDecision(villain, fresh.dealt[villain], "no preflop node for villain");
      setDecisionDebugRows((prev) => [
        ...prev,
        {
          id: `${Date.now()}-${villain}-${prev.length}`,
          phase: "preflop",
          source: "villain",
          decisionOutcome: "auto",
          validActions: decision?.positiveActions || [],
          ...decision.debug,
        },
      ]);
      const parsed = decision.parsed;
      const { contribution, nextCurrentBet } = applyContribution(villain, parsed, fresh.invested, fresh.committed, fresh.stacks, fresh.currentBet);
      fresh.currentBet = nextCurrentBet;
      fresh.mainPot = Number((Number(fresh.mainPot || 0) + Number(contribution || 0)).toFixed(2));
      fresh.history = [...fresh.history, { position: villain, action: parsed.action, raiseSize: parsed.raiseSize, contribution }];
      if (parsed.action === "fold") fresh.folded.add(villain);
      if (parsed.aggressive) fresh.queue = nextActingQueueFromRaiser(villain, fresh.folded);
      else fresh.queue = fresh.queue.slice(1).filter((s) => !fresh.folded.has(s));
      syncFromRuntime(fresh);
      setThinkingSeat(null);
      advancePreflop();
    }, preflopDelayMs);
  }, [
    applyContribution,
    buildFallbackFoldDecision,
    captureReplayCheckpoint,
    decisionDebugRows,
    finishTrainerHand,
    getDecisionForSeat,
    isPlayMode,
    postflopDebug,
    postflopDecision,
    recordHeroOutcome,
    settlePlayHandScore,
    startPostflop,
    syncFromRuntime,
  ]);

  // Deal cards honoring the drill's hero-hand filter (trainer mode).
  const dealCardsWithFilter = React.useCallback((chosenHeroSeat, handsFilter) => {
    const heroCards = sampleHeroCardsFromFilter(handsFilter);
    const deck = buildDeck().filter((c) => !heroCards || !heroCards.includes(c));
    const dealt = {};
    for (const seat of PREFLOP_ORDER) {
      dealt[seat] = seat === chosenHeroSeat && heroCards ? [...heroCards] : [deck.pop(), deck.pop()];
    }
    return dealt;
  }, []);

  // Synchronously simulate a full preflop (everyone plays sampled GTO) until we
  // find a line that reaches postflop and matches all drill filters. Used to
  // deal Flop/Turn/River drills straight into the target situation.
  const simulatePreflopToPostflop = React.useCallback((settings) => {
    const MAX_ATTEMPTS = 700;
    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      // After many failures, bias the hero away from folding so restrictive
      // hand filters (e.g. weak hands) can still reach postflop occasionally.
      const biasHeroNoFold = attempt >= 250;
      const chosenHeroSeat = settings.heroPosition !== "any"
        ? settings.heroPosition
        : PREFLOP_ORDER[Math.floor(Math.random() * PREFLOP_ORDER.length)];
      const dealt = dealCardsWithFilter(chosenHeroSeat, settings.hands);
      const runtime = {
        heroSeat: chosenHeroSeat,
        dealt,
        folded: new Set(),
        history: [],
        invested: { ...makeSeatNumberMap(0), sb: 0.5, bb: 1 },
        committed: { ...makeSeatNumberMap(0), sb: 0.5, bb: 1 },
        stacks: { ...makeSeatNumberMap(100), sb: 99.5, bb: 99 },
        mainPot: 1.5,
        currentBet: 1,
        queue: [...PREFLOP_ORDER],
        boardCards: [],
        queriedBoardCards: [],
        flopStr: null,
        desiredFlopStr: null,
        suitMappingBetweenFlops: null,
        flopRankMap: null,
        pendingChanceNodeId: null,
        gtoOffTree: false,
        __playSettled: false,
      };
      let guard = 0;
      while (runtime.queue.length && guard++ < 48) {
        const actor = runtime.queue[0];
        const decision =
          getDecisionForSeat(actor, runtime.dealt[actor], runtime.history) ||
          buildFallbackFoldDecision(actor, runtime.dealt[actor], "no preflop node (presim)");
        let parsed = decision.parsed;
        if (actor === chosenHeroSeat && biasHeroNoFold) {
          const weighted = pickByWeightsNoFold(decision.debug?.actions || [], decision.debug?.frequencies || []);
          parsed = classifyAction(weighted?.action || "Fold", countRaises(runtime.history));
        }
        const { contribution, nextCurrentBet } = applyContribution(actor, parsed, runtime.invested, runtime.committed, runtime.stacks, runtime.currentBet);
        runtime.currentBet = nextCurrentBet;
        runtime.mainPot = Number((Number(runtime.mainPot || 0) + Number(contribution || 0)).toFixed(2));
        runtime.history = [...runtime.history, { position: actor, action: parsed.action, raiseSize: parsed.raiseSize, contribution }];
        if (parsed.action === "fold") runtime.folded.add(actor);
        if (parsed.aggressive) runtime.queue = nextActingQueueFromRaiser(actor, runtime.folded);
        else runtime.queue = runtime.queue.slice(1).filter((s) => !runtime.folded.has(s));
        if (runtime.folded.has(chosenHeroSeat)) break;
      }
      if (runtime.folded.has(chosenHeroSeat)) continue;
      const active = PREFLOP_ORDER.filter((s) => !runtime.folded.has(s));
      // Heads-up only: multiway lines aren't in the database.
      if (active.length !== 2) continue;
      const derivedSpot = detectPostflopSpotFromHistory(runtime.history);
      if (!derivedSpot || !remapSpotForLegacyDb(derivedSpot)) continue;
      if (!spotMatchesPotType(derivedSpot, settings.potType)) continue;
      if (settings.relPosition !== "any") {
        const heroIp = heroIsIpPostflop(chosenHeroSeat, active);
        if (settings.relPosition === "ip" && !heroIp) continue;
        if (settings.relPosition === "oop" && heroIp) continue;
      }
      return runtime;
    }
    return null;
  }, [applyContribution, buildFallbackFoldDecision, dealCardsWithFilter, getDecisionForSeat]);

  const startTraining = React.useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (playAutoAdvanceTimerRef.current) {
      clearTimeout(playAutoAdvanceTimerRef.current);
      playAutoAdvanceTimerRef.current = null;
    }
    if (autoNextTimerRef.current) {
      clearTimeout(autoNextTimerRef.current);
      autoNextTimerRef.current = null;
    }
    handReviewRef.current = [];
    pendingDealRef.current = false;
    setHandReview(null);
    if (isPlayMode) setPlaySessionStarted(true);
    // Expired trial / no plan: do not deal or continue hands.
    if (!isEmbed) {
      if (isPlayMode) {
        if (isPaywallRequired() || !hasFeatureAccess("play")) {
          openSubscriptionGate("play");
          setIsRunning(false);
          setHeroTurn(false);
          setActionOnSeat(null);
          try { onExitPlayLobby?.(); } catch (_) {}
          return;
        }
      } else if (isPaywallRequired() || !hasFeatureAccess("trainer")) {
        openSubscriptionGate("trainer");
        setIsRunning(false);
        setHeroTurn(false);
        setActionOnSeat(null);
        setFeedback("Subscribe to keep training.");
        return;
      }
    }
    const drill = isPlayMode ? DRILL_DEFAULTS : (drillSettingsRef.current || DRILL_DEFAULTS);
    if (!isPlayMode && (drill.street === "turn" || drill.street === "river") && !hasTrainerTurnRiverAccess()) {
      ensureTurnRiverAccess(drill.street);
      setIsRunning(false);
      return;
    }
    const randomHero = (isPlayMode && !isPlaySixMax)
      ? (playHandCounter % 2 === 0 ? "bb" : "sb")
      : (drill.heroPosition !== "any" ? drill.heroPosition : PREFLOP_ORDER[Math.floor(Math.random() * PREFLOP_ORDER.length)]);
    setHeroSeat(randomHero);
    if (isPlayMode) {
      const activeSeats = (isPlaySixMax ? PREFLOP_ORDER : ["sb", "bb"]);
      const seededNames = buildPlayOpponentNamesBySeat(activeSeats, randomHero);
      if (playConfig?.opponentName && !isPlaySixMax) {
        const vill = randomHero === "sb" ? "bb" : "sb";
        seededNames[vill] = String(playConfig.opponentName);
      }
      setPlayOpponentNamesBySeat(seededNames);
    }
    const isPostflopDrill = !isPlayMode && ["flop", "turn", "river"].includes(drill.street);
    let dealt;
    let presimRuntime = null;
    if (isPostflopDrill) {
      presimRuntime = simulatePreflopToPostflop(drill);
      if (!presimRuntime) {
        setFeedback("Couldn't build a spot matching your filters. Loosen the hand/position/pot filters, then press Deal new hand.");
        setIsRunning(false);
        setActionOnSeat(null);
        setThinkingSeat(null);
        setHeroTurn(false);
        setWaitForHeroContinue(false);
        return;
      }
      dealt = presimRuntime.dealt;
      setHeroSeat(presimRuntime.heroSeat);
    } else if (!isPlayMode) {
      dealt = dealCardsWithFilter(randomHero, drill.hands);
    } else {
      const deck = buildDeck();
      dealt = {};
      for (const seat of PREFLOP_ORDER) dealt[seat] = [deck.pop(), deck.pop()];
    }
    setHandsBySeat(dealt);
    setHeroBlockedActions([]);
    setHeroOptions([]);
    setHeroValidActions([]);
    setHeroDecisionContext(null);
    setWaitForHeroContinue(false);
    setDecisionDebugRows([]);
    setPostflopDebug(null);
    setPostflopDecision(null);
    setHandSummary(null);
    setShowdownRevealedSeats(new Set());
    setShowdownWinnerSeat(null);
    setShowdownAnimating(false);
    replayCheckpointRef.current = null;
    setHasReplayCheckpoint(false);
    setHandStreetResults({
      preflop: { blunders: 0, correct: 0 },
      flop: { blunders: 0, correct: 0 },
      turn: { blunders: 0, correct: 0 },
      river: { blunders: 0, correct: 0 },
    });
    setBoardCards([]);
    setCenterMessage("");
    setStreetPhase("preflop");
    setPlayVillainSpeech("");
    if (isPostflopDrill && presimRuntime) {
      // Jump straight to the target street: preflop already played out silently.
      // Onboarding / continuePostflopThroughRiver: start on flop but play out to river.
      if (playThroughRiver) {
        presimRuntime.drillStreet = "full";
        presimRuntime.autopilotUntil = null;
      } else {
        presimRuntime.drillStreet = drill.street;
        presimRuntime.autopilotUntil = drill.street === "flop" ? null : drill.street;
      }
      runtimeRef.current = presimRuntime;
      setMainPot(Number(presimRuntime.mainPot || 0));
      setFeedback("Dealing...");
      syncFromRuntime(presimRuntime);
      startPostflop(presimRuntime);
      return;
    }
    setMainPot(1.5);
    setFeedback("Dealing...");
    const playFolded = (isPlayMode && !isPlaySixMax) ? new Set(["lj", "hj", "co", "btn"]) : new Set();
    const runtime = {
      heroSeat: randomHero,
      dealt,
      folded: playFolded,
      history: [],
      invested: { ...makeSeatNumberMap(0), sb: 0.5, bb: 1 },
      committed: { ...makeSeatNumberMap(0), sb: 0.5, bb: 1 },
      stacks: { ...makeSeatNumberMap(100), sb: 99.5, bb: 99 },
      mainPot: 1.5,
      currentBet: 1,
      queue: (isPlayMode && !isPlaySixMax) ? ["sb", "bb"] : [...PREFLOP_ORDER],
      boardCards: [],
      queriedBoardCards: [],
      flopStr: null,
      desiredFlopStr: null,
      suitMappingBetweenFlops: null,
      flopRankMap: null,
      pendingChanceNodeId: null,
      drillStreet: isPlayMode ? "full" : drill.street,
      autopilotUntil: null,
      gtoOffTree: false,
      __playSettled: false,
    };
    runtimeRef.current = runtime;
    syncFromRuntime(runtime);
    advancePreflop();
    if (isPlayMode) setPlayHandCounter((prev) => prev + 1);
  }, [dealCardsWithFilter, simulatePreflopToPostflop, startPostflop, syncFromRuntime, advancePreflop, ensureTurnRiverAccess, hasPlayPremiumAccess, isPlayMode, isPlaySixMax, isEmbed, onExitPlayLobby, playConfig?.opponentName, playHandCounter, playThroughRiver]);

  React.useEffect(() => {
    startTrainingRef.current = startTraining;
  }, [startTraining]);

  const startTrainingManual = React.useCallback(() => {
    autopilotRedealsRef.current = 0;
    setSessionSummaryOpen(false);
    startTraining();
  }, [startTraining]);

  // Trainer deals hands automatically: start the first one shortly after mount.
  React.useEffect(() => {
    if (isPlayMode) return undefined;
    const t = setTimeout(() => startTrainingRef.current?.(), 500);
    return () => clearTimeout(t);
  }, [isPlayMode]);

  const startNewDrillSession = React.useCallback(() => {
    sessionHandsPlayedRef.current = 0;
    setSessionHandsPlayed(0);
    setSessionActionLog([]);
    setSessionSummaryOpen(false);
    autopilotRedealsRef.current = 0;
    startTraining();
  }, [startTraining]);

  const toggleHandFilter = React.useCallback((label) => {
    setDrillSettings((prev) => {
      const has = (prev.hands || []).includes(label);
      const nextHands = has ? prev.hands.filter((h) => h !== label) : [...(prev.hands || []), label];
      const next = { ...prev, hands: nextHands };
      drillSettingsRef.current = next;
      saveDrillSettings(next);
      return next;
    });
  }, []);

  React.useEffect(() => {
    if (!isPlayMode) return undefined;
    if (!playSessionStarted) return undefined;
    if (playAutoAdvanceNonce <= 0) return undefined;
    if (playLastHandledAdvanceNonceRef.current >= playAutoAdvanceNonce) return undefined;
    const nonce = playAutoAdvanceNonce;
    let cancelled = false;
    // Use ref so reshaped startTraining identity does not clear this before it fires.
    const t = setTimeout(() => {
      if (cancelled) return;
      playLastHandledAdvanceNonceRef.current = nonce;
      startTrainingRef.current?.();
    }, 1200);
    playAutoAdvanceTimerRef.current = t;
    return () => {
      cancelled = true;
      clearTimeout(t);
      if (playAutoAdvanceTimerRef.current === t) playAutoAdvanceTimerRef.current = null;
    };
  }, [isPlayMode, playSessionStarted, playAutoAdvanceNonce]);

  const exitPlayToLobby = React.useCallback(async () => {
    if (isPlayMode && onPlaySessionFinalize) {
      await onPlaySessionFinalize({
        opponentId: playConfig?.opponentId || "unknown",
        mode: String(playConfig?.mode || "casual"),
        score: Number(playSessionScore || 0),
        hands: Number(playSessionHands || 0),
      });
    }
    onExitPlayLobby?.();
  }, [isPlayMode, onExitPlayLobby, onPlaySessionFinalize, playConfig?.mode, playConfig?.opponentId, playSessionHands, playSessionScore]);

  const onHeroAction = (actionLabel) => {
    if (isRunning || actionOnSeat !== heroSeat || !heroTurn) return;
    if (Number(stacksBySeat?.[heroSeat] ?? 0) <= 1e-9) return;
    const runtime = runtimeRef.current;
    if (!runtime) return;
    if (isPlayMode) setPlayVillainSpeech("");
    setIsDealing(false);

    if (streetPhase !== "preflop") {
      if (isPlayMode && postflopDecision?.isPlayHardcoded) {
        const playAction = String(actionLabel || "");
        const playActionTemplate = postflopDecision?.playActionTemplateMap?.[playAction] || null;
        const applied = applyPostflopContribution(heroSeat, playAction, runtime, { fromDb: false });
        const contribution = Number(applied?.contribution || 0);
        const displayAction = toPostflopDisplayAction(playAction, contribution);
        runtime.history = [...runtime.history, { position: heroSeat, action: String(displayAction || playAction), raiseSize: extractNumber(displayAction), contribution }];
        if (/fold/i.test(String(playAction || ""))) runtime.folded.add(heroSeat);
        setDecisionDebugRows((prev) => [
          ...prev,
          {
            id: `${Date.now()}-hero-postflop-${prev.length}`,
            phase: "postflop",
            ...(heroDecisionContext || {}),
            source: "hero",
            nodeId: postflopDecision?.nodeId || null,
            actionIndex: -1,
            nextNodeId: null,
            decisionOutcome: "chosen",
            chosenAction: displayAction,
          },
        ]);
        setHeroTurn(false);
        setHeroBlockedActions([]);
        setWaitForHeroContinue(false);
        setPostflopDecision((prev) => ({
          ...(prev || {}),
          selectedUiAction: playAction,
          selectedActionTemplate: playActionTemplate,
          selectedDbAction: null,
          selectedDbActionIndex: -1,
          nextNodeIdAfterHero: null,
        }));
        syncFromRuntime(runtime);
        const stillActive = PREFLOP_ORDER.filter((s) => !runtime.folded.has(s));
        const firstActor = postflopDecision?.firstActor || null;
        const responseActor = String(firstActor || "") === String(runtime.heroSeat || "")
          ? stillActive.find((s) => s !== runtime.heroSeat) || null
          : firstActor || null;
        const showImmediateThinking = shouldShowImmediateOpponentThinking(
          postflopDecision?.firstActorAction || null,
          playAction
        );
        if (responseActor && showImmediateThinking) {
          setActionOnSeat(responseActor);
          setThinkingSeat(responseActor);
          setIsRunning(true);
          setFeedback(`${String(responseActor || "").toUpperCase()} is thinking...`);
        } else {
          setActionOnSeat(null);
          setThinkingSeat(null);
          setIsRunning(true);
        }
        const continuationContext = {
          spot: postflopDecision?.spot || postflopDebug?.spot || null,
          firstActor,
          nextNodeIdAfterHero: null,
          heroActionLabel: playAction,
          heroActionTemplate: playActionTemplate,
          heroDecisionNodeId: postflopDecision?.nodeId || null,
        };
        setTimeout(() => continuePostflopAfterHero(continuationContext), 250);
        return;
      }
      const normalized = String(actionLabel || "").trim().toLowerCase();
      const mappedDbAction = postflopDecision?.heroUiToDbActionMap?.[normalized] || String(actionLabel || "");
      const valid = new Set((heroValidActions || []).map((a) => String(a || "").trim().toLowerCase()));
      const heroPctMap = heroDecisionContext?.actionPctMap || {};
      const selectedPct = Number(heroPctMap[normalized] ?? 0);
      const postActions = postflopDecision?.dbActions || [];
      const selectedDbIdx = getDbActionIndexFromUiAction(mappedDbAction, postActions);
      const nextNodeIdAfterHero = getActionChildNodeId(
        { actionChildren: postflopDecision?.actionChildren || [] },
        selectedDbIdx
      );
      const pauseMode = isPlayMode ? "never" : String(drillSettingsRef.current?.pauseMode || "every");
      // "After hand" analysis: play through silently, review everything at the end.
      const analysisAfter = !isPlayMode && String(drillSettingsRef.current?.analysisMode || "during") === "after";
      // Off-tree: a previous non-GTO move left the solved game tree, so this
      // decision cannot be graded. Any move always plays on now.
      const wasOffTree = !isPlayMode && !!runtime.gtoOffTree;
      const isCorrectMove = valid.has(normalized);

      const applied = applyPostflopContribution(heroSeat, mappedDbAction, runtime, { fromDb: true });
      const contribution = Number(applied?.contribution || 0);
      const displayAction = toPostflopDisplayAction(mappedDbAction, contribution);
      runtime.history = [...runtime.history, { position: heroSeat, action: String(displayAction || actionLabel), raiseSize: extractNumber(displayAction), contribution }];
      if (/fold/i.test(String(mappedDbAction || ""))) runtime.folded.add(heroSeat);

      setDecisionDebugRows((prev) => [
        ...prev,
        {
          id: `${Date.now()}-hero-postflop-${prev.length}`,
          phase: "postflop",
          ...(heroDecisionContext || {}),
          source: "hero",
          nodeId: postflopDecision?.nodeId || null,
          actionIndex: selectedDbIdx,
          nextNodeId: nextNodeIdAfterHero || null,
          decisionOutcome: wasOffTree ? "na" : isCorrectMove ? "correct" : "blunder",
          chosenAction: displayAction,
        },
      ]);
      const shouldPause = !isPlayMode && !wasOffTree && !analysisAfter && (isCorrectMove ? pauseMode === "every" : pauseMode !== "never");
      setHeroTurn(false);
      setHeroBlockedActions([]);
      setWaitForHeroContinue(shouldPause);
      setShowFullRange(false);
      if (shouldPause) {
        setFeedbackNodeSnapshot({
          street: String(streetPhase || "flop"),
          actions: postflopDecision?.dbActions || heroDecisionContext?.rangeActions || [],
          grid: postflopDecision?.dbGrid || heroDecisionContext?.rangeGrid || null,
          heroLabel: heroDecisionContext?.handLabel || heroDecisionContext?.matchedCellLabel || "",
          heroCards: [...((runtime.dealt || {})[runtime.heroSeat] || [])],
          boardCards: [...(runtime.boardCards || [])],
          nodeId: postflopDecision?.nodeId || null,
          spot: postflopDecision?.spot || postflopDebug?.spot || null,
          preflopHistory: (runtime.preflopHistory || []).map((h) => ({
            position: h.position,
            action: h.action,
            raiseSize: h.raiseSize,
          })),
        });
      } else {
        setFeedbackNodeSnapshot(null);
      }
      setPostflopDecision((prev) => ({
        ...(prev || {}),
        selectedUiAction: actionLabel,
        selectedDbAction: mappedDbAction,
        selectedDbActionIndex: selectedDbIdx,
        nextNodeIdAfterHero: nextNodeIdAfterHero || null,
      }));
      if (!isPlayMode) {
        const outcome = wasOffTree ? "na" : isCorrectMove ? "correct" : "blunder";
        if (!isCorrectMove && !wasOffTree) runtime.gtoOffTree = true;
        recordHeroOutcome(streetPhase, outcome, displayAction);
        handReviewRef.current.push({
          street: String(streetPhase || "flop"),
          action: String(displayAction || actionLabel),
          outcome,
          pctMap: { ...heroPctMap },
          board: [...(runtime.boardCards || [])],
          heroCards: [...((runtime.dealt || {})[runtime.heroSeat] || [])],
        });
        if (analysisAfter) {
          setFeedback("");
        } else if (wasOffTree) {
          setFeedback("N/A, off the GTO line, no solver feedback for this spot.");
        } else if (isCorrectMove) {
          setFeedback(`Correct${selectedPct > 0 ? ` (${Math.round(selectedPct)}%)` : ""}.${shouldPause ? " Click Continue to proceed." : ""}`);
        } else {
          setFeedback(`Blunder. GTO play was ${describeBestActionFromPctMap(heroPctMap)}.${shouldPause ? " Click Continue to proceed." : ""}`);
        }
      }
      syncFromRuntime(runtime);
      if (!isPlayMode && !shouldPause) {
        const continuationContext = {
          spot: postflopDecision?.spot || postflopDebug?.spot || null,
          firstActor: postflopDecision?.firstActor || null,
          nextNodeIdAfterHero: nextNodeIdAfterHero || null,
        };
        setIsRunning(true);
        setTimeout(() => {
          setIsRunning(false);
          continuePostflopRef.current?.(continuationContext);
        }, analysisAfter ? 500 : isCorrectMove || wasOffTree ? 900 : 1600);
        return;
      }
      if (isPlayMode) {
        const stillActive = PREFLOP_ORDER.filter((s) => !runtime.folded.has(s));
        const firstActor = postflopDecision?.firstActor || null;
        const responseActor = String(firstActor || "") === String(runtime.heroSeat || "")
          ? stillActive.find((s) => s !== runtime.heroSeat) || null
          : firstActor || null;
        const showImmediateThinking = shouldShowImmediateOpponentThinking(
          postflopDecision?.firstActorAction || null,
          mappedDbAction
        );
        if (responseActor && showImmediateThinking) {
          setActionOnSeat(responseActor);
          setThinkingSeat(responseActor);
          setIsRunning(true);
          setFeedback(`${String(responseActor || "").toUpperCase()} is thinking...`);
        } else {
          setActionOnSeat(null);
          setThinkingSeat(null);
          setIsRunning(true);
        }
        const continuationContext = {
          spot: postflopDecision?.spot || postflopDebug?.spot || null,
          firstActor: firstActor,
          nextNodeIdAfterHero: nextNodeIdAfterHero || null,
        };
        setTimeout(() => continuePostflopAfterHero(continuationContext), 250);
      }
      return;
    }

    const normalized = normalizeActionToken(actionLabel);
    const valid = new Set((heroValidActions || []).map((a) => normalizeActionToken(a)));
    const heroPctMap = heroDecisionContext?.actionPctMap || {};
    const raiseCountBefore = countRaises(runtime.history);
    const seat = String(runtime.heroSeat || "").toLowerCase();
    let selectedPct = Number(heroPctMap[normalized] ?? 0);
    if (selectedPct <= 0 && normalized === "bet 10" && raiseCountBefore === 1 && (seat === "sb" || seat === "bb")) {
      selectedPct = Number(heroPctMap["raise 7.5"] ?? heroPctMap["raise 10"] ?? heroPctMap["3bet 10"] ?? heroPctMap["3bet 9"] ?? 0);
    }
    const preflopPauseMode = isPlayMode ? "never" : String(drillSettingsRef.current?.pauseMode || "every");
    const preflopAnalysisAfter = !isPlayMode && String(drillSettingsRef.current?.analysisMode || "during") === "after";
    const preflopWasOffTree = !isPlayMode && !!runtime.gtoOffTree;
    const preflopIsCorrect = valid.has(normalized);
    setDecisionDebugRows((prev) => [
      ...prev,
      {
        id: `${Date.now()}-hero-${prev.length}`,
        phase: "preflop",
        ...(heroDecisionContext || {}),
        source: "hero",
        decisionOutcome: preflopWasOffTree ? "na" : preflopIsCorrect ? "correct" : "blunder",
        validActions: heroValidActions || [],
        chosenAction: actionLabel,
      },
    ]);
    const actor = runtime.heroSeat;
    const parsed = classifyAction(actionLabel, raiseCountBefore);
    const { contribution, nextCurrentBet } = applyContribution(actor, parsed, runtime.invested, runtime.committed, runtime.stacks, runtime.currentBet);
    runtime.currentBet = nextCurrentBet;
    runtime.mainPot = Number((Number(runtime.mainPot || 0) + Number(contribution || 0)).toFixed(2));
    runtime.history = [...runtime.history, { position: actor, action: parsed.action, raiseSize: parsed.raiseSize, contribution }];
    if (parsed.action === "fold") runtime.folded.add(actor);
    if (parsed.aggressive) runtime.queue = nextActingQueueFromRaiser(actor, runtime.folded);
    else runtime.queue = runtime.queue.slice(1).filter((s) => !runtime.folded.has(s));
    const preflopShouldPause = !isPlayMode && !preflopWasOffTree && !preflopAnalysisAfter && (preflopIsCorrect ? preflopPauseMode === "every" : preflopPauseMode !== "never");
    setHeroTurn(false);
    setHeroBlockedActions([]);
    setWaitForHeroContinue(preflopShouldPause);
    setShowFullRange(false);
    if (preflopShouldPause) {
      const historyBeforeHero = (runtime.history || []).slice(0, -1);
      setFeedbackNodeSnapshot({
        street: "preflop",
        actions: heroDecisionContext?.rangeActions || heroDecisionContext?.actions || [],
        grid: heroDecisionContext?.rangeGrid || null,
        heroLabel: heroDecisionContext?.handLabel || heroDecisionContext?.matchedCellLabel || "",
        heroCards: [...((runtime.dealt || {})[runtime.heroSeat] || [])],
        boardCards: [],
        nodeId: null,
        spot: null,
        preflopHistory: historyBeforeHero.map((h) => ({
          position: h.position,
          action: h.action,
          raiseSize: h.raiseSize,
        })),
      });
    } else {
      setFeedbackNodeSnapshot(null);
    }
    setIsRunning(false);
    if (!isPlayMode) {
      const outcome = preflopWasOffTree ? "na" : preflopIsCorrect ? "correct" : "blunder";
      if (!preflopIsCorrect && !preflopWasOffTree) runtime.gtoOffTree = true;
      recordHeroOutcome("preflop", outcome, actionLabel);
      handReviewRef.current.push({
        street: "preflop",
        action: String(actionLabel || ""),
        outcome,
        pctMap: { ...heroPctMap },
        board: [],
        heroCards: [...((runtime.dealt || {})[runtime.heroSeat] || [])],
      });
      if (preflopAnalysisAfter) {
        setFeedback("");
      } else if (preflopWasOffTree) {
        setFeedback("N/A, off the GTO line, no solver feedback for this spot.");
      } else if (preflopIsCorrect) {
        setFeedback(`Correct (${Math.round(selectedPct)}%).${preflopShouldPause ? " Click Continue to proceed." : ""}`);
      } else {
        setFeedback(`Blunder. GTO play was ${describeBestActionFromPctMap(heroPctMap)}.${preflopShouldPause ? " Click Continue to proceed." : ""}`);
      }
    }
    syncFromRuntime(runtime);
    if (isPlayMode) {
      const nextActor = runtime.queue?.[0] || null;
      if (nextActor) {
        setActionOnSeat(nextActor);
        setThinkingSeat(nextActor);
        setIsRunning(true);
        setFeedback(`${String(nextActor || "").toUpperCase()} is thinking...`);
      }
      setTimeout(() => advancePreflop(), 250);
    } else if (!preflopShouldPause) {
      setIsRunning(true);
      setTimeout(() => {
        setIsRunning(false);
        advancePreflop();
      }, preflopAnalysisAfter ? 500 : preflopIsCorrect || preflopWasOffTree ? 900 : 1600);
    }
  };

  onHeroActionRef.current = onHeroAction;

  // ── Seat clocks (trainer only): hero timebank + visible opponent think timer.
  React.useEffect(() => {
    if (isPlayMode) return;
    const tb = Number(drillSettings.timebank || 0);
    if (heroTurn && actionOnSeat === heroSeat && !waitForHeroContinue && tb > 0) {
      heroClockFiredRef.current = false;
      setSeatClock({ seat: heroSeat, startAt: Date.now(), totalMs: tb * 1000, isHero: true });
      return;
    }
    if (thinkingSeat) {
      // Opponents display the same 10s clock as the hero; they act partway through it.
      setSeatClock({ seat: thinkingSeat, startAt: Date.now(), totalMs: (tb > 0 ? tb : 10) * 1000, isHero: false });
      return;
    }
    setSeatClock(null);
  }, [isPlayMode, heroTurn, actionOnSeat, heroSeat, waitForHeroContinue, thinkingSeat, drillSettings.timebank]);

  React.useEffect(() => {
    if (!seatClock) return undefined;
    const id = setInterval(() => {
      setClockNow(Date.now());
      if (seatClock.isHero && !heroClockFiredRef.current && Date.now() - seatClock.startAt >= seatClock.totalMs) {
        heroClockFiredRef.current = true;
        // Time's up: auto check when possible, otherwise fold (graded like any action).
        const opts = heroOptions || [];
        const autoPick = opts.find((o) => /check/i.test(String(o || ""))) || opts.find((o) => /fold/i.test(String(o || "")));
        if (autoPick) onHeroActionRef.current?.(autoPick);
      }
    }, 100);
    return () => clearInterval(id);
  }, [seatClock, heroOptions]);

  const continuePostflopAfterHero = React.useCallback(async (overrideContext = null) => {
    const runtime = runtimeRef.current;
    if (!runtime) return;

    const moveToNextStreetOrEnd = async () => {
      // runtime.currentStreet is set synchronously by beginPostflopStreet, so it
      // stays accurate during autopilot chains where streetPhase state lags.
      const effStreet = runtime.currentStreet || streetPhase;
      const stillActive = PREFLOP_ORDER.filter((s) => !runtime.folded.has(s));
      const heroOut = !isPlayMode && runtime.folded.has(runtime.heroSeat);
      if (stillActive.length < 2 || heroOut) {
        setActionOnSeat(null);
        setThinkingSeat(null);
        setIsDealing(false);
        setIsRunning(false);
        if (!isPlayMode && runtime.autopilotUntil) {
          scheduleAutopilotRedeal("hand ended before target street");
          return;
        }
        const settled = settlePlayHandScore(runtime);
        setFeedback(isPlayMode && settled
          ? `Hand ended. ${settled.handDelta >= 0 ? "+" : ""}${settled.handDelta.toFixed(1)} chips.`
          : heroOut ? "You folded. Hand over." : "Hand ended.");
        if (!isPlayMode) finishTrainerHand();
        return;
      }
      // Flop/turn drills start on that street (via autopilotUntil) but continue
      // through the remaining streets to river. (Preflop-only still ends early above.)
      if (effStreet === "flop") {
        // Starter stops after flop; Improver/Pro continue to turn/river.
        if (!isPlayMode && !hasTrainerTurnRiverAccess()) {
          setActionOnSeat(null);
          setThinkingSeat(null);
          setIsDealing(false);
          setHeroTurn(false);
          setIsRunning(false);
          setFeedback("Flop complete. Turn & River drills require Improver or Pro.");
          finishTrainerHand();
          return;
        }
        setActionOnSeat(null);
        setThinkingSeat(null);
        setIsRunning(true);
        setIsDealing(true);
        setFeedback("Dealing...");
        try {
          await beginPostflopStreet(runtime, "turn");
        } finally {
          setIsDealing(false);
        }
        return;
      }
      if (effStreet === "turn") {
        if (!isPlayMode && !hasTrainerTurnRiverAccess()) {
          setActionOnSeat(null);
          setThinkingSeat(null);
          setIsDealing(false);
          setHeroTurn(false);
          setIsRunning(false);
          setFeedback("Turn complete. River drills require Improver or Pro.");
          finishTrainerHand();
          return;
        }
        setActionOnSeat(null);
        setThinkingSeat(null);
        setIsRunning(true);
        setIsDealing(true);
        setFeedback("Dealing...");
        try {
          await beginPostflopStreet(runtime, "river");
        } finally {
          setIsDealing(false);
        }
        return;
      }
      setActionOnSeat(null);
      setThinkingSeat(null);
      setIsDealing(false);
      setHeroTurn(false);
      setIsRunning(false);
      runPlayShowdown(runtime, "Hand complete.");
    };

    const active = PREFLOP_ORDER.filter((s) => !runtime.folded.has(s));
    if (active.length < 2) {
      await moveToNextStreetOrEnd();
      return;
    }

    const lastHeroAction = [...(runtime.history || [])].reverse().find((h) => String(h?.position || "") === String(runtime.heroSeat || ""));
    const heroActionText = String(lastHeroAction?.action || "").toLowerCase();
    if (heroActionText.includes("fold")) {
      runtime.folded.add(runtime.heroSeat);
      await moveToNextStreetOrEnd();
      return;
    }

    const spot = overrideContext?.spot || postflopDecision?.spot || postflopDebug?.spot;
    // Use ?? so an explicit null from Play hardcoded actions forces remap (|| would keep stale ids).
    let nextNodeIdAfterHero =
      overrideContext?.nextNodeIdAfterHero ?? postflopDecision?.nextNodeIdAfterHero;
    if (isPlayMode && !nextNodeIdAfterHero && (overrideContext?.heroActionLabel || postflopDecision?.selectedUiAction)) {
      const heroUiAction = String(overrideContext?.heroActionLabel || postflopDecision?.selectedUiAction || "").trim();
      const heroActionTemplate = String(overrideContext?.heroActionTemplate || postflopDecision?.selectedActionTemplate || postflopDecision?.playActionTemplateMap?.[heroUiAction] || "").trim();
      let heroDecisionNodeId = String(overrideContext?.heroDecisionNodeId || postflopDecision?.nodeId || "");
      try {
        if (!heroDecisionNodeId) {
          if (streetPhase === "flop" || !runtime.pendingChanceNodeId) {
            heroDecisionNodeId = "root";
          } else {
            const chanceSourceNodeId = String(runtime.pendingChanceNodeId || "");
            const chanceCard = String((runtime.queriedBoardCards || runtime.boardCards || []).slice(-1)?.[0] || "");
            const chanceNode = await fetchV6Grid({ spot, flop: runtime.flopStr, nodeId: chanceSourceNodeId });
            const chanceChosen = getChanceChildNodeId(chanceNode?.v6 || null, chanceCard);
            if (!chanceChosen) throw new Error("Could not resolve chance node for hero decision.");
            heroDecisionNodeId = String(chanceChosen);
          }
        }
        const heroNode = await fetchV6Grid({ spot, flop: runtime.flopStr, nodeId: heroDecisionNodeId });
        const heroActions = Array.isArray(heroNode?.actions) ? heroNode.actions : [];
        const mappedDbAction = mapPlayHeroActionToDbAction(heroUiAction, heroActions, heroActionTemplate);
        const idx = getDbActionIndexFromUiAction(mappedDbAction, heroActions);
        nextNodeIdAfterHero = getActionChildNodeId(heroNode?.v6 || null, idx);
        setPostflopDecision((prev) => ({
          ...(prev || {}),
          nodeId: heroDecisionNodeId,
          selectedDbAction: mappedDbAction || null,
          selectedDbActionIndex: idx,
          nextNodeIdAfterHero: nextNodeIdAfterHero || null,
        }));
      } catch (err) {
        setPostflopDebug((prev) => ({
          ...(prev || {}),
          spot,
          nodeId: heroDecisionNodeId || null,
          selectedBoard: runtime?.boardCards || [],
          queriedBoard: runtime?.queriedBoardCards || [],
          heroAction: heroUiAction,
          error: err?.message || String(err),
        }));
      }
    }
    if (!spot || !nextNodeIdAfterHero) {
      await moveToNextStreetOrEnd();
      return;
    }

    let responseNode = null;
    try {
      responseNode = await fetchV6Grid({ spot, flop: runtime.flopStr, nodeId: String(nextNodeIdAfterHero) });
    } catch {
      await moveToNextStreetOrEnd();
      return;
    }

    if (responseNode?.v6?.kind && responseNode.v6.kind !== "decision") {
      if (responseNode.v6.kind === "chance") {
        runtime.pendingChanceNodeId = String(responseNode?.nodeId || nextNodeIdAfterHero);
      }
      await moveToNextStreetOrEnd();
      return;
    }

    const firstActor = overrideContext?.firstActor || postflopDecision?.firstActor;
    const responseActor = String(firstActor || "") === String(runtime.heroSeat || "")
      ? active.find((s) => s !== runtime.heroSeat) || null
      : firstActor || null;
    if (!responseActor) {
      await moveToNextStreetOrEnd();
      return;
    }

    const responseActions = Array.isArray(responseNode?.actions) ? responseNode.actions : [];
    const responseGrid = Array.isArray(responseNode?.grid) ? responseNode.grid : [];
    const responseV6 = responseNode?.v6 || null;
    const isAutopilotChain = !isPlayMode && !!runtime.autopilotUntil;
    if (!isAutopilotChain) {
      setActionOnSeat(responseActor);
      setThinkingSeat(responseActor);
      setIsRunning(true);
      setFeedback(`${responseActor.toUpperCase()} is thinking...`);
    }
    const responseDelayMs = isPlayMode ? POST_FLOP_THINK_DELAY_MS : (isAutopilotChain ? 0 : realisticThinkDelayMs());

    timerRef.current = setTimeout(async () => {
      const fresh = runtimeRef.current;
      if (!fresh) {
        setThinkingSeat(null);
        setActionOnSeat(null);
        setIsRunning(false);
        return;
      }
      let responseDecision;
      try {
        responseDecision = (isPlayMode && responseActor !== fresh.heroSeat)
          ? await resolvePlayVillainPostflopDecision(responseActor, fresh, responseActions, responseGrid, { spot, nodeId: String(nextNodeIdAfterHero) })
          : resolvePostflopSeatDecision(responseActor, fresh, responseActions, responseGrid);
      } catch (err) {
        setCenterMessage("something went wrong - please refresh");
        setFeedback(err?.message || "Villain mapping failed.");
        setPostflopDebug({
          street: streetPhase,
          spot,
          nodeId: String(nextNodeIdAfterHero),
          selectedBoard: fresh?.boardCards || [],
          queriedBoard: fresh?.queriedBoardCards || [],
          actingSeat: responseActor,
          mappingDebug: err?.__solverDebug || null,
          error: err?.message || String(err),
        });
        setThinkingSeat(null);
        setActionOnSeat(null);
        setIsRunning(false);
        return;
      }
      const profileMix = applyProfileToMixedStrategy(responseDecision.actions, responseDecision.frequencies, playVillainProfileKey);
      const stillAutopilot = !isPlayMode && !!fresh.autopilotUntil;
      const weighted = profileMix.forcedIndex != null
        ? { action: responseDecision.actions?.[profileMix.forcedIndex] || "Check", rngRoll: null, rngTotal: null }
        : (stillAutopilot
          ? pickByWeightsNoFold(responseDecision.actions, profileMix.frequencies)
          : pickByWeights(responseDecision.actions, profileMix.frequencies));
      const chosen = weighted?.action || "Check";
      const chosenIdx = responseDecision.actions.findIndex((a) => String(a || "").trim().toLowerCase() === String(chosen || "").trim().toLowerCase());
      const villainNextNodeId = getActionChildNodeId(responseV6, chosenIdx);
      const applied = applyPostflopContribution(responseActor, chosen, fresh, { fromDb: true });
      const contribution = Number(applied?.contribution || 0);
      const speech = maybeVillainBetSpeech(responseActor, chosen, contribution);
      if (speech) setPlayVillainSpeech(speech);
      const displayAction = toPostflopDisplayAction(chosen, contribution);
      fresh.history = [...fresh.history, { position: responseActor, action: String(displayAction || chosen), raiseSize: extractNumber(displayAction), contribution }];
      if (/fold/i.test(String(chosen || ""))) fresh.folded.add(responseActor);
      syncFromRuntime(fresh);
      setDecisionDebugRows((prev) => [
        ...prev,
        {
          id: `${Date.now()}-postflop-next-${responseActor}-${prev.length}`,
          phase: "postflop",
          source: "villain",
          nodeId: nextNodeIdAfterHero,
          actionIndex: chosenIdx,
          nextNodeId: villainNextNodeId || null,
          seat: responseActor,
          handCards: responseDecision.handCards,
          handLabel: responseDecision.handLabel,
          matchedCellLabel: String(responseDecision.cell?.label || "--"),
          exactComboMatch: !!responseDecision.freqInfo.exactMatch,
          usedFallbackCellFrequencies: !!responseDecision.freqInfo.usedFallbackCellFrequencies,
          actions: responseDecision.actions,
          frequencies: profileMix.frequencies,
          validActions: responseDecision.validActions,
          mappingDebug: responseDecision.mappingDebug || null,
          rngRoll: weighted?.rngRoll ?? null,
          rngTotal: weighted?.rngTotal ?? null,
          chosenAction: displayAction,
          decisionOutcome: "auto",
          note: `postflop response (${streetPhase})`,
          profileOverride: playVillainProfileKey || null,
          profileForced: profileMix.forcedIndex != null,
        },
      ]);
      setThinkingSeat(null);
      setActionOnSeat(null);
      // Keep isRunning true through the next-node fetch so the hero bar stays locked
      // until we either bind a decision or advance/settle (avoids a "frozen" gap).
      setIsRunning(true);

      const activeAfterResponse = PREFLOP_ORDER.filter((s) => !fresh.folded.has(s));
      const responseFolded = /fold/i.test(String(chosen || ""));
      if (activeAfterResponse.length < 2 || responseFolded || isZeroNodeId(villainNextNodeId)) {
        fresh.pendingChanceNodeId = null;
        setIsRunning(false);
        await moveToNextStreetOrEnd();
        return;
      }

      if (!villainNextNodeId) {
        fresh.pendingChanceNodeId = null;
        setIsRunning(false);
        await moveToNextStreetOrEnd();
        return;
      }

      try {
        const heroNode = await fetchV6Grid({ spot, flop: fresh.flopStr, nodeId: String(villainNextNodeId) });
        if (heroNode?.v6?.kind !== "decision" || fresh.folded.has(fresh.heroSeat)) {
          if (heroNode?.v6?.kind === "chance") {
            fresh.pendingChanceNodeId = String(heroNode?.nodeId || villainNextNodeId);
          }
          setIsRunning(false);
          await moveToNextStreetOrEnd();
          return;
        }
        if (isPlayMode) {
          bindPlayHeroTurnWithoutLookup(fresh, streetPhase, {
            spot,
            nodeId: String(heroNode?.nodeId || villainNextNodeId || ""),
            firstActor: responseActor,
            firstActorAction: displayAction,
          });
          return;
        }
        const heroActions = Array.isArray(heroNode?.actions) ? heroNode.actions : [];
        const heroGrid = Array.isArray(heroNode?.grid) ? heroNode.grid : [];
        const heroV6 = heroNode?.v6 || null;
        const heroNodeId = String(heroNode?.nodeId || villainNextNodeId);
        const heroSeatDecision = resolvePostflopSeatDecision(fresh.heroSeat, fresh, heroActions, heroGrid);
        // Turn/river drill autopilot: keep playing the hero silently until the target street.
        if (!isPlayMode && fresh.autopilotUntil) {
          const autoWeighted = pickByWeightsNoFold(heroSeatDecision.actions, heroSeatDecision.frequencies);
          const autoChosen = autoWeighted?.action || "Check";
          const autoIdx = heroSeatDecision.actions.findIndex((a) => String(a || "").trim().toLowerCase() === String(autoChosen || "").trim().toLowerCase());
          const autoNextNodeId = getActionChildNodeId(heroV6, autoIdx);
          const autoApplied = applyPostflopContribution(fresh.heroSeat, autoChosen, fresh, { fromDb: true });
          const autoContribution = Number(autoApplied?.contribution || 0);
          const autoDisplay = toPostflopDisplayAction(autoChosen, autoContribution);
          fresh.history = [...fresh.history, { position: fresh.heroSeat, action: String(autoDisplay || autoChosen), raiseSize: extractNumber(autoDisplay), contribution: autoContribution }];
          if (/fold/i.test(String(autoChosen || ""))) fresh.folded.add(fresh.heroSeat);
          syncFromRuntime(fresh);
          setFeedback("Dealing...");
          continuePostflopRef.current?.({ spot, firstActor: responseActor, nextNodeIdAfterHero: autoNextNodeId || null });
          return;
        }
        const heroCfg = buildPostflopHeroTurnConfig(heroSeatDecision.actions, heroSeatDecision.frequencies);
        const heroUiMap = buildHeroUiActionMapFromDb(heroCfg.options);
        const validUi = heroCfg.validActions.map((a) => toPostflopHeroUiActionLabel(a));
        const uiPctMap = {};
        for (const action of heroCfg.options) {
          const key = toPostflopHeroUiActionLabel(action).trim().toLowerCase();
          uiPctMap[key] = Number(heroCfg.actionPctMap?.[String(action || "").trim().toLowerCase()] ?? 0);
        }
        const nextPostflopDecision = {
          street: streetPhase,
          spot,
          flop: fresh.flopStr,
          board: fresh.boardCards,
          firstActor: responseActor,
          firstActorAction: displayAction,
          nodeId: heroNodeId,
          dbActions: heroActions,
          dbGrid: heroGrid,
          v6Kind: heroV6?.kind || null,
          actionChildren: Array.isArray(heroV6?.actionChildren) ? [...heroV6.actionChildren] : [],
          nextNodeIdAfterHero: null,
          heroUiToDbActionMap: heroUiMap.uiToDb,
        };
        setActionOnSeat(fresh.heroSeat);
        setHeroTurn(true);
        setIsRunning(false);
        setHeroBlockedActions([]);
        setHeroOptions(heroUiMap.optionsUi);
        setHeroValidActions(validUi);
        setHeroDecisionContext({
          seat: fresh.heroSeat,
          handCards: heroSeatDecision.handCards,
          handLabel: heroSeatDecision.handLabel,
          matchedCellLabel: String(heroSeatDecision.cell?.label || "--"),
          exactComboMatch: !!heroSeatDecision.freqInfo.exactMatch,
          matchedComboHand: heroSeatDecision.freqInfo.matchedHand,
          usedFallbackCellFrequencies: !!heroSeatDecision.freqInfo.usedFallbackCellFrequencies,
          actions: heroUiMap.optionsUi,
          frequencies: heroSeatDecision.frequencies,
          validActions: validUi,
          actionPctMap: uiPctMap,
          rangeGrid: heroGrid || null,
          rangeActions: heroSeatDecision.actions || [],
          rngRoll: null,
          rngTotal: null,
        });
        setPostflopDebug({
          street: streetPhase,
          flop: fresh.flopStr,
          board: fresh.boardCards,
          derivedSpot: postflopDebug?.derivedSpot,
          spot,
          nodeId: heroNodeId,
          actingSeat: fresh.heroSeat,
          handCards: heroSeatDecision.handCards,
          handLabel: heroSeatDecision.handLabel,
          comboKey: heroSeatDecision.comboKey,
          dbKind: heroV6?.kind,
          dbStreet: heroNode?.street,
          actionChildren: Array.isArray(heroV6?.actionChildren) ? heroV6.actionChildren : [],
          dbActions: heroSeatDecision.actions,
          dbFrequencies: heroSeatDecision.frequencies,
          exactComboMatch: !!heroSeatDecision.freqInfo.exactMatch,
          matchedComboHand: heroSeatDecision.freqInfo.matchedHand,
          usedFallbackCellFrequencies: !!heroSeatDecision.freqInfo.usedFallbackCellFrequencies,
          validActions: heroSeatDecision.validActions,
        });
        setPostflopDecision(nextPostflopDecision);
        setFeedback(`Villain acted (${displayAction}). Your turn.`);
      } catch {
        setIsRunning(false);
        await moveToNextStreetOrEnd();
      }
    }, responseDelayMs);
  }, [applyPostflopContribution, beginPostflopStreet, bindPlayHeroTurnWithoutLookup, finishTrainerHand, hasPlayPremiumAccess, isPlayMode, maybeVillainBetSpeech, playVillainProfileKey, postflopDecision, postflopDebug, resolvePlayVillainPostflopDecision, resolvePostflopSeatDecision, runPlayShowdown, scheduleAutopilotRedeal, settlePlayHandScore, streetPhase, syncFromRuntime]);

  React.useEffect(() => {
    continuePostflopRef.current = continuePostflopAfterHero;
  }, [continuePostflopAfterHero]);

  const onContinueAfterHero = () => {
    if (!waitForHeroContinue) return;
    setWaitForHeroContinue(false);
    setShowFullRange(false);
    setFeedbackNodeSnapshot(null);
    setFeedback("Continuing hand...");
    if (streetPhase === "preflop") {
      advancePreflop();
      return;
    }
    continuePostflopAfterHero();
  };

  const onSolveThisSpot = React.useCallback(() => {
    const snap = feedbackNodeSnapshot;
    if (!snap) return;
    const handoff = {
      version: 1,
      street: snap.street || "preflop",
      preflopHistory: Array.isArray(snap.preflopHistory) ? snap.preflopHistory : [],
      boardCards: Array.isArray(snap.boardCards) ? snap.boardCards : [],
      nodeId: snap.nodeId || null,
      spot: snap.spot || null,
      userHand: Array.isArray(snap.heroCards) ? snap.heroCards.slice(0, 2) : [null, null],
    };
    try {
      sessionStorage.setItem(TRAINER_SOLVE_HANDOFF_KEY, JSON.stringify(handoff));
    } catch (_) {}
    navigate("/cash");
  }, [feedbackNodeSnapshot, navigate]);

  const clockElapsedMs = seatClock ? Date.now() - seatClock.startAt : 0;
  const clockFraction = seatClock ? Math.max(0, 1 - clockElapsedMs / seatClock.totalMs) : 0;
  const clockSecondsLeft = seatClock ? Math.max(0, Math.ceil((seatClock.totalMs - clockElapsedMs) / 1000)) : 0;

  const navTabSx = (active = false) => ({
    minWidth: "unset",
    px: { xs: 1.6, sm: 2.1 },
    py: { xs: 0.45, sm: 0.6 },
    borderRadius: "999px",
    border: "none",
    backgroundColor: active ? "rgba(185,158,79,0.14)" : "rgba(255,255,255,0.02)",
    color: active ? COLORS.white : COLORS.textMuted,
    fontSize: { xs: "11px", sm: "12px" },
    fontWeight: active ? 700 : 600,
    textTransform: "none",
    letterSpacing: "0.01em",
    lineHeight: 1.2,
    transition: "all 160ms ease",
    "&:hover": {
      color: COLORS.white,
      backgroundColor: active ? "rgba(185,158,79,0.22)" : "rgba(255,255,255,0.07)",
    },
  });

  const pageBg = (BACKGROUND_OPTIONS[appearance.background] || BACKGROUND_OPTIONS.graphite).color;
  const feltTheme = FELT_OPTIONS[appearance.felt] || FELT_OPTIONS.emerald;
  const railColor = (RAIL_OPTIONS[appearance.rail] || RAIL_OPTIONS.walnut).color;
  const tableRadius = (TABLE_STYLE_OPTIONS[appearance.tableStyle] || TABLE_STYLE_OPTIONS.racetrack).radius;
  const glowColor = (GLOW_OPTIONS[appearance.glow] || GLOW_OPTIONS.gold).color;
  const chipTheme = CHIP_COLOR_OPTIONS[appearance.chipColor] || CHIP_COLOR_OPTIONS.gold;

  return (
    <AppearanceContext.Provider value={appearance}>
    <Box sx={{ minHeight: isEmbed ? "auto" : "100vh", backgroundColor: pageBg, color: COLORS.text, width: "100%" }}>
      {!isEmbed && (
      <Box sx={{ py: 1.5, px: 2, pl: { xs: 1.2, sm: 3 }, pr: { xs: 14, sm: 2 }, borderBottom: `1px solid ${COLORS.border}`, backgroundColor: pageBg, display: "flex", alignItems: "center", justifyContent: "space-between", minWidth: 0, gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.6, sm: 1.2 }, minWidth: 0, flex: 1, overflowX: "auto" }}>
          <img src={logoWithText} alt="PokerMoose" onClick={() => navigate("/")} style={{ height: 32, width: 32, flexShrink: 0, opacity: 0.5, filter: "grayscale(1)", cursor: "pointer" }} />
          <Typography
            sx={{
              display: { xs: "none", sm: "inline" },
              color: COLORS.white,
              fontSize: "13px",
              fontWeight: 800,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              fontFamily: "Play, Arial, sans-serif",
              lineHeight: 1,
              mr: 0.4,
              userSelect: "none",
              flexShrink: 0,
            }}
          >
            PokerMoose
          </Typography>
          <Button
            variant="text"
            size="small"
            onClick={() => navigate("/")}
            sx={{ ...navTabSx(false), flexShrink: 0 }}
          >
            Home
          </Button>
          <Button
            variant="text"
            size="small"
            onClick={() => navigate("/cash")}
            sx={{ ...navTabSx(false), flexShrink: 0, display: { xs: "none", sm: "inline-flex" } }}
          >
            Solve
          </Button>
          <Button
            variant="text"
            size="small"
            onClick={() => navigate("/trainer")}
            sx={{ ...navTabSx(!isPlayMode), flexShrink: 0, display: { xs: "none", sm: "inline-flex" } }}
          >
            Train
          </Button>
          <Button
            variant="text"
            size="small"
            onClick={() => {
              if (isPlayMode) onExitPlayLobby?.();
              else navigate("/play");
            }}
            sx={{ ...navTabSx(isPlayMode), flexShrink: 0, display: { xs: "none", sm: "inline-flex" } }}
          >
            Play
          </Button>
          <Button
            variant="text"
            size="small"
            onClick={() => navigate("/analysis")}
            sx={{ ...navTabSx(false), flexShrink: 0, display: { xs: "none", sm: "inline-flex" } }}
          >
            Analyze
          </Button>
        </Box>
        <Box />
      </Box>
      )}

      {isEmbed && (
        <Box sx={{ px: 2, pt: 1.2, pb: 0.4, textAlign: "center" }}>
          <Typography sx={{ fontSize: 13, color: COLORS.textMuted, fontWeight: 700 }}>
            Scenario {Math.min(sessionHandsPlayed + (isRunning || heroTurn ? 1 : 0), Number(drillSettings.sessionLength || 3) || 3)} of {Number(drillSettings.sessionLength || 3) || 3}
            {sessionGradedCount > 0
              ? ` · ${Math.round((sessionCorrectCount / sessionGradedCount) * 100)}% so far`
              : " · Flop to river"}
          </Typography>
        </Box>
      )}

      <Box sx={{ p: { xs: 1.25, sm: 2 }, display: "grid", gridTemplateColumns: { xs: "1fr", md: isPlayMode || isEmbed ? "1fr" : "300px 1fr" }, gap: { xs: 1.2, sm: 2 }, justifyItems: isPlayMode || isEmbed ? "center" : "stretch" }}>
        {!isPlayMode && !isEmbed && (
        <Box sx={{ backgroundColor: COLORS.cardBg, border: `1px solid ${COLORS.border}`, borderRadius: "12px", p: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
            <Typography className="pm-title" sx={{ fontSize: 14, fontWeight: 700 }}>Drill setup</Typography>
            <Button
              size="small"
              onClick={startTrainingManual}
              sx={{
                minWidth: "unset",
                px: 0.9,
                py: 0.2,
                borderRadius: "6px",
                border: `1px solid ${COLORS.border}`,
                color: COLORS.textMuted,
                fontSize: "10px",
                fontWeight: 700,
                textTransform: "none",
                "&:hover": { color: COLORS.green, borderColor: COLORS.green },
              }}
            >
              Deal new hand
            </Button>
          </Box>

          <DrillSection title="Street">
            {["full", "preflop", "flop", "turn", "river"].map((s) => {
              const streetLocked = turnRiverStreetLocked && (s === "turn" || s === "river");
              return (
                <DrillChip
                  key={`street-${s}`}
                  label={DRILL_STREET_LABELS[s]}
                  active={drillSettings.street === s}
                  locked={streetLocked}
                  onClick={() => {
                    if (streetLocked) {
                      ensureTurnRiverAccess(s);
                      return;
                    }
                    updateDrillSettings({ street: s });
                  }}
                />
              );
            })}
          </DrillSection>

          <DrillSection title="Pot type" hint={isPostflopDrillSelected ? "" : "postflop drills only"}>
            {[["any", "Any"], ["srp", "Single raised"], ["3bet", "3-bet pot"], ["4bet", "4-bet pot"]].map(([id, label]) => (
              <DrillChip
                key={`pot-${id}`}
                label={label}
                active={drillSettings.potType === id}
                disabled={!isPostflopDrillSelected}
                onClick={() => updateDrillSettings({ potType: id })}
              />
            ))}
          </DrillSection>

          <DrillSection title="Your position">
            {[["any", "Any"], ["lj", "LJ"], ["hj", "HJ"], ["co", "CO"], ["btn", "BTN"], ["sb", "SB"], ["bb", "BB"]].map(([id, label]) => (
              <DrillChip
                key={`pos-${id}`}
                label={label}
                active={drillSettings.heroPosition === id}
                onClick={() => updateDrillSettings({ heroPosition: id })}
              />
            ))}
          </DrillSection>

          <DrillSection title="Relative position" hint={isPostflopDrillSelected ? "" : "postflop drills only"}>
            {[["any", "Any"], ["ip", "In position"], ["oop", "Out of position"]].map(([id, label]) => (
              <DrillChip
                key={`rel-${id}`}
                label={label}
                active={drillSettings.relPosition === id}
                disabled={!isPostflopDrillSelected}
                onClick={() => updateDrillSettings({ relPosition: id })}
              />
            ))}
          </DrillSection>

          <Box sx={{ mb: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: COLORS.text }}>Your hands</Typography>
              <Box sx={{ display: "flex", gap: 0.4 }}>
                {!!(drillSettings.hands || []).length && (
                  <Button
                    size="small"
                    onClick={() => updateDrillSettings({ hands: [] })}
                    sx={{ minWidth: "unset", px: 0.7, py: 0.1, fontSize: "9.5px", textTransform: "none", color: COLORS.danger }}
                  >
                    Clear
                  </Button>
                )}
                <Button
                  size="small"
                  onClick={() => setHandPickerOpen((v) => !v)}
                  sx={{ minWidth: "unset", px: 0.7, py: 0.1, fontSize: "9.5px", textTransform: "none", color: COLORS.white }}
                >
                  {handPickerOpen ? "Hide grid" : "Pick hands"}
                </Button>
              </Box>
            </Box>
            <Typography sx={{ fontSize: 10, color: COLORS.textMuted }}>
              {(drillSettings.hands || []).length
                ? `${drillSettings.hands.length} selected: ${drillSettings.hands.slice(0, 8).join(", ")}${drillSettings.hands.length > 8 ? "…" : ""}`
                : "All hands"}
            </Typography>
            {handPickerOpen && <HandFilterGrid selected={drillSettings.hands || []} onToggle={toggleHandFilter} />}
          </Box>

          <DrillSection title="Timebank">
            {[[0, "Off"], [7, "7s"], [10, "10s"], [15, "15s"]].map(([sec, label]) => (
              <DrillChip
                key={`tb-${sec}`}
                label={label}
                active={Number(drillSettings.timebank) === sec}
                onClick={() => updateDrillSettings({ timebank: sec })}
              />
            ))}
          </DrillSection>

          <DrillSection title="Show analysis">
            {[["during", "During hand"], ["after", "After hand"]].map(([id, label]) => (
              <DrillChip
                key={`analysis-${id}`}
                label={label}
                active={(drillSettings.analysisMode || "during") === id}
                onClick={() => updateDrillSettings({ analysisMode: id })}
              />
            ))}
          </DrillSection>

          <DrillSection title="Pause game after" hint={(drillSettings.analysisMode || "during") === "after" ? "during-hand analysis only" : ""}>
            {[["every", "Every decision"], ["mistake", "Mistakes only"], ["never", "Never"]].map(([id, label]) => (
              <DrillChip
                key={`pause-${id}`}
                label={label}
                active={drillSettings.pauseMode === id}
                disabled={(drillSettings.analysisMode || "during") === "after"}
                onClick={() => updateDrillSettings({ pauseMode: id })}
              />
            ))}
          </DrillSection>

          <DrillSection title="Session length">
            {[[0, "Endless"], [10, "10 hands"], [25, "25 hands"], [50, "50 hands"]].map(([n, label]) => (
              <DrillChip
                key={`sess-${n}`}
                label={label}
                active={Number(drillSettings.sessionLength) === n}
                onClick={() => updateDrillSettings({ sessionLength: n })}
              />
            ))}
          </DrillSection>

          <Typography sx={{ fontSize: 10, color: COLORS.textMuted, mb: 1 }}>
            {Number(drillSettings.sessionLength) > 0
              ? `Session: ${sessionHandsPlayed} / ${drillSettings.sessionLength} hands`
              : `Hands this session: ${sessionHandsPlayed}`}
            {". Filters apply from the next hand."}
          </Typography>

            <Box sx={{ mt: 1, border: `1px solid ${COLORS.border}`, borderRadius: "10px", backgroundColor: "#1f1f1f", p: 1 }}>
              <Typography sx={{ fontSize: 12, color: COLORS.textMuted }}>Player Ability Rating</Typography>
              <Typography sx={{ fontSize: 20, fontWeight: 800, color: COLORS.white, lineHeight: 1.1 }}>{abilityRating} / 10.0</Typography>
              <Typography sx={{ mt: 0.8, fontSize: 11, color: COLORS.textMuted }}>Session Action History</Typography>
              <Box sx={{ mt: 0.4, maxHeight: 170, overflowY: "auto", borderTop: `1px solid ${COLORS.border}`, pt: 0.5 }}>
                {!sessionActionLog.length && (
                  <Typography sx={{ fontSize: 11, color: COLORS.textMuted }}>No actions logged yet.</Typography>
                )}
                {sessionActionLog.map((item) => (
                  <Typography
                    key={item.id}
                    sx={{ fontSize: 11, color: item.outcome === "correct" ? COLORS.green : item.outcome === "blunder" ? COLORS.danger : COLORS.textMuted, mb: 0.2 }}
                  >
                    {String(item.street || "").toUpperCase()} - {item.outcome === "correct" ? "✓" : item.outcome === "blunder" ? "✗" : "N/A"} {item.action}
                  </Typography>
                ))}
              </Box>
            </Box>
        </Box>
        )}

        <Box sx={{ backgroundColor: isPlayMode || isEmbed ? "transparent" : COLORS.cardBg, border: isPlayMode || isEmbed ? "none" : `1px solid ${COLORS.border}`, borderRadius: "12px", p: isPlayMode || isEmbed ? 0 : 1.5, width: "100%", maxWidth: isPlayMode || isEmbed ? 1220 : "none" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
            {!isPlayMode && !isEmbed && <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Trainer</Typography>}
          </Box>
          {isPlayMode && (
            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 0.75, position: "relative", zIndex: 8 }}>
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.4, minWidth: 0 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Play</Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.35 }}>
                  <Button
                    size="small"
                    variant="text"
                    onClick={exitPlayToLobby}
                    sx={{
                      minWidth: "unset",
                      px: 0.4,
                      py: 0.05,
                      color: COLORS.textMuted,
                      fontSize: "11px",
                      textTransform: "none",
                      lineHeight: 1.1,
                      "&:hover": { color: COLORS.white, backgroundColor: "rgba(255,255,255,0.06)" },
                    }}
                  >
                    ←
                  </Button>
                  <Button
                    size="small"
                    variant="text"
                    onClick={exitPlayToLobby}
                    sx={{
                      minWidth: "unset",
                      px: 0.55,
                      py: 0.15,
                      color: COLORS.white,
                      fontSize: "11px",
                      fontWeight: 400,
                      fontFamily: FUTURA_BOOK,
                      textTransform: "none",
                      lineHeight: 1.2,
                      whiteSpace: "normal",
                      textAlign: "left",
                      maxWidth: 160,
                      "&:hover": { color: COLORS.white, backgroundColor: "rgba(255,255,255,0.08)" },
                    }}
                  >
                    Stand up and take chips
                  </Button>
                </Box>
              </Box>
              <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: playSessionScore >= 0 ? COLORS.green : COLORS.danger }}>
                  Session {playSessionScore >= 0 ? "+" : ""}{Number.isInteger(playSessionScore) ? String(playSessionScore) : playSessionScore.toFixed(1)} chips
                </Typography>
                <Typography sx={{ fontSize: 10, color: COLORS.textMuted }}>{playSessionHands} hands</Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={startNewPlaySession}
                  sx={{
                    mt: 0.3,
                    minWidth: "unset",
                    px: 0.8,
                    py: 0.05,
                    borderColor: COLORS.border,
                    color: COLORS.textMuted,
                    fontSize: "9px",
                    textTransform: "none",
                    lineHeight: 1.1,
                    backgroundColor: "rgba(0,0,0,0.35)",
                    "&:hover": { borderColor: COLORS.white, color: COLORS.white },
                  }}
                >
                  {String(playConfig?.mode || "casual") === "challenge" ? "End Challenge" : "New Session"}
                </Button>
              </Box>
            </Box>
          )}
          <Box
            sx={{
              border: isPlayMode ? "none" : `1px solid ${COLORS.border}`,
              backgroundColor: isPlayMode ? "transparent" : pageBg,
              borderRadius: isPlayMode ? 0 : "12px",
              p: isPlayMode ? 0 : { xs: 0.75, sm: 1.25 },
              mb: 1.25,
              ...(isPlayHu
                ? {
                    position: "relative",
                    // Phone: clip overhanging opponent. Desktop: keep visible so seat borders aren't cut.
                    overflow: isPhonePlayHu ? "hidden" : "visible",
                    pt: isPhonePlayHu ? (playPhoneTableH < 460 ? 2.2 : 3) : { sm: 1.25 },
                    pb: { xs: 0.5, sm: 0.75 },
                  }
                : {}),
            }}
          >
            <Box
              sx={{
                position: "relative",
                height: {
                  xs: isPlayHu
                    ? (isPhonePlayHu ? playPhoneTableH : 500)
                    : (usePhoneTableLayout ? 520 : 470),
                  sm: isPlayHu ? (isDesktopPlayHu ? 520 : 480) : 420,
                },
                mx: isPlayMode ? 0 : { xs: usePhoneTableLayout ? (isSlimPhone ? 3.25 : 4) : 5, sm: 7 },
                my: isPlayMode ? 0 : { xs: usePhoneTableLayout ? 3.5 : 4, sm: 4.5 },
                borderRadius: tableRadius,
                border: `10px solid ${railColor}`,
                boxShadow: isPlayMode
                  ? `inset 0 0 60px rgba(0,0,0,0.55), 0 8px 26px rgba(0,0,0,0.45), inset 0 0 0 2px ${hexToRgba(chipTheme.border, 0.25)}`
                  : `inset 0 0 0 2px ${hexToRgba(chipTheme.border, 0.3)}`,
                background: `radial-gradient(ellipse at 50% 42%, ${feltTheme.c1} 0%, ${feltTheme.c2} 55%, ${feltTheme.c3} 100%)`,
                overflow: "visible",
              }}
            >
              {!isPlayMode && !isPhoneTable && (() => {
                const heroVisual = (visualSeats || []).find((s) => s.isHero);
                const heroGeo = heroVisual
                  ? resolveSeatGeometry(heroVisual, {
                      slimTable: useCompactTableSeats && isSlimPhone,
                      phoneTable: useCompactTableSeats && !isSlimPhone,
                    })
                  : { top: "97%", left: "50%" };
                const placeBelowHero = sitOutNeedsBelowHero;
                return (
                <Button
                  size="small"
                  onClick={toggleSitOut}
                  sx={{
                    position: "absolute",
                    top: placeBelowHero
                      ? `calc(${heroGeo.top} + 52px)`
                      : "97%",
                    left: placeBelowHero ? heroGeo.left : "50%",
                    transform: placeBelowHero ? "translate(-50%, 0)" : "translate(-50%, -50%)",
                    zIndex: 6,
                    minWidth: "unset",
                    px: 1.1,
                    py: 0.25,
                    borderRadius: "999px",
                    fontSize: "10px",
                    fontWeight: 700,
                    textTransform: "none",
                    whiteSpace: "nowrap",
                    border: `1px solid ${sitOutNextHand ? COLORS.gold : "rgba(255,255,255,0.18)"}`,
                    color: sitOutNextHand ? "#111" : COLORS.textMuted,
                    backgroundColor: sitOutNextHand ? COLORS.gold : "rgba(16,18,20,0.94)",
                    boxShadow: "0 4px 10px rgba(0,0,0,0.5)",
                    "&:hover": {
                      backgroundColor: sitOutNextHand ? COLORS.gold : "rgba(30,32,36,0.94)",
                      borderColor: COLORS.gold,
                    },
                  }}
                >
                  {sitOutNextHand ? "Sitting out next hand" : "Sit out next hand"}
                </Button>
                );
              })()}
              {visualSeats.map((seat) => {
                const geo = resolveSeatGeometry(seat, {
                  slimTable: useCompactTableSeats && isSlimPhone,
                  phoneTable: useCompactTableSeats && !isSlimPhone,
                });
                const stackCardsInSeat = usePhoneTableLayout && !isPlayMode;
                const cardsAboveInfo = stackCardsInSeat && isBottomTableSeat(seat.id);
                const phoneOpp = usePhoneTableLayout && !seat.isHero;
                const renderSeatCards = () => (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      gap: phoneOpp ? 0.3 : 0.5,
                      mt: stackCardsInSeat && !cardsAboveInfo ? 0.45 : 0,
                      mb: stackCardsInSeat && cardsAboveInfo ? 0.45 : 0,
                      opacity: seat.folded ? 0.45 : 1,
                    }}
                  >
                    {(handsBySeat[seat.logicalSeat] || [null, null]).map((c, i) => {
                      if (phoneOpp) {
                        if (!c) return <SmallCardBack key={`${seat.id}-blank-${i}`} />;
                        if (showdownRevealedSeats.has(seat.logicalSeat)) {
                          return <SmallHoleCard key={`${seat.id}-${i}`} card={c} />;
                        }
                        return <SmallCardBack key={`${seat.id}-back-${i}`} />;
                      }
                      if (!c) return <CardBack key={`${seat.id}-blank-${i}`} />;
                      if (seat.logicalSeat === heroSeat || showdownRevealedSeats.has(seat.logicalSeat)) {
                        return <HoleCard key={`${seat.id}-${i}`} card={c} />;
                      }
                      return <CardBack key={`${seat.id}-back-${i}`} />;
                    })}
                  </Box>
                );
                return (
                <React.Fragment key={seat.id}>
                  <Box
                    sx={{
                      position: "absolute",
                      ...(isPlayHu
                        ? (seat.isHero
                          ? {
                              top: "auto",
                              bottom: { xs: 8, sm: 14 },
                              left: "50%",
                              transform: "translateX(-50%)",
                            }
                          : {
                              // Keep opponent near the top rail so felt center has room for chips/board.
                              top: isDesktopPlayHu ? 8 : { xs: -28, sm: -36 },
                              bottom: "auto",
                              left: "50%",
                              transform: "translateX(-50%)",
                            })
                        : {
                            top: isPlayMode
                              ? (seat.isHero
                                ? { xs: `calc(${geo.top} + 82px)`, sm: `calc(${geo.top} + 86px)` }
                                : { xs: `calc(${geo.top} - 108px)`, sm: `calc(${geo.top} - 124px)` })
                              : geo.top,
                            left: geo.left,
                            transform: "translate(-50%, -50%)",
                          }),
                      minWidth: isPlayHu
                        ? (isDesktopPlayHu
                          ? (seat.isHero ? 190 : 200)
                          : { xs: 148, sm: 168 })
                        : (isPlayMode
                          ? { xs: 132, sm: 168 }
                          : (phoneOpp
                            ? (isSlimPhone ? 74 : 84)
                            : (usePhoneTableLayout ? 92 : { xs: 86, sm: 102 }))),
                      maxWidth: isPlayHu
                        ? (isDesktopPlayHu ? (seat.isHero ? 240 : 230) : { xs: 196, sm: 210 })
                        : (isPlayMode && !seat.isHero
                          ? { xs: 168, sm: 200 }
                          : (phoneOpp ? (isSlimPhone ? 88 : 100) : undefined)),
                      width: isPlayHu
                        ? (isDesktopPlayHu ? "auto" : { xs: "52%", sm: 190 })
                        : (isPlayMode && !seat.isHero && !isPlaySixMax ? { xs: "44%", sm: 180 } : undefined),
                      px: isPlayMode && !seat.isHero ? 0 : (phoneOpp ? (isSlimPhone ? 0.45 : 0.55) : { xs: 0.6, sm: 0.9 }),
                      py: isPlayMode && !seat.isHero ? 0 : (isDesktopPlayHu && seat.isHero ? 0.55 : (phoneOpp ? (isSlimPhone ? 0.35 : 0.45) : { xs: 0.4, sm: 0.55 })),
                      borderRadius: (phoneOpp || (isPlayMode && !seat.isHero) || isDesktopPlayHu) ? "8px" : "10px",
                      border: `1px solid ${seat.isAction ? glowColor : seat.isHero ? COLORS.green : "rgba(255,255,255,0.14)"}`,
                      boxShadow: seat.isAction
                        ? `0 0 0 2px ${glowColor}, 0 0 14px ${hexToRgba(glowColor, 0.45)}, 0 4px 10px rgba(0,0,0,0.5)`
                        : "0 4px 10px rgba(0,0,0,0.5)",
                      backgroundColor: seat.folded ? "rgba(40,40,40,0.6)" : "rgba(16,18,20,0.94)",
                      opacity: seat.folded ? 0.5 : 1,
                      textAlign: "center",
                      // Info panels always paint above cards / chips / board / pot on collision.
                      zIndex: 20,
                      display: (stackCardsInSeat || isDesktopPlayHu) ? "flex" : undefined,
                      flexDirection: stackCardsInSeat ? "column" : (isDesktopPlayHu ? "row" : undefined),
                      alignItems: (stackCardsInSeat || isDesktopPlayHu) ? (isDesktopPlayHu ? "stretch" : "center") : undefined,
                      overflow: isPlayHu && !seat.isHero ? "visible" : undefined,
                    }}
                  >
                    {isPlayMode && !seat.isHero ? (
                      <Box
                        sx={{
                          width: "100%",
                          display: "flex",
                          flexDirection: isDesktopPlayHu ? "row" : "column",
                          alignItems: isDesktopPlayHu ? "stretch" : "stretch",
                          position: "relative",
                          overflow: "visible",
                          borderRadius: "8px",
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", alignItems: "stretch" }}>
                        {(!isDesktopPlayHu || !!playVillainSpeech) && (!!playVillainSpeech || (!playSessionStarted && !isPlaySixMax)) && (() => {
                          const seatOpponentName = playOpponentNamesBySeat?.[seat.logicalSeat] || playConfig?.opponentName || "Opponent";
                          const quote = playVillainSpeech || getPlayOpponentQuoteByName(seatOpponentName);
                          if (!quote) return null;
                          return (
                            <Box
                              sx={{
                                position: "relative",
                                mx: { xs: 0.45, sm: 0.5 },
                                mt: { xs: 0.45, sm: 0.45 },
                                mb: { xs: 0.35, sm: 0.35 },
                                px: { xs: 0.7, sm: 0.75 },
                                py: { xs: 0.45, sm: 0.45 },
                                borderRadius: "8px",
                                backgroundColor: "#F6ECCE",
                                color: "#111",
                                border: "1px solid #1d1d1d",
                                boxShadow: "0 4px 10px rgba(0,0,0,0.28)",
                                zIndex: 2,
                                "&::after": {
                                  content: "\"\"",
                                  position: "absolute",
                                  left: "50%",
                                  bottom: -7,
                                  transform: "translateX(-50%)",
                                  width: 0,
                                  height: 0,
                                  borderLeft: "7px solid transparent",
                                  borderRight: "7px solid transparent",
                                  borderTop: "7px solid #F6ECCE",
                                },
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: { xs: 10, sm: 10 },
                                  lineHeight: 1.25,
                                  color: "#111",
                                  fontWeight: 700,
                                  textAlign: "left",
                                  display: "-webkit-box",
                                  WebkitLineClamp: isDesktopPlayHu ? 2 : 2,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                }}
                              >
                                {quote}
                              </Typography>
                            </Box>
                          );
                        })()}
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "stretch",
                            width: "100%",
                            minWidth: 0,
                          }}
                        >
                          <Box
                            sx={{
                              position: "relative",
                              width: isDesktopPlayHu ? "42%" : 56,
                              minWidth: isDesktopPlayHu ? 0 : 56,
                              maxWidth: isDesktopPlayHu ? "none" : 56,
                              aspectRatio: isDesktopPlayHu ? "1 / 0.55" : "1 / 1.15",
                              maxHeight: isDesktopPlayHu ? 72 : 64,
                              backgroundColor: "#111",
                              flexShrink: 0,
                              overflow: "hidden",
                              borderTopLeftRadius: isDesktopPlayHu ? "8px" : 0,
                              borderBottomLeftRadius: isDesktopPlayHu ? 0 : "8px",
                            }}
                          >
                            {playConfig?.opponentImageUrl && (
                              <img
                                src={playConfig.opponentImageUrl}
                                alt={playConfig?.opponentName || "opponent"}
                                style={{
                                  position: "absolute",
                                  inset: 0,
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                  objectPosition: "center center",
                                }}
                                onError={(e) => { e.currentTarget.style.display = "none"; }}
                              />
                            )}
                          </Box>
                          <Box
                            sx={{
                              flex: 1,
                              minWidth: 0,
                              px: { xs: 0.55, sm: 0.55 },
                              py: { xs: 0.35, sm: 0.3 },
                              textAlign: isDesktopPlayHu ? "center" : "left",
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "center",
                              gap: 0.1,
                            }}
                          >
                            <Typography sx={{ fontSize: seatFsFloor || { xs: 10, sm: 10 }, color: COLORS.textMuted, lineHeight: 1.15 }}>
                              {playOpponentNamesBySeat?.[seat.logicalSeat] || playConfig?.opponentName || "Opponent"}
                            </Typography>
                            <Typography sx={{ fontSize: seatFsFloor || { xs: 11, sm: 11 }, color: COLORS.white, fontWeight: 700, lineHeight: 1.15 }}>{seat.label}</Typography>
                            <Typography sx={{ fontSize: seatFsFloor || { xs: 11, sm: 11 }, color: seat.folded ? COLORS.textMuted : COLORS.green, fontWeight: 700, lineHeight: 1.15 }}>
                              {isPlayMode ? `${formatUiChips(seat.stack)} chips` : `${seat.stack.toFixed(1)}bb`}
                            </Typography>
                            <Typography sx={{ fontSize: seatFsFloor ? Math.max(seatFsFloor - 2, CHIP_LABEL_FS_FLOOR) : { xs: 9, sm: 9 }, color: seat.isThinking ? COLORS.gold : COLORS.textMuted, fontWeight: 700, lineHeight: 1.15 }}>
                              {seat.isThinking ? "Thinking..." : (latestActionBySeat[seat.logicalSeat] || "-")}
                            </Typography>
                          </Box>
                        </Box>
                        <Box
                          sx={{
                            px: { xs: 0.5, sm: 0.45 },
                            pt: isDesktopPlayHu ? 0 : 0.2,
                            pb: { xs: 0.45, sm: isDesktopPlayHu ? 0.35 : 0.5 },
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 0.2,
                            borderTop: "1px solid rgba(255,255,255,0.08)",
                            opacity: seat.folded ? 0.45 : 1,
                          }}
                        >
                          <Button
                            size="small"
                            variant="text"
                            onClick={onTogglePlayViewCards}
                            startIcon={playCardsLocked ? <LockOutlinedIcon sx={{ fontSize: 11 }} /> : null}
                            sx={{
                              minWidth: "unset",
                              px: 0.4,
                              py: 0,
                              minHeight: 18,
                              color: playCardsLocked ? COLORS.gold : COLORS.textMuted,
                              fontSize: "10px",
                              textTransform: "none",
                              lineHeight: 1.1,
                              "&:hover": { color: COLORS.white, backgroundColor: "rgba(255,255,255,0.06)" },
                            }}
                          >
                            {playShowOpponentCards ? "Hide cards" : "View cards"}
                          </Button>
                          {!isDesktopPlayHu && (
                          <Box sx={{ display: "flex", justifyContent: "center" }}>
                            {(handsBySeat[seat.logicalSeat] || [null, null]).map((c, i) => (
                              <Box key={`${seat.id}-inline-wrap-${i}`} sx={{ ml: i === 0 ? 0 : { xs: -0.6, sm: -0.7 } }}>
                                {!c ? (
                                  <SmallCardBack key={`${seat.id}-inline-blank-${i}`} />
                                ) : (seat.logicalSeat === heroSeat || showdownRevealedSeats.has(seat.logicalSeat) || playShowOpponentCards) ? (
                                  <SmallHoleCard key={`${seat.id}-inline-${i}`} card={c} />
                                ) : (
                                  <SmallCardBack key={`${seat.id}-inline-back-${i}`} />
                                )}
                              </Box>
                            ))}
                          </Box>
                          )}
                        </Box>
                        </Box>
                        {isDesktopPlayHu && (
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "row",
                              justifyContent: "center",
                              alignItems: "center",
                              px: 0.65,
                              borderLeft: "1px solid rgba(255,255,255,0.08)",
                              opacity: seat.folded ? 0.45 : 1,
                              minWidth: 72,
                            }}
                          >
                            {(handsBySeat[seat.logicalSeat] || [null, null]).map((c, i) => (
                              <Box key={`${seat.id}-desk-wrap-${i}`} sx={{ ml: i === 0 ? 0 : -0.7 }}>
                                {!c ? (
                                  <SmallCardBack />
                                ) : (showdownRevealedSeats.has(seat.logicalSeat) || playShowOpponentCards) ? (
                                  <SmallHoleCard card={c} />
                                ) : (
                                  <SmallCardBack />
                                )}
                              </Box>
                            ))}
                          </Box>
                        )}
                      </Box>
                    ) : (
                      <>
                        {stackCardsInSeat && cardsAboveInfo ? renderSeatCards() : null}
                        <Box sx={{ width: isDesktopPlayHu ? "auto" : "100%", px: 0.1, flex: isDesktopPlayHu ? 1 : undefined, minWidth: isDesktopPlayHu ? 110 : undefined }}>
                          <Typography sx={{ fontSize: seatFsFloor || (phoneOpp ? (isSlimPhone ? 9 : 10) : { xs: 10, sm: 11 }), color: COLORS.textMuted, lineHeight: 1.15 }}>
                            {seat.isHero ? "Hero" : (isPlayMode ? (playOpponentNamesBySeat?.[seat.logicalSeat] || playConfig?.opponentName || "Opponent") : "Villain")}
                          </Typography>
                          <Typography sx={{ fontSize: seatFsFloor || (phoneOpp ? (isSlimPhone ? 10 : 11) : { xs: 11, sm: 12 }), color: COLORS.white, fontWeight: 700, lineHeight: 1.15 }}>{seat.label}</Typography>
                          <Typography sx={{ fontSize: seatFsFloor || (phoneOpp ? (isSlimPhone ? 10 : 11) : { xs: 11, sm: 12 }), color: seat.folded ? COLORS.textMuted : COLORS.green, fontWeight: 700, lineHeight: 1.15 }}>
                            {isPlayMode ? `${formatUiChips(seat.stack)} chips` : `${seat.stack.toFixed(1)}bb`}
                          </Typography>
                          <Typography sx={{ fontSize: seatFsFloor ? Math.max(seatFsFloor - 2, CHIP_LABEL_FS_FLOOR) : (phoneOpp ? (isSlimPhone ? 8 : 9) : { xs: 9, sm: 10 }), color: seat.isThinking ? COLORS.gold : COLORS.textMuted, fontWeight: 700, lineHeight: 1.15 }}>
                            {seat.isThinking ? "Thinking..." : (latestActionBySeat[seat.logicalSeat] || "-")}
                          </Typography>
                          {!isPlayMode && seatClock?.seat === seat.logicalSeat && !seat.folded && (
                            <Box sx={{ mt: 0.4, px: 0.2, display: "flex", alignItems: "center", gap: 0.5 }}>
                              <Box sx={{ flex: 1, height: phoneOpp && isSlimPhone ? 3 : 4, borderRadius: 2, backgroundColor: "rgba(255,255,255,0.12)", overflow: "hidden" }}>
                                <Box
                                  sx={{
                                    height: "100%",
                                    width: `${Math.round(clockFraction * 100)}%`,
                                    borderRadius: 2,
                                    backgroundColor: clockFraction > 0.45 ? COLORS.green : clockFraction > 0.2 ? COLORS.gold : COLORS.danger,
                                    transition: "width 100ms linear",
                                  }}
                                />
                              </Box>
                              <Typography sx={{ fontSize: phoneOpp && isSlimPhone ? 8 : 9, fontWeight: 800, color: clockFraction > 0.2 ? COLORS.textMuted : COLORS.danger, minWidth: 14, textAlign: "right" }}>
                                {clockSecondsLeft}s
                              </Typography>
                            </Box>
                          )}
                        </Box>
                        {isDesktopPlayHu && seat.isHero && (
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "row",
                              justifyContent: "center",
                              alignItems: "center",
                              px: 0.75,
                              borderLeft: "1px solid rgba(255,255,255,0.1)",
                              opacity: seat.folded ? 0.45 : 1,
                              minWidth: 88,
                            }}
                          >
                            {(handsBySeat[seat.logicalSeat] || [null, null]).map((c, i) => (
                              <Box key={`${seat.id}-hero-desk-${i}`} sx={{ ml: i === 0 ? 0 : -0.85 }}>
                                {!c ? <CardBack /> : <HoleCard card={c} />}
                              </Box>
                            ))}
                          </Box>
                        )}
                        {stackCardsInSeat && !cardsAboveInfo ? renderSeatCards() : null}
                      </>
                    )}
                    {isPlayHu && !seat.isHero && Number(committedBySeat[seat.logicalSeat] || 0) > 0 && (
                      <Box
                        sx={{
                          position: "absolute",
                          left: "50%",
                          // Chips sit on the felt in front of the opponent (toward pot).
                          top: "100%",
                          mt: isDesktopPlayHu ? 0.7 : 0.45,
                          transform: "translateX(-50%)",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: `${chipGap}px`,
                          zIndex: 3,
                          pointerEvents: "none",
                        }}
                      >
                        <Box sx={{ position: "relative", width: 34, height: 16, flexShrink: 0 }}>
                          {Array.from({
                            length:
                              (seat.logicalSeat === "sb" && Number(committedBySeat[seat.logicalSeat] || 0) <= 0.5001) ||
                              (seat.logicalSeat === "bb" && Number(committedBySeat[seat.logicalSeat] || 0) <= 1.0001)
                                ? 1
                                : 3,
                          }).map((_, i) => (
                            <PokerChip
                              key={`${seat.logicalSeat}-chip-inline-${i}`}
                              size={16}
                              alt={i % 2 === 1}
                              sx={{ position: "absolute", top: 0, left: `${i * 9}px` }}
                            />
                          ))}
                        </Box>
                        <Typography sx={{ fontSize: chipFsFloor || 10, color: chipTheme.text, fontWeight: 800, lineHeight: 1 }}>
                          {formatUiChips(Number(committedBySeat[seat.logicalSeat] || 0))}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  {!stackCardsInSeat && !(isDesktopPlayHu && seat.isHero) && (
                  <Box
                    sx={{
                      position: "absolute",
                      ...(isPlayHu && seat.isHero
                        ? {
                            top: "auto",
                            bottom: isPhonePlayHu
                              ? playPhoneHeroCardsBottom
                              : { xs: 92, sm: 108 },
                            left: "50%",
                            transform: "translateX(-50%)",
                          }
                        : {
                            top: isPlayMode
                              ? (seat.isHero
                                ? { xs: `calc(${geo.top} + 8px)`, sm: `calc(${geo.top} + 14px)` }
                                : { xs: `calc(${geo.top} + 23px)`, sm: `calc(${geo.top} + 31px)` })
                              : (geo.cardTop || { xs: `calc(${geo.top} + 50px)`, sm: `calc(${geo.top} + 58px)` }),
                            left: isPlayMode ? geo.left : (geo.cardLeft || geo.left),
                            transform: isPlayMode ? "translate(-50%, -50%)" : (geo.cardTop ? "translate(-50%, -50%)" : "translate(-50%, 0)"),
                          }),
                      display: isPlayMode && !seat.isHero ? "none" : "flex",
                      gap: 0.5,
                      zIndex: isPlayMode ? 6 : (seat.isHero ? 3 : 2),
                      opacity: seat.folded ? 0.45 : 1,
                    }}
                  >
                    {(handsBySeat[seat.logicalSeat] || [null, null]).map((c, i) => {
                      if (!c) return isPlayMode && !seat.isHero ? <SmallCardBack key={`${seat.id}-blank-${i}`} /> : <CardBack key={`${seat.id}-blank-${i}`} />;
                      if (seat.logicalSeat === heroSeat || showdownRevealedSeats.has(seat.logicalSeat)) {
                        return isPlayMode && !seat.isHero
                          ? <SmallHoleCard key={`${seat.id}-${i}`} card={c} />
                          : <HoleCard key={`${seat.id}-${i}`} card={c} />;
                      }
                      return isPlayMode && !seat.isHero ? <SmallCardBack key={`${seat.id}-back-${i}`} /> : <CardBack key={`${seat.id}-back-${i}`} />;
                    })}
                  </Box>
                  )}
                  {isPlayHu && seat.isHero && Number(committedBySeat[seat.logicalSeat] || 0) > 0 && (
                    <Box
                      sx={{
                        position: "absolute",
                        left: "50%",
                        // Above hero hole cards / seat row toward the pot.
                        bottom: isDesktopPlayHu
                          ? { sm: 118 }
                          : (isPhonePlayHu ? playPhoneHeroChipsBottom : { xs: 150, sm: 174 }),
                        transform: "translateX(-50%)",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: `${chipGap}px`,
                        zIndex: 7,
                        pointerEvents: "none",
                      }}
                    >
                      <Box sx={{ position: "relative", width: 34, height: 16, flexShrink: 0 }}>
                        {Array.from({
                          length:
                            (seat.logicalSeat === "sb" && Number(committedBySeat[seat.logicalSeat] || 0) <= 0.5001) ||
                            (seat.logicalSeat === "bb" && Number(committedBySeat[seat.logicalSeat] || 0) <= 1.0001)
                              ? 1
                              : 3,
                        }).map((_, i) => (
                          <PokerChip
                            key={`${seat.logicalSeat}-chip-hero-${i}`}
                            size={16}
                            alt={i % 2 === 1}
                            sx={{ position: "absolute", top: 0, left: `${i * 9}px` }}
                          />
                        ))}
                      </Box>
                      <Typography sx={{ fontSize: chipFsFloor || 10, color: chipTheme.text, fontWeight: 800, lineHeight: 1 }}>
                        {formatUiChips(Number(committedBySeat[seat.logicalSeat] || 0))}
                      </Typography>
                    </Box>
                  )}
                  {!(isPlayHu) && Number(committedBySeat[seat.logicalSeat] || 0) > 0 && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: geo.chipTop,
                        left: geo.chipLeft,
                        transform: "translate(-50%, -50%)",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: isPlayMode ? `${chipGap}px` : 0.2,
                        zIndex: 9,
                        pointerEvents: "none",
                      }}
                    >
                      <Box sx={{ position: "relative", width: phoneOpp && isSlimPhone ? 28 : 34, height: phoneOpp && isSlimPhone ? 14 : 16, flexShrink: 0 }}>
                        {Array.from({
                          length:
                            (seat.logicalSeat === "sb" && Number(committedBySeat[seat.logicalSeat] || 0) <= 0.5001) ||
                            (seat.logicalSeat === "bb" && Number(committedBySeat[seat.logicalSeat] || 0) <= 1.0001)
                              ? 1
                              : 3,
                        }).map((_, i) => (
                          <PokerChip
                            key={`${seat.logicalSeat}-chip-${i}`}
                            size={phoneOpp && isSlimPhone ? 13 : 16}
                            alt={i % 2 === 1}
                            sx={{ position: "absolute", top: 0, left: `${i * (phoneOpp && isSlimPhone ? 7 : 9)}px` }}
                          />
                        ))}
                      </Box>
                      <Typography sx={{ fontSize: chipFsFloor || (phoneOpp && isSlimPhone ? 9 : 10), color: chipTheme.text, fontWeight: 800, lineHeight: 1 }}>
                        {isPlayMode
                          ? formatUiChips(Number(committedBySeat[seat.logicalSeat] || 0))
                          : Number(committedBySeat[seat.logicalSeat]).toFixed(1)}
                      </Typography>
                    </Box>
                  )}
                </React.Fragment>
                );
              })}
              <Box sx={{
                position: "absolute",
                top: isPlayHu
                  ? (isDesktopPlayHu ? "48%" : (isPhonePlayHu ? "52%" : { xs: "56%", sm: "54%" }))
                  : "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: isPlayMode ? `${chipGap}px` : 0.75,
                zIndex: 3,
                pointerEvents: "none",
              }}>
                {!isPlayMode && (
                  <Typography sx={{ fontSize: 13, color: COLORS.textMuted, fontWeight: 600 }}>
                    {`Pot ${pot.toFixed(1)}bb`}
                  </Typography>
                )}
                {pot > 0 && (
                  <Box
                    sx={{
                      position: "relative",
                      width: 44,
                      height: 18,
                      flexShrink: 0,
                      transform: showdownChipTransform,
                      transition: "transform 700ms ease",
                    }}
                  >
                    {[0, 1, 2].map((i) => (
                      <PokerChip
                        key={`main-pot-chip-${i}`}
                        size={18}
                        alt={i % 2 === 1}
                        sx={{ position: "absolute", top: 0, left: `${i * 12}px` }}
                      />
                    ))}
                  </Box>
                )}
                {isPlayMode && (
                  <Typography sx={{ fontSize: chipFsFloor || CHIP_LABEL_FS_FLOOR, color: chipTheme.text, fontWeight: 800, lineHeight: 1 }}>
                    {formatUiChips(pot)}
                  </Typography>
                )}
                {!!boardCards.length && (
                  <Box sx={{ display: "flex", gap: 0.5, mt: isPlayMode ? 0.5 : 0 }}>
                    {boardCards.map((c) => <Card key={`board-${c}`} card={c} />)}
                  </Box>
                )}
                {!!centerMessage && (
                  <Typography sx={{ fontSize: 12, color: COLORS.white, fontWeight: 700, textAlign: "center" }}>
                    {centerMessage}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>

          {/* While paused for GTO feedback: range/solve chips, analysis, Continue. */}
          {!isPlayMode && waitForHeroContinue ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, mb: { xs: 0.45, sm: 0.8 } }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: { xs: "stretch", sm: "center" },
                  gap: { xs: 0.75, sm: 1.25 },
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    minHeight: { xs: 52, sm: 58 },
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: "10px",
                    backgroundColor: "#1f1f1f",
                    px: 1.25,
                    py: 0.95,
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: { xs: "stretch", sm: "center" },
                    gap: { xs: 0.75, sm: 0 },
                  }}
                >
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, flexShrink: 0, justifyContent: { xs: "stretch", sm: "flex-start" } }}>
                    <Button
                      variant="contained"
                      onClick={() => setShowFullRange((v) => !v)}
                      sx={{
                        textTransform: "none",
                        width: { xs: "100%", sm: "auto" },
                        minWidth: { xs: "100%", sm: 160 },
                        minHeight: { xs: 28, sm: 39 },
                        px: { xs: 1.5, sm: 2.4 },
                        fontSize: { xs: 14, sm: 15 },
                        fontWeight: 400,
                        fontFamily: FUTURA_BOOK,
                        borderRadius: "10px",
                        flexShrink: 0,
                        backgroundColor: COLORS.green,
                        color: "#111",
                        boxShadow: "none",
                        "&:hover": { backgroundColor: "#45b39b", boxShadow: "none" },
                      }}
                    >
                      {showFullRange ? "Hide full range" : "See full range"}
                    </Button>
                    <Button
                      variant="contained"
                      onClick={onSolveThisSpot}
                      disabled={!feedbackNodeSnapshot}
                      sx={{
                        textTransform: "none",
                        width: { xs: "100%", sm: "auto" },
                        minWidth: { xs: "100%", sm: 160 },
                        minHeight: { xs: 28, sm: 39 },
                        px: { xs: 1.5, sm: 2.4 },
                        fontSize: { xs: 14, sm: 15 },
                        fontWeight: 400,
                        fontFamily: FUTURA_BOOK,
                        borderRadius: "10px",
                        flexShrink: 0,
                        backgroundColor: "#4C8DFF",
                        color: "#111",
                        boxShadow: "none",
                        "&:hover": { backgroundColor: "#3a7aef", boxShadow: "none" },
                        "&.Mui-disabled": { backgroundColor: "#2b2b2b", color: "#555" },
                      }}
                    >
                      Solve this spot
                    </Button>
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      pl: { xs: 0, sm: 2.5 },
                      pt: { xs: 0.5, sm: 0 },
                    }}
                  >
                    <Typography sx={{ fontSize: 12, color: COLORS.textMuted, mb: 0.25, textAlign: { xs: "center", sm: "left" }, fontFamily: FUTURA_BOOK, fontWeight: 400 }}>
                      Analysis
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: { xs: 15, sm: 16 },
                        fontWeight: 400,
                        fontFamily: FUTURA_BOOK,
                        textAlign: { xs: "center", sm: "left" },
                        color: feedback.toLowerCase().startsWith("correct")
                          ? COLORS.green
                          : feedback.toLowerCase().includes("blunder") || feedback.toLowerCase().includes("use street")
                            ? COLORS.danger
                            : COLORS.text,
                      }}
                    >
                      {feedback}
                    </Typography>
                  </Box>
                </Box>
                <Button
                  variant="contained"
                  onClick={onContinueAfterHero}
                  sx={{
                    textTransform: "none",
                    width: { xs: "100%", sm: "auto" },
                    minWidth: { xs: "100%", sm: 160 },
                    minHeight: { xs: 42, sm: 58 },
                    px: { xs: 1.5, sm: 2.4 },
                    fontSize: { xs: 14, sm: 15 },
                    fontWeight: 400,
                    fontFamily: FUTURA_BOOK,
                    borderRadius: "10px",
                    flexShrink: 0,
                    backgroundColor: COLORS.white,
                    color: "#111",
                    "&:hover": { backgroundColor: "#e8e8e8" },
                  }}
                >
                  Continue
                </Button>
              </Box>
              {showFullRange && (
                <Box
                  sx={{
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: "10px",
                    backgroundColor: "#1a1a1a",
                    p: { xs: 0.9, sm: 1.1 },
                  }}
                >
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: COLORS.text, mb: 0.75 }}>
                    Hero range at this node
                    {feedbackNodeSnapshot?.heroLabel ? ` (${feedbackNodeSnapshot.heroLabel})` : ""}
                  </Typography>
                  <TrainerMiniRangePanel
                    actions={feedbackNodeSnapshot?.actions || []}
                    grid={feedbackNodeSnapshot?.grid || null}
                    heroLabel={feedbackNodeSnapshot?.heroLabel || ""}
                    boardCards={feedbackNodeSnapshot?.boardCards || []}
                  />
                </Box>
              )}
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: { xs: 0.6, sm: 1.25 }, flexWrap: "wrap", justifyContent: "center", mb: { xs: 0.45, sm: 0.8 } }}>
              {isPlayMode && !playSessionStarted && (
                <Button
                  variant="contained"
                  onClick={startTraining}
                  sx={{
                    textTransform: "none",
                    width: { xs: "100%", sm: "auto" },
                    minWidth: { xs: "100%", sm: 220 },
                    minHeight: { xs: 38, sm: 44 },
                    px: { xs: 1.2, sm: 2.2 },
                    py: { xs: 0.4, sm: 0.55 },
                    fontSize: { xs: 13, sm: 14 },
                    fontWeight: 800,
                    borderRadius: "10px",
                    backgroundColor: COLORS.green,
                    color: "#111",
                    "&:hover": { backgroundColor: "#45b39b" },
                  }}
                >
                  Begin session
                </Button>
              )}
              {(heroOptions.length ? heroOptions : (isPlayMode ? ["Open 3", "Fold"] : ["Open 2.5", "Call", "Fold"])).map((action, idx, actionList) => {
                const displayAction = isPlayMode ? toUiActionLabel(action) : action;
                return (
                <Button
                  key={`${action}-${idx}`}
                  variant="contained"
                  onClick={() => onHeroAction(action)}
                  disabled={isRunning || !heroTurn || actionOnSeat !== heroSeat || (!isPlayMode && heroBlockedActions.includes(action)) || heroStack <= 1e-9 || (isPlayMode && !playSessionStarted)}
                  sx={{
                    textTransform: "none",
                    width: { xs: "100%", sm: "auto" },
                    minWidth: { xs: "100%", sm: 170 },
                    minHeight: { xs: isPlayMode ? 42 : 38, sm: 44 },
                    px: { xs: 1.2, sm: 2.2 },
                    py: { xs: 0.4, sm: 0.55 },
                    fontSize: { xs: isPlayMode ? TABLE_FS_FLOOR : 13, sm: isPlayMode ? TABLE_FS_FLOOR + 1 : 14 },
                    fontWeight: 800,
                    borderRadius: "10px",
                    backgroundColor: getHeroActionButtonColor(action, heroBlockedActions.includes(action), actionList),
                    color: heroBlockedActions.includes(action) ? COLORS.textMuted : "#111",
                    "&:hover": {
                      backgroundColor: getHeroActionButtonColor(action, heroBlockedActions.includes(action), actionList),
                      filter: "brightness(1.05)",
                    },
                  }}
                >
                  {displayAction}
                </Button>
              );
              })}
            </Box>
          )}

          {/* Between decisions (or end-of-hand summary): keep Analysis under the action buttons. */}
          {!isPlayMode && !waitForHeroContinue && (
            <Box sx={{ minHeight: 58, border: `1px solid ${COLORS.border}`, borderRadius: "10px", backgroundColor: "#1f1f1f", px: 1.25, py: 0.95 }}>
              <Typography sx={{ fontSize: 14, color: COLORS.textMuted, mb: 0.35, textAlign: "center" }}>Analysis</Typography>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                <Typography
                  sx={{
                    fontSize: 16,
                    textAlign: "center",
                    color: feedback.toLowerCase().startsWith("correct")
                      ? COLORS.green
                      : feedback.toLowerCase().includes("blunder") || feedback.toLowerCase().includes("use street")
                        ? COLORS.danger
                        : COLORS.text,
                  }}
                >
                  {feedback}
                </Typography>
              </Box>
              {!!handSummary && (
                <Box sx={{ mt: 0.8, borderTop: `1px solid ${COLORS.border}`, pt: 0.6, display: "grid", gap: 0.3 }}>
                  {["preflop", "flop", "turn", "river"].map((streetKey) => {
                    const row = handSummary?.[streetKey] || { blunders: 0, correct: 0 };
                    const crosses = "✗".repeat(Number(row.blunders || 0));
                    const ticks = "✓".repeat(Number(row.correct || 0));
                    const streetColor = Number(row.blunders || 0) > 0 ? COLORS.danger : Number(row.correct || 0) > 0 ? COLORS.green : COLORS.textMuted;
                    return (
                      <Box key={`summary-${streetKey}`} sx={{ display: "flex", justifyContent: "center", gap: 0.7, alignItems: "center" }}>
                        <Typography sx={{ fontSize: 15, fontWeight: 700, color: streetColor }}>
                          {streetKey.toUpperCase()}
                        </Typography>
                        {crosses ? (
                          <Typography sx={{ fontSize: 15, fontWeight: 700, color: COLORS.danger }}>{crosses}</Typography>
                        ) : null}
                        {ticks ? (
                          <Typography sx={{ fontSize: 15, fontWeight: 700, color: COLORS.green }}>{ticks}</Typography>
                        ) : null}
                        {!crosses && !ticks ? (
                          <Typography sx={{ fontSize: 15, fontWeight: 700, color: COLORS.textMuted }}>-</Typography>
                        ) : null}
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          )}

        </Box>
      </Box>
      <Dialog
        open={sessionSummaryOpen}
        onClose={() => setSessionSummaryOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { backgroundColor: COLORS.cardBg, border: `1px solid ${COLORS.border}`, borderRadius: "14px", color: COLORS.text } }}
      >
        <DialogTitle sx={{ fontSize: 17, fontWeight: 800, color: COLORS.white }}>Session complete</DialogTitle>
        <DialogContent sx={{ pt: "6px !important" }}>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, mb: 1.2 }}>
            <Box sx={{ border: `1px solid ${COLORS.border}`, borderRadius: "10px", p: 1, textAlign: "center", backgroundColor: "#1f1f1f" }}>
              <Typography sx={{ fontSize: 11, color: COLORS.textMuted }}>Hands</Typography>
              <Typography sx={{ fontSize: 22, fontWeight: 800, color: COLORS.white }}>{sessionHandsPlayed}</Typography>
            </Box>
            <Box sx={{ border: `1px solid ${COLORS.border}`, borderRadius: "10px", p: 1, textAlign: "center", backgroundColor: "#1f1f1f" }}>
              <Typography sx={{ fontSize: 11, color: COLORS.textMuted }}>Accuracy</Typography>
              <Typography sx={{ fontSize: 22, fontWeight: 800, color: COLORS.green }}>
                {sessionGradedCount ? `${Math.round((sessionCorrectCount / sessionGradedCount) * 100)}%` : "-"}
              </Typography>
            </Box>
          </Box>
          <Typography sx={{ fontSize: 12, color: COLORS.textMuted, mb: 0.6 }}>
            {sessionCorrectCount} correct / {sessionGradedCount - sessionCorrectCount} blunders across {sessionGradedCount} graded decisions
            {sessionActionLog.length > sessionGradedCount ? ` (${sessionActionLog.length - sessionGradedCount} off-tree N/A)` : ""}.
          </Typography>
          {["preflop", "flop", "turn", "river"].map((sk) => {
            const rows = sessionActionLog.filter((r) => r.street === sk && (r.outcome === "correct" || r.outcome === "blunder"));
            if (!rows.length) return null;
            const good = rows.filter((r) => r.outcome === "correct").length;
            return (
              <Box key={`sess-street-${sk}`} sx={{ display: "flex", justifyContent: "space-between", borderTop: `1px solid ${COLORS.border}`, py: 0.5 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: COLORS.text }}>{sk.toUpperCase()}</Typography>
                <Typography sx={{ fontSize: 12, color: good === rows.length ? COLORS.green : COLORS.textMuted }}>
                  {good}/{rows.length} correct
                </Typography>
              </Box>
            );
          })}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSessionSummaryOpen(false)} sx={{ color: COLORS.textMuted, textTransform: "none" }}>Close</Button>
          <Button
            variant="contained"
            onClick={startNewDrillSession}
            sx={{ backgroundColor: COLORS.green, color: "#111", fontWeight: 800, textTransform: "none", "&:hover": { backgroundColor: "#45b39b" } }}
          >
            Start new session
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={!!handReview}
        onClose={closeHandReview}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { backgroundColor: COLORS.cardBg, border: `1px solid ${COLORS.border}`, borderRadius: "14px", color: COLORS.text } }}
      >
        {handReview && (() => {
          const streets = handReview.streets || [];
          const idx = Math.min(handReview.idx || 0, Math.max(streets.length - 1, 0));
          const streetKey = streets[idx] || "preflop";
          const entries = (handReview.entries || []).filter((e) => e.street === streetKey);
          const last = entries[entries.length - 1] || {};
          return (
            <>
              <DialogTitle sx={{ fontSize: 17, fontWeight: 800, color: COLORS.white }}>
                Hand review: {String(streetKey).toUpperCase()}
                <Typography component="span" sx={{ ml: 1, fontSize: 12, color: COLORS.textMuted }}>
                  {idx + 1} / {streets.length}
                </Typography>
              </DialogTitle>
              <DialogContent sx={{ pt: "6px !important" }}>
                <Box sx={{ display: "flex", gap: 0.4, mb: 0.6, alignItems: "center", flexWrap: "wrap" }}>
                  <Box sx={{ display: "flex", gap: 0.7, mr: 1.6, alignItems: "center" }}>
                    <Typography sx={{ fontSize: 11, color: COLORS.textMuted }}>You:</Typography>
                    {(last.heroCards || []).map((c, i) => <MiniCard key={`rv-hero-${i}`} card={c} />)}
                  </Box>
                  {!!(last.board || []).length && (
                    <Box sx={{ display: "flex", gap: 0.7, alignItems: "center" }}>
                      <Typography sx={{ fontSize: 11, color: COLORS.textMuted }}>Board:</Typography>
                      {(last.board || []).map((c, i) => <MiniCard key={`rv-board-${i}`} card={c} />)}
                    </Box>
                  )}
                </Box>
                {entries.map((e, i) => {
                  const pctPairs = Object.entries(e.pctMap || {})
                    .map(([a, p]) => [a, Number(p) || 0])
                    .filter(([, p]) => p > 0)
                    .sort((a, b) => b[1] - a[1]);
                  const chosenPct = Number((e.pctMap || {})[String(e.action || "").trim().toLowerCase()] ?? 0);
                  const outcomeColor = e.outcome === "correct" ? COLORS.green : e.outcome === "blunder" ? COLORS.danger : COLORS.textMuted;
                  return (
                    <Box key={`rv-${streetKey}-${i}`} sx={{ borderTop: `1px solid ${COLORS.border}`, py: 0.8 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: COLORS.white }}>
                          You: {e.action}{chosenPct > 0 ? ` (${Math.round(chosenPct)}%)` : ""}
                        </Typography>
                        <Typography sx={{ fontSize: 12, fontWeight: 800, color: outcomeColor }}>
                          {e.outcome === "correct" ? "✓ Correct" : e.outcome === "blunder" ? "✗ Blunder" : "N/A, off GTO line"}
                        </Typography>
                      </Box>
                      {e.outcome !== "na" ? (
                        <Box sx={{ mt: 0.4 }}>
                          <Typography sx={{ fontSize: 11, color: COLORS.textMuted, mb: 0.2 }}>GTO strategy:</Typography>
                          {pctPairs.map(([a, p]) => {
                            const isChosen = a === String(e.action || "").trim().toLowerCase();
                            return (
                              <Box key={`rv-pct-${i}-${a}`} sx={{ display: "flex", alignItems: "center", gap: 0.7, mb: 0.25 }}>
                                <Typography sx={{ fontSize: 11.5, fontWeight: isChosen ? 800 : 500, color: isChosen ? COLORS.gold : COLORS.text, minWidth: 78, textTransform: "capitalize" }}>
                                  {a}
                                </Typography>
                                <Box sx={{ flex: 1, height: 7, borderRadius: "4px", backgroundColor: "#1f1f1f", overflow: "hidden" }}>
                                  <Box sx={{ width: `${Math.min(100, Math.round(p))}%`, height: "100%", backgroundColor: isChosen ? COLORS.gold : COLORS.green }} />
                                </Box>
                                <Typography sx={{ fontSize: 11, color: COLORS.textMuted, minWidth: 32, textAlign: "right" }}>{Math.round(p)}%</Typography>
                              </Box>
                            );
                          })}
                          {!pctPairs.length && (
                            <Typography sx={{ fontSize: 11, color: COLORS.textMuted }}>No strategy data available.</Typography>
                          )}
                        </Box>
                      ) : (
                        <Typography sx={{ mt: 0.4, fontSize: 11, color: COLORS.textMuted }}>
                          A previous move left the solved GTO tree, so this decision can't be graded.
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </DialogContent>
              <DialogActions>
                <Button
                  disabled={idx === 0}
                  onClick={() => setHandReview((prev) => (prev ? { ...prev, idx: Math.max(0, (prev.idx || 0) - 1) } : prev))}
                  sx={{ color: COLORS.textMuted, textTransform: "none" }}
                >
                  ← Prev
                </Button>
                {idx < streets.length - 1 ? (
                  <Button
                    variant="contained"
                    onClick={() => setHandReview((prev) => (prev ? { ...prev, idx: Math.min(streets.length - 1, (prev.idx || 0) + 1) } : prev))}
                    sx={{ backgroundColor: COLORS.green, color: "#111", fontWeight: 800, textTransform: "none", "&:hover": { backgroundColor: "#45b39b" } }}
                  >
                    Next →
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={closeHandReview}
                    sx={{ backgroundColor: COLORS.green, color: "#111", fontWeight: 800, textTransform: "none", "&:hover": { backgroundColor: "#45b39b" } }}
                  >
                    Next hand
                  </Button>
                )}
              </DialogActions>
            </>
          );
        })()}
      </Dialog>
      <Dialog open={feedbackOpen} onClose={() => setFeedbackOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Feedback</DialogTitle>
        <DialogContent sx={{ display: "grid", gap: 1, pt: "10px !important" }}>
          <TextField
            label="What did you like?"
            value={feedbackLike}
            onChange={(e) => setFeedbackLike(e.target.value)}
            multiline
            minRows={2}
            size="small"
            disabled={feedbackBusy}
          />
          <TextField
            label="What did you dislike?"
            value={feedbackDislike}
            onChange={(e) => setFeedbackDislike(e.target.value)}
            multiline
            minRows={2}
            size="small"
            disabled={feedbackBusy}
          />
          <TextField
            label="Other thoughts"
            value={feedbackOther}
            onChange={(e) => setFeedbackOther(e.target.value)}
            multiline
            minRows={2}
            size="small"
            disabled={feedbackBusy}
          />
          {!!feedbackError && <Typography sx={{ color: COLORS.danger, fontSize: 12 }}>{feedbackError}</Typography>}
          {!!feedbackInfo && <Typography sx={{ color: COLORS.green, fontSize: 12 }}>{feedbackInfo}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFeedbackOpen(false)} disabled={feedbackBusy}>Close</Button>
          <Button variant="contained" onClick={sendFeedback} disabled={feedbackBusy} sx={{ backgroundColor: COLORS.gold, color: "#111", "&:hover": { backgroundColor: "#a88f47" } }}>
            {feedbackBusy ? "Sending..." : "Send"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </AppearanceContext.Provider>
  );
}
